import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  LogIn,
  ClipboardCopy,
  Archive,
  Download,
  Image,
  Eye,
  EyeOff,
  Filter,
  Layers,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Truck,
  Upload,
  X,
  FileSpreadsheet,
  Mail,
  MessageCircle,
  UserPlus,
  Users,
  Phone,
  MapPinned,
  FileText,
  CheckCircle,
  ChevronDown,
  PanelRightOpen,
  Map as LucideMap,
  Info,
  Loader2,
  Lock,
  BellRing,
  Navigation,
  Send,
  Maximize2,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import { supplyService } from '../services/supplyService';
import { shipmentService } from '../services/shipmentService';
import { accountingService } from '../services/accountingService';
import { citiesService } from '../services/citiesService';
import { documentService } from '../services/documentService';
import { settingsService } from '../services/settingsService';
import { vehicleService } from '../services/vehicleService';
import { driverService } from '../services/driverService';
import { taskNotificationService } from '../services/taskNotificationService';
import { bidService } from '../services/bidService';
import { archiveService } from '../services/archiveService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { generalList2Service } from '../services/generalList2Service';
import { userService } from '../services/userService';
import { auditService } from '../services/auditService';
import { ShipmentWizardModal } from '../components/ShipmentWizardModal';
import { LocationReminderModal } from '../components/LocationReminderModal';
import { BulkServiceTypeModal } from '../components/BulkServiceTypeModal';
import { BulkShipmentImportModal } from '../components/BulkShipmentImportModal';
import { ShipmentRouteMap, ShipmentRouteSummary } from '../components/ShipmentRouteMap';
import { useAuth } from '../contexts/AuthContext';
import { normalizeVehicleType, parseVehicleTypes } from '../constants/vehicleTypes';

const sanitizeShipmentNote = (value?: string | null) => {
  const cleaned = String(value || '')
    .split(' | ')
    .map((part) => String(part || '').trim())
    .filter((part) => part && !/^(Sipariş Tarihi:|Termin:|Yükleme Ara Noktaları:|Boşaltma Ara Noktaları:)/.test(part))
    .join(' | ')
    .trim();
  return cleaned || '-';
};

const buildDeliveryPointsText = (deliveryPoint?: string | null, intermediateStops?: string | null) => {
  const mainPoint = String(deliveryPoint || '').trim() || '-';
  const deliveryStops = parseIntermediateDeliveryStops(intermediateStops);
  const labels = Array.from(new Set([mainPoint, ...deliveryStops.map((stop) => String(stop.label || stop.customerName || '').trim()).filter(Boolean)]));
  return labels.length > 0 ? labels.join(' | ') : '-';
};

const buildUniqueLocationList = (values: Array<string | null | undefined>, fallback = '-') => {
  const unique = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
  return unique.length > 0 ? unique.join(', ') : fallback;
};

const getRoutePointLocationSummary = (item: any, field: 'city' | 'district', fallbackValue?: string | null) => {
  const routePoints = Array.isArray(item?.route_points) ? item.route_points : [];
  const unloadingValues = routePoints
    .filter((point: any) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'unloading')
    .map((point: any) => point?.[field]);
  const intermediateValues = parseIntermediateDeliveryStops(item?.ara_noktalar || item?.intermediate_stops).map((stop) => stop?.[field]);
  return buildUniqueLocationList([fallbackValue, ...unloadingValues, ...intermediateValues]);
};

const parseIntermediateDeliveryStops = (value: any): Array<{ label?: string; customerName?: string; city?: string; district?: string }> => {
  if (!value || value === '-') return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') {
    if (Array.isArray(value.delivery)) return value.delivery;
    return [];
  }
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.delivery)) return parsed.delivery;
  } catch {
    return trimmed
      .replace(/^(Boşaltma:|Bosaltma:|Yükleme:|Yukleme:)/i, '')
      .split(/[,|]/)
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .map((part) => ({ label: part }));
  }
  return [];
};

const normalizeStopLabel = (value?: string | null) => String(value || '').replace(/^(Boşaltma:|Bosaltma:|Yükleme:|Yukleme:)/i, '').trim().toLocaleLowerCase('tr-TR');

const getIntermediateStopLabels = (value: any) => {
  const parsedStops = parseIntermediateDeliveryStops(value);
  return parsedStops
    .map((stop) => String(stop?.label || stop?.customerName || '').trim())
    .filter(Boolean);
};

const getUniqueDeliveryPointCount = (deliveryPoint: any, intermediateStops: any) => {
  const labels = [
    String(deliveryPoint || '').trim(),
    ...getIntermediateStopLabels(intermediateStops),
  ]
    .map((label) => normalizeStopLabel(label))
    .filter(Boolean);
  return new Set(labels).size;
};

const getIntermediateStopCount = (value: any, _stopCount?: number | string | null, deliveryName?: string | null) => {
  const uniqueDeliveryPointCount = getUniqueDeliveryPointCount(deliveryName, value);
  return Math.max(0, uniqueDeliveryPointCount - 1);
};

const getDeliveryPointCount = (item: any) => {
  const intermediateLabels = getIntermediateStopLabels(item?.ara_noktalar || item?.intermediate_stops);
  const routeStopLabels = (Array.isArray(item?.route_points) ? item.route_points : [])
    .filter((point: any) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'unloading')
    .map((point: any) => String(point?.location_name || point?.label || '').trim());
  const labels = [
    String(item?.teslim_musterisi_ad || item?.delivery_name || '').trim(),
    ...intermediateLabels,
    ...routeStopLabels,
  ].map(normalizeStopLabel).filter(Boolean);

  const resolvedCount = new Set(labels).size;
  // Legacy imports may not have route records; use their stored stop count in that case.
  return intermediateLabels.length || routeStopLabels.length
    ? Math.max(1, resolvedCount)
    : Math.max(1, resolvedCount, Number(item?.stop_count || 0));
};

const resolveRowStopFee = (item: any, unitStopFee: number, _autoCalculateStopFees?: boolean) => {
  const explicit = Number(item?.ugrama_bedeli || item?.ugarma_bedeli || 0);
  if (explicit > 0) return explicit;
  const serverStopIncluded = Number(item?.base_price_with_stop_fee || 0);
  const serverBase = Number(item?.base_price_without_stop_fee || item?.baz_fiyat || item?.base_price || 0);
  if (serverStopIncluded > 0 && serverStopIncluded > serverBase) {
    return Number((serverStopIncluded - serverBase).toFixed(2));
  }
  const extraStopCount = Math.max(0, getDeliveryPointCount(item) - 1);
  if (extraStopCount <= 0 || unitStopFee <= 0) return 0;
  return Number((extraStopCount * unitStopFee).toFixed(2));
};

type SortDirection = 'asc' | 'desc';
type FilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty' | 'minutesOver' | 'dateEquals' | 'dateOnOrAfter' | 'dateOnOrBefore' | 'dateToday' | 'dateThisWeek' | 'dateThisMonth' | 'dateLastMonth' | 'dateRange';
type FilterJoin = 'and' | 'or';
type GroupByKey = ColumnKey | 'none';
type FlowTabValue = 'all' | string;
type CellFilterMenuState = {
  x: number;
  y: number;
  column: ColumnKey;
  columnLabel: string;
  value: string;
};

type OperationRow = {
  rowKey: string;
  id: string;
  archiveCode?: string;
  folderName?: string;
  invoiceNumber?: string;
  ettnNo?: string;
  invoiceGroupKey?: string | null;
  invoiceGroupCount?: number | null;
  folderNumber?: string;
  evrakSiraNo?: string;
  sevkKodu: string;
  planKodu: string;
  cekici: string;
  dorse: string;
  surucuAdi: string;
  surucuKonumu: string;
  surucuSonKonum: string;
  surucuKonumLatitude?: number | null;
  surucuKonumLongitude?: number | null;
  irtibatTelefon: string;
  surucuTelefon: string;
  surucuGunlukSureKalani: string;
  surucuHaftalikSureKalani: string;
  surucuIkiHaftalikSureKalani: string;
  gunlukKm: number;
  tcKimlikNo: string;
  faturaBasligi: string;
  hedefFiyat?: number | null;
  disNakliye: string;
  yuklemeMusterisi: string;
  loadingPointsText: string;
  bosaltmaMusterisi: string;
  deliveryPointsText: string;
  yuklemeIli: string;
  yuklemeIlcesi: string;
  bosaltmaIli: string;
  teslimIlcesi: string;
  lkm: string;
  yuklemeAdres: string;
  teslimAdres: string;
  aracAtamaTarihi: string;
  yuklemeTarihi: string;
  terminTarihi: string;
  randevuluTeslim: string;
  teslimNoktasiSayisi: number;
  ugrmaSayisi: number;
  tersLojistik?: string;
  navlun: number;
  bazFiyat?: number;
  durakBedeli: number;
  beklemeBedeli: number;
  yatmaGunSayisi?: number;
  demurrageManualEdit?: number | null;
  seferKm: number;
  tlKm: number;
  hammaliye: number;
  toplamBedel: number;
  lojistikTuru: string;
  hizmetTuru: string;
  istenenKasaTipi: string;
  yukAgirlik: number;
  yukHacim: number;
  palletCount: number;
  operasyonNot: string;
  araNoktalar: string;
  nearestHub: string;
  yuklemeEvraklari: string;
  teslimEvraklari: string;
  iadeTutanaklari: string;
  faturalar: string;
  islemDurum: string;
  durum: string;
  accountingStatus: string;
  mailGonderimTarihi?: string;
  createdAt: string;
  updatedAt: string;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  pickupShiftId?: string | null;
  deliveryShiftId?: string | null;
  shipmentContactPhone?: string | null;
  vehicleCount?: number;
  sonNot?: string;
  sonNotYazan?: string;
  activeShipmentCount?: number;
  driverId?: string | null;
  hasTemporaryAssignment?: boolean;
  temporaryAssignmentStartDate?: string;
  temporaryAssignmentEndDate?: string;
  temporaryAssignmentNotes?: string;
  permanentDriverName?: string;
  temporaryDriverName?: string;
  driverUserId?: string | null;
  paymentControllerInfo?: string;
  paymentControllerAt?: string;
  paymentControllerPdfPath?: string;
  preApprovalPdfPath?: string;
  paymentApproverInfo?: string;
  paymentApprovedAt?: string;
  mailAtildi?: string;
  eSignAction?: string;
  eSignStatus?: string;
  __requestESign?: () => void;
  spotSource?: string;
  spotEmail?: string;
  spotEhliyetSinifi?: string;
  spotSrcNo?: string;
  spotDeneyimYil?: string;
  spotSehirIlce?: string;
  spotKurumsalFirma?: string;
  spotKontrolSorumlusu?: string;
  spotIkametKonum?: string;
};

type ColumnKey = keyof OperationRow;

type ColumnDef = {
  key: ColumnKey;
  label: string;
  description?: string;
  width?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: OperationRow[ColumnKey], row: OperationRow) => string | number;
  render?: (row: OperationRow) => ReactNode;
};

type FilterRule = {
  id: string;
  join: FilterJoin;
  column: ColumnKey;
  operator: FilterOperator;
  value: string;
};

type SavedTableView = {
  visibleColumns?: ColumnKey[];
  pageSize?: number;
  sortKey?: ColumnKey | null;
  sortDirection?: SortDirection;
  groupBy?: GroupByKey;
  columnWidths?: Partial<Record<ColumnKey, number>>;
};

type SavedFilterTab = {
  id: string;
  name: string;
  filters: FilterRule[];
  groupBy: GroupByKey;
  flowTab?: FlowTabValue;
  sortKey?: ColumnKey | null;
  sortDirection?: SortDirection;
  visibility?: 'private' | 'public';
  ownerUserId?: string | number | null;
  ownerName?: string;
};

const MAIN_TAB_ID = 'main';
const FLOW_TAB_ID = 'flow-management';

const MAIN_FILTER_TAB: SavedFilterTab = {
  id: MAIN_TAB_ID,
  name: 'Ana Tablo',
  filters: [],
  groupBy: 'none',
  flowTab: 'all',
  sortKey: null,
  sortDirection: 'asc',
};

const FLOW_FILTER_TAB: SavedFilterTab = {
  id: FLOW_TAB_ID,
  name: 'Akış Yönetimi',
  filters: [],
  groupBy: 'none',
  flowTab: 'all',
  sortKey: null,
  sortDirection: 'asc',
};

const getFlexibleTrackingBasicViewSettingKey = (userId: string | number, scope: string) =>
  `user_${userId}_${scope}_basic_view_v4`;

const getFlexibleTrackingSavedTabsSettingKey = (userId: string | number, scope: string) =>
  `user_${userId}_${scope}_saved_filter_tabs_v1`;

const getFlexibleTrackingSharedTabsSettingKey = (scope: string) =>
  `shared_${scope}_saved_filter_tabs_v1`;

type FlexibleTrackingProps = {
  dataService?: { getAll: (params?: { status?: string; shipmentId?: string; updatedSince?: string; includeLocked?: boolean }) => Promise<any> };
  dataParams?: { status?: string; shipmentId?: string; updatedSince?: string; includeLocked?: boolean };
  settingsKeyScope?: string;
  title?: string;
  subtitle?: string;
  defaultVisibleColumns?: ColumnKey[];
  externalRows?: OperationRow[];
  externalLoading?: boolean;
  onRowSelect?: (row: OperationRow | null) => void;
  selectedExternalRowKey?: string | null;
  customColumns?: ColumnDef[];
  flowStatusOptions?: Array<{ value: string; label: string }>;
  hideArchiveTab?: boolean;
  hideCreateActions?: boolean;
  onBulkRequestESign?: (rows: OperationRow[], role: 'controller' | 'approver') => Promise<void>;
  onBulkResetESign?: (rows: OperationRow[]) => Promise<void>;
  onBulkSendSignedPdfEmail?: (rows: OperationRow[]) => Promise<void>;
  onBulkAccountingStatusUpdate?: () => Promise<void> | void;
  onBulkRefreshPreApprovalPdf?: (rows: OperationRow[]) => Promise<void> | void;
  onRefreshPreApprovalPdf?: (row: OperationRow) => Promise<void> | void;
  forceBlackTableText?: boolean;
  constrainTableToViewport?: boolean;
  externalRowActions?: (row: OperationRow) => ReactNode;
  disableDetailPanel?: boolean;
  onExternalRefresh?: () => Promise<void> | void;
};

const makeId = () => `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const normalizeSavedTab = (
  tab: Partial<SavedFilterTab>,
  fallbackVisibility: 'private' | 'public',
  ownerUserId?: string | number | null,
  ownerName?: string,
): SavedFilterTab => ({
  id: String(tab.id || makeId()),
  name: String(tab.name || 'Tab'),
  filters: sanitizeFilters(tab.filters),
  groupBy: sanitizeGroupBy(tab.groupBy),
  sortKey: tab.sortKey && getColumn(tab.sortKey) ? tab.sortKey : null,
  sortDirection: tab.sortDirection === 'desc' ? 'desc' : 'asc',
  visibility: tab.visibility === 'public' ? 'public' : fallbackVisibility,
  ownerUserId: tab.ownerUserId ?? ownerUserId ?? null,
  ownerName: String(tab.ownerName || ownerName || ''),
});

const normalizeSearchText = (value?: string | number | null) =>
  String(value ?? '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ')
    .trim();

const normalizePhone = (value?: string) => {
  const raw = String(value || '').replace(/\D/g, '');
  if (!raw) return '-';
  if (raw.startsWith('90')) return `+${raw}`;
  if (raw.startsWith('0')) return `+9${raw}`;
  return `+90${raw}`;
};

const normalizePlate = (value?: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const normalizeUpper = (value?: string) => String(value || '').toLocaleUpperCase('tr-TR');
const normalizePhoneInput = (value?: string) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const isValidSpotPlate = (value?: string) => {
  const plate = normalizePlate(value);
  return !plate || (plate.length >= 6 && plate.length <= 9 && /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate));
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatLocationAge = (value?: string) => {
  if (!value) return '-';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '-';
  const totalMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (totalMinutes < 1) return 'Az önce';
  if (totalMinutes < 60) return `${totalMinutes} dk önce`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes ? `${hours} sa ${minutes} dk önce` : `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
};

const haversineKm = async (lat1?: number | null, lon1?: number | null, lat2?: number | null, lon2?: number | null): Promise<number | null> => {
  if ([lat1, lon1, lat2, lon2].some((v) => v === null || v === undefined || Number.isNaN(Number(v)))) return null;

  try {
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${Number(lon1)},${Number(lat1)};${Number(lon2)},${Number(lat2)}?overview=false&alternatives=false&steps=false`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const distanceMeters = data?.routes?.[0]?.distance;

    if (typeof distanceMeters !== 'number') return null;

    return Number((distanceMeters / 1000).toFixed(1));
  } catch (error) {
    console.error('OSRM mesafe hesaplama hatası:', error);
    return null;
  }
};

const getDistanceBadgeClass = (distance?: number | null) => {
  if (distance == null) return 'bg-slate-800 text-slate-200';
  if (distance <= 5) return 'bg-emerald-950/60 text-emerald-300';
  if (distance <= 20) return 'bg-amber-950/60 text-amber-300';
  return 'bg-rose-950/60 text-rose-300';
};

const matchesRequestedVehicleTypes = (requestedValue?: string, candidateValue?: string) => {
  const requestedTypes = parseVehicleTypes(requestedValue).map((item) => normalizeVehicleType(item)).filter(Boolean);
  if (requestedTypes.length === 0) return true;
  const candidateType = normalizeVehicleType(candidateValue);
  if (!candidateType) return true;
  return requestedTypes.includes(candidateType);
};

const ACTIVE_ASSIGNMENT_STATUSES = new Set([
  'atandi-islem-bekliyor',
  'yukleme-bekliyor',
  'yukleme-devam',
  'yukleme-tamamlandi',
  'tasima-yolunda',
  'teslim-noktasina-ulasildi',
  'bosaltma-bekliyor',
  'bosaltma-devam',
]);

const DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS = [
  'Tır Tenteli Kayar Perde',
  'Tır Frigo',
  'Tır Havuz',
  'Tır Damper',
  'Kırkayak',
  'Kamyon Tenteli',
  'Kamyonet',
];

const SUPPLY_STATUS_OPTIONS = [
  { value: 'tedarik-bekleniyor', label: 'Tedarik Bekleniyor' },
  { value: 'spot-teklif-bekleniyor', label: 'Spot Teklif Bekleniyor' },
  { value: 'filo-tedarik-edildi', label: 'Filo Tedarik Edildi' },
  { value: 'spot-tedarik-edildi', label: 'Spot Tedarik Edildi' },
  { value: 'faturalandirildi', label: 'Faturalandırıldı' },
  { value: 'iptal', label: 'İptal' },
] as const;

const MANUAL_PROCESS_STATUS_OPTIONS = [
  { value: 'tedarik-bekleniyor', label: 'Tedarik Bekleniyor' },
  { value: 'spot-teklif-bekleniyor', label: 'Spot Teklif Bekleniyor' },
  { value: 'atandi-islem-bekliyor', label: 'Atandı İşlem Bekliyor' },
  { value: 'yukleme-bekliyor', label: 'Yükleme Bekliyor' },
  { value: 'yukleme-devam', label: 'Yükleme Devam' },
  { value: 'yukleme-tamamlandi', label: 'Yükleme Tamamlandı' },
  { value: 'tasima-yolunda', label: 'Teslim Yolunda' },
  { value: 'teslim-noktasina-ulasildi', label: 'Teslim Noktasına Ulaşıldı' },
  { value: 'bosaltma-bekliyor', label: 'Boşaltma Bekliyor' },
  { value: 'bosaltma-devam', label: 'Boşaltma Devam' },
  { value: 'bosaltma-tamamlandi', label: 'Boşaltma Tamamlandı' },
  { value: 'delivery-approval-pending', label: 'Teslim Onayı Bekliyor' },
  { value: 'invoice-waiting', label: 'Fatura Bekliyor' },
  { value: 'faturalandirildi', label: 'Faturalandırıldı' },
  { value: 'invoice-approval-pending', label: 'Fatura Kontrol' },
  { value: 'invoice-check-approved', label: 'Fatura Onay' },
  { value: 'payment-pending', label: 'Ödeme Bekliyor' },
  { value: 'payment-completed', label: 'Ödemesi Gerçekleşen' },
] as const;

const FREIGHT_UPDATE_REASONS = [
  { value: 'waiting', label: 'Bekleme oluştu' },
  { value: 'handling', label: 'Hammaliye oluştu' },
  { value: 'route-extended', label: 'Rota uzadı' },
  { value: 'extra-stop', label: 'Ek uğrama eklendi' },
  { value: 'vehicle-change', label: 'Araç değişti' },
  { value: 'other', label: 'Diğer' },
] as const;

const MANUAL_PROCESS_STATUS_VALUES = new Set<string>(MANUAL_PROCESS_STATUS_OPTIONS.map((option) => option.value));

const ACCOUNTING_ONLY_FLOW = [
  'delivery-approval-pending',
  'invoice-waiting',
  'faturalandirildi',
  'invoice-approval-pending',
  'invoice-check-approved',
  'payment-pending',
  'payment-completed',
] as const;

const PROCESS_FLOW_ORDER = [
  'atandi-islem-bekliyor',
  'yukleme-bekliyor',
  'yukleme-devam',
  'yukleme-tamamlandi',
  'tasima-yolunda',
  'teslim-noktasina-ulasildi',
  'bosaltma-bekliyor',
  'bosaltma-devam',
  'bosaltma-tamamlandi',
  'delivery-approval-pending',
  'invoice-waiting',
  'faturalandirildi',
  'invoice-approval-pending',
  'invoice-check-approved',
  'payment-pending',
  'payment-completed',
] as const;

const getEffectiveFlowStatus = (row: Pick<OperationRow, 'islemDurum' | 'durum' | 'accountingStatus'>) => {
  const processStatus = String(row.islemDurum || '').trim();
  const rawStatus = String(row.durum || '').trim();
  if (rawStatus === 'tedarik-bekleniyor' || rawStatus === 'spot-teklif-bekleniyor') return rawStatus;
  if (processStatus && processStatus !== '-' && MANUAL_PROCESS_STATUS_VALUES.has(processStatus)) return processStatus;
  const accountingStatus = String(row.accountingStatus || '').trim();
  if (accountingStatus && accountingStatus !== 'none' && MANUAL_PROCESS_STATUS_VALUES.has(accountingStatus)) return accountingStatus;
  if (rawStatus && rawStatus !== '-' && MANUAL_PROCESS_STATUS_VALUES.has(rawStatus)) return rawStatus;
  return rawStatus || processStatus || (accountingStatus !== 'none' ? accountingStatus : '') || '-';
};

const getFlowStatusLabel = (value: string) => {
  const direct = MANUAL_PROCESS_STATUS_OPTIONS.find((option) => option.value === value)?.label;
  if (direct) return direct;
  return formatSlugLabel(String(value || ''));
};

const formatMoney = (value: unknown) =>
  `₺${Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;

const formatNumber = (value: unknown) =>
  Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });


type TrackingDocumentBucket = 'loading' | 'delivery' | 'return' | 'invoice';
const DETAIL_DOCUMENT_BUCKETS: TrackingDocumentBucket[] = ['loading', 'delivery', 'return', 'invoice'];

type TrackingDocumentPreview = {
  id?: string | number;
  fileName: string;
  url: string;
  isImage: boolean;
};

type DocumentSliderState = {
  docs: TrackingDocumentPreview[];
  currentIndex: number;
};

const DOCUMENT_COLUMN_KEYS = new Set<ColumnKey>(['yuklemeEvraklari', 'teslimEvraklari', 'iadeTutanaklari', 'faturalar']);

const getDocumentBucketForColumn = (key: ColumnKey): TrackingDocumentBucket | null => {
  if (key === 'yuklemeEvraklari') return 'loading';
  if (key === 'teslimEvraklari') return 'delivery';
  if (key === 'iadeTutanaklari') return 'return';
  if (key === 'faturalar') return 'invoice';
  return null;
};

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

const normalizeDocument = (doc: any, index: number): TrackingDocumentPreview => {
  const fileName = doc?.file_name || doc?.fileName || doc?.original_name || doc?.name || `Evrak ${index + 1}`;
  const rawPath = doc?.file_path || doc?.file_url || doc?.filePath || doc?.url || doc?.path || '';
  const mimeType = String(doc?.file_type || doc?.mime_type || doc?.mimetype || '').toLocaleLowerCase('tr-TR');
  const isImage = mimeType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(String(fileName));
  return {
    id: doc?.id,
    fileName: String(fileName),
    url: resolveFileUrl(rawPath),
    isImage,
  };
};

const uniqueDocuments = (documents: TrackingDocumentPreview[]) => {
  const seen = new Set<string>();
  return documents.filter((doc) => {
    const key = `${doc.url}-${doc.fileName}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return Boolean(doc.url && doc.url !== '#');
  });
};

const getDocumentTypeQueries = (bucket: TrackingDocumentBucket) => {
  switch (bucket) {
    case 'loading':
      return ['loading', 'completion', 'shipment-stage-document', 'pickup', 'yukleme'];
    case 'delivery':
      return ['delivery', 'teslim', 'delivery-document', 'delivery-report', 'delivery-proof', 'shortage-surplus-report', 'incident-report', 'return-report'];
    case 'return':
      return ['return', 'return-report', 'iade', 'shortage-surplus', 'incident', 'shortage-surplus-report', 'incident-report'];
    case 'invoice':
      return ['invoice', 'fatura'];
    default:
      return [];
  }
};

const isShipmentStageDeliveryDocument = (doc: any) => {
  const documentType = String(doc?.document_type || doc?.documentType || '').trim();
  const stage = Number(doc?.stage || 0);
  return documentType === 'shipment-stage-document' && stage >= 7;
};

const matchesDocumentBucket = (doc: any, bucket: TrackingDocumentBucket) => {
  const documentType = String(doc?.document_type || doc?.documentType || '').trim();
  const documentTypes = new Set(getDocumentTypeQueries(bucket));

  if (bucket === 'delivery') {
    return isShipmentStageDeliveryDocument(doc) || documentTypes.has(documentType);
  }

  if (bucket === 'loading') {
    return documentTypes.has(documentType) && !isShipmentStageDeliveryDocument(doc);
  }

  return documentTypes.has(documentType);
};

const getDurumText = (durum: string) => {
  switch (durum) {
    case 'tedarik-bekliyor':
      return 'Tedarik Bekliyor';
    case 'filo-tedarik-edildi':
      return 'Filo Tedarik Edildi';
    case 'spot-tedarik-edildi':
      return 'Spot Tedarik Edildi';
    case 'teklif-topla':
      return 'Teklif Topla';
    case 'faturalandirildi':
      return 'Faturalandırıldı';
    case 'iptal':
      return 'İptal';
    default:
      return durum || '-';
  }
};

const getDurumClass = (durum: string) => {
  switch (durum) {
    case 'tedarik-bekliyor':
      return 'bg-rose-500 text-white';
    case 'filo-tedarik-edildi':
      return 'bg-blue-500 text-white';
    case 'spot-tedarik-edildi':
      return 'bg-sky-300 text-slate-950';
    case 'teklif-topla':
      return 'bg-violet-500 text-white';
    case 'faturalandirildi':
      return 'bg-emerald-500 text-white';
    case 'iptal':
      return 'bg-slate-700 text-white';
    default:
      return 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-white';
  }
};


const getAccountingStatusText = (status: string) => {
  switch (status) {
    case 'none':
      return 'İşlem Bekliyor';
    case 'delivery-approval-pending':
      return 'Teslim Onayı Bekliyor';
    case 'delivery-rejected':
      return 'Teslim Reddedildi';
    case 'invoice-waiting':
      return 'Fatura Bekliyor';
    case 'invoice-rejected':
      return 'Fatura Reddedildi';
    case 'invoice-approval-pending':
      return 'Fatura Kontrol';
    case 'payment-pre-approval':
      return 'Fatura Kontrol';
    case 'invoice-check-pending':
      return 'Fatura Kontrol';
    case 'invoice-check-approved':
      return 'Fatura Onay';
    case 'payment-pending':
      return 'Muhasebeye Aktarıldı';
    case 'payment-completed':
      return 'Ödemesi Gerçekleşen';
    default:
      return status || '-';
  }
};

const getAccountingStatusClass = (status: string) => {
  switch (status) {
    case 'none':
      return 'bg-yellow-100 text-yellow-950 dark:bg-yellow-900/45 dark:text-yellow-100';
    case 'delivery-approval-pending':
      return 'bg-amber-100 text-amber-950 dark:bg-amber-900/45 dark:text-amber-100';
    case 'delivery-rejected':
      return 'bg-red-100 text-red-950 dark:bg-red-900/45 dark:text-red-100';
    case 'invoice-waiting':
      return 'bg-sky-100 text-sky-950 dark:bg-sky-900/45 dark:text-sky-100';
    case 'invoice-rejected':
      return 'bg-rose-100 text-rose-950 dark:bg-rose-900/45 dark:text-rose-100';
    case 'invoice-approval-pending':
      return 'bg-violet-100 text-violet-950 dark:bg-violet-900/45 dark:text-violet-100';
    case 'payment-pre-approval':
      return 'bg-violet-100 text-violet-950 dark:bg-violet-900/45 dark:text-violet-100';
    case 'invoice-check-pending':
      return 'bg-violet-100 text-violet-950 dark:bg-violet-900/45 dark:text-violet-100';
    case 'invoice-check-approved':
      return 'bg-teal-100 text-teal-950 dark:bg-teal-900/45 dark:text-teal-100';
    case 'payment-pending':
      return 'bg-orange-100 text-orange-950 dark:bg-orange-900/45 dark:text-orange-100';
    case 'payment-completed':
      return 'bg-green-100 text-green-950 dark:bg-green-900/45 dark:text-green-100';
    default:
      return 'bg-yellow-100 text-yellow-950 dark:bg-yellow-900/45 dark:text-yellow-100';
  }
};

const getFlowStatusRowClass = (status: string) => {
  switch (String(status || '').trim()) {
    case 'tedarik-bekleniyor':
      return 'bg-rose-50 dark:bg-rose-950/20';
    case 'spot-teklif-bekleniyor':
      return 'bg-violet-50 dark:bg-violet-950/20';
    case 'filo-tedarik-edildi':
      return 'bg-blue-50 dark:bg-blue-950/20';
    case 'spot-tedarik-edildi':
      return 'bg-sky-50 dark:bg-sky-950/20';
    case 'atandi-islem-bekliyor':
      return 'bg-indigo-50 dark:bg-indigo-950/20';
    case 'yukleme-bekliyor':
      return 'bg-amber-50 dark:bg-amber-950/20';
    case 'yukleme-devam':
      return 'bg-orange-50 dark:bg-orange-950/20';
    case 'yukleme-tamamlandi':
      return 'bg-lime-50 dark:bg-lime-950/20';
    case 'tasima-yolunda':
      return 'bg-cyan-50 dark:bg-cyan-950/20';
    case 'teslim-noktasina-ulasildi':
      return 'bg-teal-50 dark:bg-teal-950/20';
    case 'bosaltma-bekliyor':
      return 'bg-fuchsia-50 dark:bg-fuchsia-950/20';
    case 'bosaltma-devam':
      return 'bg-pink-50 dark:bg-pink-950/20';
    case 'bosaltma-tamamlandi':
      return 'bg-emerald-50 dark:bg-emerald-950/20';
    case 'delivery-approval-pending':
      return 'bg-yellow-50 dark:bg-yellow-950/20';
    case 'invoice-waiting':
      return 'bg-slate-100 dark:bg-slate-800/60';
    case 'faturalandirildi':
      return 'bg-emerald-50 dark:bg-emerald-950/20';
    case 'invoice-approval-pending':
      return 'bg-violet-50 dark:bg-violet-950/20';
    case 'payment-pre-approval':
      return 'bg-violet-50 dark:bg-violet-950/20';
    case 'invoice-check-pending':
      return 'bg-violet-50 dark:bg-violet-950/20';
    case 'payment-pending':
      return 'bg-orange-50 dark:bg-orange-950/20';
    case 'payment-completed':
      return 'bg-green-50 dark:bg-green-950/20';
    default:
      return '';
  }
};

const getFlowStatusRowBackground = (status: string) => {
  switch (String(status || '').trim()) {
    case 'tedarik-bekleniyor': return '#fff1f2';
    case 'spot-teklif-bekleniyor': return '#f5f3ff';
    case 'filo-tedarik-edildi': return '#eff6ff';
    case 'spot-tedarik-edildi': return '#f0f9ff';
    case 'atandi-islem-bekliyor': return '#eef2ff';
    case 'yukleme-bekliyor': return '#fffbeb';
    case 'yukleme-devam': return '#fff7ed';
    case 'yukleme-tamamlandi': return '#f7fee7';
    case 'tasima-yolunda': return '#ecfeff';
    case 'teslim-noktasina-ulasildi': return '#f0fdfa';
    case 'bosaltma-bekliyor': return '#fdf4ff';
    case 'bosaltma-devam': return '#fdf2f8';
    case 'bosaltma-tamamlandi': return '#ecfdf5';
    case 'delivery-approval-pending': return '#fefce8';
    case 'invoice-waiting': return '#f8fafc';
    case 'faturalandirildi': return '#ecfdf5';
    case 'invoice-approval-pending': return '#f5f3ff';
    case 'payment-pre-approval': return '#f5f3ff';
    case 'invoice-check-pending': return '#f5f3ff';
    case 'invoice-check-approved': return '#f0fdfa';
    case 'payment-pending': return '#fff7ed';
    case 'payment-completed': return '#f0fdf4';
    default: return '';
  }
};

const INVOICE_GROUP_ROW_COLORS = ['#fff7ed', '#fffbeb', '#fef3c7', '#fde68a', '#fed7aa', '#ffedd5', '#fef2f2', '#fff1f2'];
const INVOICE_GROUP_BORDER_COLOR = '#7f1d1d';

const getInvoiceGroupRowBackground = (invoiceGroupKey?: string | null) => {
  const key = String(invoiceGroupKey || '').trim();
  if (!key) return '';
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(index);
    hash |= 0;
  }
  return INVOICE_GROUP_ROW_COLORS[Math.abs(hash) % INVOICE_GROUP_ROW_COLORS.length];
};

const prioritizeSharedInvoiceRows = (rows: OperationRow[]) => {
  const result = [...rows];
  result.sort((a, b) => {
    const aInvoiceGroupKey = String(a.invoiceGroupKey || '').trim();
    const bInvoiceGroupKey = String(b.invoiceGroupKey || '').trim();
    const aSharedInvoice = aInvoiceGroupKey && Number(a.invoiceGroupCount || 0) > 1;
    const bSharedInvoice = bInvoiceGroupKey && Number(b.invoiceGroupCount || 0) > 1;

    if (aSharedInvoice || bSharedInvoice) {
      if (aSharedInvoice && bSharedInvoice && aInvoiceGroupKey !== bInvoiceGroupKey) {
        return aInvoiceGroupKey.localeCompare(bInvoiceGroupKey, 'tr-TR', { numeric: true, sensitivity: 'base' });
      }
      if (aSharedInvoice !== bSharedInvoice) {
        return aSharedInvoice ? -1 : 1;
      }
      if (aSharedInvoice && bSharedInvoice && aInvoiceGroupKey === bInvoiceGroupKey) {
        return String(a.sevkKodu || '').localeCompare(String(b.sevkKodu || ''), 'tr-TR', { numeric: true, sensitivity: 'base' });
      }
    }

    return 0;
  });
  return result;
};

const buildInvoiceGroupItems = (rows: OperationRow[], collapsedGroups: Record<string, boolean>) => {
  const items: Array<any> = [];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const invoiceGroupKey = String(row.invoiceGroupKey || '').trim();
    const isSharedInvoice = invoiceGroupKey && Number(row.invoiceGroupCount || 0) > 1;
    if (!isSharedInvoice) {
      items.push(row);
      continue;
    }

    const groupedRows: OperationRow[] = [row];
    while (index + 1 < rows.length) {
      const nextRow = rows[index + 1];
      if (String(nextRow.invoiceGroupKey || '').trim() !== invoiceGroupKey) break;
      groupedRows.push(nextRow);
      index += 1;
    }

    const groupKey = `invoice-group:${invoiceGroupKey}`;
    const header = {
      __invoiceGroup: true,
      group: {
        key: groupKey,
        label: `Tek Fatura Grubu (${groupedRows.map((item) => String(item.sevkKodu || item.id || '-')).join(', ')})`,
        rows: groupedRows,
        invoiceGroupKey,
      },
    };
    items.push(header);
    if (!(collapsedGroups[groupKey] ?? false)) {
      items.push(...groupedRows.map((groupedRow, groupedIndex) => ({
        ...groupedRow,
        __invoiceGroupMeta: {
          key: groupKey,
          index: groupedIndex,
          count: groupedRows.length,
          color: getInvoiceGroupRowBackground(invoiceGroupKey) || '#ecfeff',
        },
      })));
    }
  }
  return items;
};

const getComparableCellValue = (row: OperationRow, key: ColumnKey) => {
  const column = getColumn(key);
  const rawValue = row[key];
  const baseValue = column?.format ? column.format(rawValue, row) : String(rawValue ?? '-');

  if (key === 'durum') {
    return `${rawValue} ${getDurumText(String(rawValue))}`;
  }

  if (key === 'islemDurum') {
    return `${rawValue} ${formatSlugLabel(String(rawValue))}`;
  }

  if (key === 'accountingStatus') {
    return `${rawValue} ${getAccountingStatusText(String(rawValue))}`;
  }

  return baseValue;
};

const getDateFilterComparableValue = (value: string | number | null | undefined) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const hasTimeZone = /(Z|[+-]\d{2}:?\d{2})$/.test(raw) && raw.includes('T');
  if (hasTimeZone) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(parsed);
      const year = parts.find((part) => part.type === 'year')?.value;
      const month = parts.find((part) => part.type === 'month')?.value;
      const day = parts.find((part) => part.type === 'day')?.value;
      if (year && month && day) return `${year}-${month}-${day}`;
    }
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const trMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (trMatch) {
    return `${trMatch[3]}-${trMatch[2]}-${trMatch[1]}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDatePresetRange = (operator: FilterOperator) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const format = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (operator === 'dateToday') {
    const value = format(today);
    return { start: value, end: value };
  }

  if (operator === 'dateThisWeek') {
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: format(start), end: format(end) };
  }

  if (operator === 'dateThisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: format(start), end: format(end) };
  }

  if (operator === 'dateLastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: format(start), end: format(end) };
  }

  return null;
};

const CityMultiSelect = ({ value, options, onChange, disabled }: { value: string; options: string[]; onChange: (value: string) => void; disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const selected = String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  const toggle = (city: string) => {
    const next = selected.includes(city) ? selected.filter((item) => item !== city) : [...selected, city];
    onChange(next.join(','));
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
      >
        <span className="truncate">{selected.length > 0 ? selected.join(', ') : 'İl seçiniz'}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-[#171717]">
            {options.length === 0 && <div className="px-2 py-2 text-sm text-slate-400">Seçenek yok</div>}
            {options.map((city) => (
              <label key={city} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800">
                <input type="checkbox" checked={selected.includes(city)} onChange={() => toggle(city)} className="h-4 w-4 accent-blue-600" />
                <span className="truncate">{city}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const getFlowStatusClass = (value: string) => {
  const normalized = normalizeSearchText(value);
  if (normalized.includes('tamam') || normalized.includes('teslim') || normalized.includes('onay') || normalized.includes('fatura')) {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-800';
  }
  if (normalized.includes('bekli') || normalized.includes('tedarik') || normalized.includes('topla')) {
    return 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800';
  }
  if (normalized.includes('yol') || normalized.includes('devam') || normalized.includes('yukleme') || normalized.includes('yükleme')) {
    return 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-800';
  }
  if (normalized.includes('iptal') || normalized.includes('red')) {
    return 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-800';
  }
  return 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700';
};

const formatSlugLabel = (value: string) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  return raw
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toLocaleUpperCase('tr-TR') + part.slice(1))
    .join(' ');
};

const REMAINING_TIME_COLUMN_KEYS = new Set<ColumnKey>(['surucuGunlukSureKalani', 'surucuHaftalikSureKalani', 'surucuIkiHaftalikSureKalani']);

const getRemainingHoursBadgeClass = (value: string) => {
  const num = Number(String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/)?.[0] || NaN);
  if (!Number.isFinite(num)) return 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700';
  if (num <= 8) return 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-800';
  if (num <= 24) return 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800';
  return 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-800';
};

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'archiveCode', label: 'Arşiv No', width: 120 },
  { key: 'folderNumber', label: 'Klasör', width: 120, format: (_v, row) => row.folderName || '-' },
  { key: 'evrakSiraNo', label: 'Evrak Sıra No', width: 130 },
  { key: 'invoiceNumber', label: 'Fatura No', width: 140 },
  { key: 'ettnNo', label: 'ETTN No', width: 260 },
  { key: 'sevkKodu', label: 'Sipariş #', width: 130 },
  { key: 'planKodu', label: 'Plan Kodu', width: 130 },
  { key: 'durum', label: 'Durum', width: 190 },
  { key: 'islemDurum', label: 'İşlem Durum', width: 190 },
  { key: 'yuklemeMusterisi', label: 'Yükleme Müşterisi', width: 230 },
  { key: 'loadingPointsText', label: 'Yükleme Noktaları', width: 260 },
  { key: 'yuklemeIli', label: 'Yükleme İli', width: 150 },
  { key: 'yuklemeIlcesi', label: 'Yükleme İlçesi', width: 160 },
  { key: 'yuklemeAdres', label: 'Yükleme Adresi', width: 260 },
  { key: 'bosaltmaMusterisi', label: 'Teslim Noktaları', width: 320 },
  { key: 'deliveryPointsText', label: 'Teslim Noktaları Metin', width: 260 },
  { key: 'nearestHub', label: 'Dönüş Hub', width: 220 },
  { key: 'bosaltmaIli', label: 'Teslim İli', width: 150 },
  { key: 'teslimIlcesi', label: 'Teslim İlçesi', width: 160 },
  { key: 'lkm', label: 'LKM', width: 120 },
  { key: 'teslimAdres', label: 'Teslim Adresi', width: 260 },
  { key: 'cekici', label: 'Çekici', width: 130 },
  { key: 'dorse', label: 'Dorse', width: 130 },
  { key: 'surucuAdi', label: 'Sürücü', width: 180 },
  { key: 'surucuKonumu', label: 'Sürücü Konumu', width: 260 },
  { key: 'surucuSonKonum', label: 'Son Konum', width: 140, format: (v) => formatLocationAge(String(v || '')), render: (row) => {
    const latitude = Number(row.surucuKonumLatitude);
    const longitude = Number(row.surucuKonumLongitude);
    const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
    const label = formatLocationAge(row.surucuSonKonum);
    if (!hasCoordinates) return <span className="text-slate-400">{label}</span>;
    return <button type="button" onClick={(event) => { event.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank', 'noopener,noreferrer'); }} className="font-semibold text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200" title="Google Maps'te aç">{label}</button>;
  } },
  { key: 'irtibatTelefon', label: 'İrtibat Telefonu', width: 170 },
  { key: 'surucuTelefon', label: 'Sürücü Telefonu', width: 170 },
  { key: 'surucuGunlukSureKalani', label: 'Günlük Süre Kalanı', width: 150 },
  { key: 'surucuHaftalikSureKalani', label: 'Haftalık Süre Kalanı', width: 160 },
  { key: 'surucuIkiHaftalikSureKalani', label: '2 Haftalık Süre Kalanı', width: 170 },
  { key: 'gunlukKm', label: 'Günlük KM', width: 120, align: 'right', format: (v) => formatNumber(v) },
  { key: 'tcKimlikNo', label: 'TC Kimlik', width: 150 },
  { key: 'hedefFiyat', label: 'Hedef Fiyat', description: 'Nakliye Satın Alma Uzmanı’nın sipariş için pazarlıkla çıkabileceği en yüksek tavan fiyattır. Varsayılan olarak Baz Fiyat + Durak Bedeli hesaplanır.', width: 140, align: 'right', format: (v) => v != null && v !== '' ? formatMoney(v) : '-' },
  { key: 'aracAtamaTarihi', label: 'Atama Tarihi', width: 170, format: (v) => formatDateTime(String(v || '')) },
  { key: 'yuklemeTarihi', label: 'Yükleme Tarihi', width: 170, format: (v) => formatDateTime(String(v || '')) },
  { key: 'terminTarihi', label: 'Termin Tarihi', width: 170, format: (v) => formatDateTime(String(v || '')) },
  { key: 'randevuluTeslim', label: 'Randevu', width: 130 },
  { key: 'faturaBasligi', label: 'Fatura Başlığı', width: 220 },
  { key: 'disNakliye', label: 'Dış Nakliye', width: 140 },
  { key: 'lojistikTuru', label: 'Lojistik Türü', width: 150 },
  { key: 'hizmetTuru', label: 'Hizmet Türü', width: 150 },
  { key: 'istenenKasaTipi', label: 'Kasa Tipi', width: 140 },
  { key: 'teslimNoktasiSayisi', label: 'Teslim Noktası', width: 140, align: 'right' },
  { key: 'ugrmaSayisi', label: 'Ek Durak', width: 110, align: 'right' },
  { key: 'tersLojistik', label: 'Ters Lojistik', description: 'Yükleme noktası müşteri, teslim noktası depo olan sevkiyatlarda Evet görünür.', width: 130, align: 'center', format: (v) => String(v || 'Hayır'), render: (row) => {
    const isReverse = String(row.tersLojistik || '') === 'Evet';
    return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isReverse ? 'bg-violet-100 text-violet-800 ring-1 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800' : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700'}`}>{isReverse ? 'Evet' : 'Hayır'}</span>;
  } },
  { key: 'navlun', label: 'Navlun', description: 'Taşıyıcı kullanıcı ile anlaşılan, KDV hariç çıplak fiyattır.', width: 140, align: 'right', format: (v) => formatMoney(v) },
  { key: 'bazFiyat', label: 'Baz Fiyat', description: 'İlgili navlunun son teslim noktasına ait hedeflenen ana satın alma fiyatıdır.', width: 140, align: 'right', format: (v) => formatMoney(v) },
  { key: 'durakBedeli', label: 'Durak Bedeli', description: 'İlk duraktan sonraki her ek durak için, ek durak sayısı ile birim durak bedelinin çarpımından sistem tarafından otomatik hesaplanır.', width: 150, align: 'right', format: (v) => formatMoney(v) },
  { key: 'beklemeBedeli', label: 'Bekleme Bedeli', description: 'Alıcı noktanın nizamiye giriş ve çıkış saatleri arasındaki süreye (T2 - T1) göre sistem tarafından hesaplanan demuraj tutarıdır.', width: 150, align: 'right', format: (v) => formatMoney(v) },
  { key: 'hammaliye', label: 'Hammaliye Bedeli', description: 'Operasyon sorumlusu onayıyla, sahadaki tahliye veya tahmil ihtiyacının işçilikle çözülmesi halinde oluşan ek anlaşma bedelidir.', width: 150, align: 'right', format: (v) => formatMoney(v) },
  { key: 'toplamBedel', label: 'Toplam Navlun Bedeli', description: 'Taşıyıcının KDV hariç net hakediş bedelidir: Navlun + Hammaliye Bedeli + Bekleme Bedeli. Durak Bedeli bu toplama dahil edilmez.', width: 180, align: 'right', format: (v) => formatMoney(v) },
  { key: 'seferKm', label: 'Sefer KM', width: 120, align: 'right', format: (v) => formatNumber(v) },
  { key: 'tlKm', label: 'TL/KM', width: 110, align: 'right', format: (v) => formatNumber(v) },
  { key: 'yukAgirlik', label: 'Ağırlık', width: 120, align: 'right', format: (v) => `${formatNumber(v)} kg` },
  { key: 'yukHacim', label: 'Hacim', width: 110, align: 'right', format: (v) => `${formatNumber(v)} m³` },
  { key: 'palletCount', label: 'Palet', width: 100, align: 'right' },
  { key: 'yuklemeEvraklari', label: 'Yükleme Evrakları', width: 190 },
  { key: 'teslimEvraklari', label: 'Teslim Evrakları', width: 190 },
  { key: 'iadeTutanaklari', label: 'İade Tutanakları', width: 190 },
  { key: 'faturalar', label: 'Faturalar', width: 190 },
  { key: 'operasyonNot', label: 'Not', width: 280 },
  { key: 'accountingStatus', label: 'Muhasebe', width: 160 },
  { key: 'createdAt', label: 'Oluşturma', width: 170, format: (v) => formatDateTime(String(v || '')) },
  { key: 'updatedAt', label: 'Güncelleme', width: 170, format: (v) => formatDateTime(String(v || '')) },
  { key: 'activeShipmentCount', label: 'Aktif Sevkiyat', width: 120 },
  { key: 'preApprovalPdfPath', label: 'Ön Onay PDF', width: 160 },
  { key: 'paymentControllerInfo', label: 'Kontrol Eden', width: 220 },
  { key: 'paymentApproverInfo', label: 'Onaylayan', width: 220 },
  { key: 'mailAtildi', label: 'Mail Atıldı', width: 120 },
  { key: 'mailGonderimTarihi', label: 'Mail Gönderim Tarihi', width: 180, format: (v) => formatDateTime(String(v || '')) },
  { key: 'eSignAction', label: 'E-İmza', width: 180 },
  { key: 'spotKurumsalFirma', label: 'Kurumsal Firma', width: 200 },
  { key: 'spotSource', label: 'Kaynak', width: 90 },
  { key: 'spotEmail', label: 'E-posta', width: 200 },
  { key: 'spotEhliyetSinifi', label: 'Ehliyet Sınıfı', width: 120 },
  { key: 'spotSrcNo', label: 'SRC No', width: 120 },
  { key: 'spotDeneyimYil', label: 'Deneyim (Yıl)', width: 120 },
  { key: 'spotSehirIlce', label: 'İl / İlçe', width: 180 },
  { key: 'spotKontrolSorumlusu', label: 'Kontrol Sorumlusu', width: 150 },
  { key: 'spotIkametKonum', label: 'İkamet Konumu', width: 160 },
];

const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = [
  'sevkKodu',
  'planKodu',
  'durum',
  'islemDurum',
  'yuklemeMusterisi',
  'yuklemeIli',
  'yuklemeIlcesi',
  'bosaltmaMusterisi',
  'bosaltmaIli',
  'teslimIlcesi',
  'lkm',
  'cekici',
  'dorse',
  'surucuAdi',
  'surucuKonumu',
  'surucuSonKonum',
  'irtibatTelefon',
  'surucuTelefon',
  'yuklemeTarihi',
  'terminTarihi',
  'yuklemeEvraklari',
  'teslimEvraklari',
  'faturalar',
  'accountingStatus',
  'navlun',
  'durakBedeli',
  'beklemeBedeli',
  'hammaliye',
  'toplamBedel',
  'hizmetTuru',
  'lojistikTuru',
];

const FILTERABLE_COLUMNS = ALL_COLUMNS.filter((column) => column.key !== 'rowKey');
const GROUPABLE_COLUMNS = ALL_COLUMNS.filter((column) => ['durum', 'islemDurum', 'yuklemeIli', 'bosaltmaIli', 'surucuAdi', 'cekici', 'disNakliye', 'lojistikTuru', 'accountingStatus'].includes(column.key));
const DATE_FILTER_COLUMNS = new Set<ColumnKey>(['aracAtamaTarihi', 'yuklemeTarihi', 'terminTarihi', 'mailGonderimTarihi', 'createdAt', 'updatedAt']);
const CITY_FILTER_COLUMNS = new Set<ColumnKey>(['yuklemeIli', 'bosaltmaIli']);

const getColumn = (key: ColumnKey) => ALL_COLUMNS.find((column) => column.key === key);

const sanitizeVisibleColumns = (columns: unknown, fallbackColumns: ColumnKey[]): ColumnKey[] => {
  const validKeys = new Set(ALL_COLUMNS.map((column) => column.key));
  const parsed = Array.isArray(columns) ? columns : fallbackColumns;
  const unique = parsed.filter((key): key is ColumnKey => validKeys.has(key as ColumnKey));
  return unique.length > 0 ? [...new Set(unique)] : fallbackColumns;
};

const sanitizeFilters = (filters?: unknown): FilterRule[] => {
  const validKeys = new Set(ALL_COLUMNS.map((column) => column.key));
  const validOperators = new Set<FilterOperator>(['contains', 'notContains', 'equals', 'notEquals', 'empty', 'notEmpty', 'dateEquals', 'dateOnOrAfter', 'dateOnOrBefore', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth', 'dateRange']);
  if (!Array.isArray(filters)) return [];
  return filters
    .filter((item: any) => item && validKeys.has(item.column) && validOperators.has(item.operator))
    .map((item: any, index: number) => ({
      id: String(item.id || makeId()),
      join: index === 0 ? 'and' : item.join === 'or' ? 'or' : 'and',
      column: item.column as ColumnKey,
      operator: item.operator as FilterOperator,
      value: String(item.value || ''),
    }));
};

const sanitizeGroupBy = (value?: unknown): GroupByKey => {
  if (value === 'none') return 'none';
  return getColumn(value as ColumnKey) ? (value as ColumnKey) : 'none';
};

const mapSupplyToRow = (item: any, index: number, unitStopFee = 0, autoCalculateStopFees = true): OperationRow => {
  const seferKm = Number(item.sefer_km || item.distance_km || item.mesafe || item.distance || 0);
  const navlun = Number(item.fixed_price || item.navlun || item.final_price || item.maliyet || item.cost || 0);
  const durakBedeli = resolveRowStopFee(item, unitStopFee, autoCalculateStopFees);
  const teslimNoktasiSayisi = getDeliveryPointCount(item);
  const ekDurakSayisi = Math.max(0, teslimNoktasiSayisi - 1);
  const beklemeBedeli = Number(item.bekleme_bedeli || item.bekleme_fee || item.waiting_fee || 0);
  const hammaliye = Number(item.hammaliye_bedeli || item.hammaliye_fee || 0);

  return {
    rowKey: [String(item.id || ''), String(item.sevk_kodu || item.shipment_no || ''), String(index)].join('::'),
    id: String(item.id || ''),
    archiveCode: item.archive_code || item.archiveCode || '-',
    folderName: item.folder_name || item.folderName || '-',
    invoiceNumber: item.fatura_no || item.invoice_no || '-',
    folderNumber: item.folder_number ? String(item.folder_number) : item.folderNumber ? String(item.folderNumber) : '',
    evrakSiraNo: item.evrak_sira_no ? String(item.evrak_sira_no) : item.folder_number ? String(item.folder_number) : '',
    sevkKodu: item.sevk_kodu || item.shipment_no || '-',
    planKodu: item.plan_kodu || item.plan_code || '-',
    cekici: item.cekici || item.cekici_plaka || item.arac_plaka || item.assigned_vehicle_plate || '-',
    dorse: item.dorse_plaka || item.assigned_trailer_plate || '-',
     surucuAdi: item.surucu_bilgileri || item.driver_name || item.assigned_driver_name || '-',
     surucuKonumu: item.current_address || item.currentAddress || item.driver_location_description || item.driverLocationDescription || (item.driver_location_latitude != null && item.driver_location_longitude != null ? `${item.driver_location_latitude}, ${item.driver_location_longitude}` : '-'),
     surucuSonKonum: item.driver_location_at || item.driverLocationAt || item.latest_location_at || item.latestLocationAt || item.location_updated_at || '',
     surucuKonumLatitude: item.driver_location_latitude != null ? Number(item.driver_location_latitude) : item.driverLocationLatitude != null ? Number(item.driverLocationLatitude) : null,
     surucuKonumLongitude: item.driver_location_longitude != null ? Number(item.driver_location_longitude) : item.driverLocationLongitude != null ? Number(item.driverLocationLongitude) : null,
    irtibatTelefon: normalizePhone(item.irtibat_telefon || item.contact_phone || item.telefon || item.loading_contact_phone || item.shipment_contact_phone || item.source_phone),
    surucuTelefon: normalizePhone(item.surucu_telefon || item.driver_phone || item.assigned_driver_phone || item.fallback_driver_phone),
    surucuGunlukSureKalani: item.surucu_gunluk_sure_kalani || item.available_drive_period || '-',
    surucuHaftalikSureKalani: item.surucu_haftalik_sure_kalani || '-',
    surucuIkiHaftalikSureKalani: item.surucu_iki_haftalik_sure_kalani || '-',
    gunlukKm: Number(item.gunluk_km || item.daily_km || 0),
    tcKimlikNo: item.tc_kimlik_no || item.tc_number || '-',
    faturaBasligi: item.fatura_basligi || item.invoice_title || '-',
    hedefFiyat: Number(item.target_price || item.hedef_fiyat || (Number(item.base_price_without_stop_fee || item.baz_fiyat || item.base_price || 0) + durakBedeli)),
    disNakliye: item.dis_nakliye || (Number(item.is_external_transport || 0) > 0 ? 'Evet' : 'Hayır'),
    yuklemeMusterisi: item.yukleme_musterisi_ad || item.pickup_name || '-',
    loadingPointsText: item.loading_points_text || '-',
    bosaltmaMusterisi: item.teslim_musterisi_ad || item.delivery_name || '-',
    deliveryPointsText: item.delivery_points_text || '-',
    nearestHub: item.nearest_hub_name ? `${item.nearest_hub_name}${item.nearest_hub_km != null ? ` (${Number(item.nearest_hub_km).toFixed(1)} km)` : ''}` : '-',
    yuklemeIli: item.yukleme_il || item.pickup_city || '-',
    yuklemeIlcesi: item.yukleme_ilce || item.pickup_district || '-',
    bosaltmaIli: getRoutePointLocationSummary(item, 'city', item.teslim_il || item.delivery_city || '-'),
    teslimIlcesi: getRoutePointLocationSummary(item, 'district', item.teslim_ilce || item.delivery_district || '-'),
    lkm: String(item.lkm || item.lkm_decision || '-').trim() || '-',
    yuklemeAdres: item.yukleme_adres || item.pickup_address || '-',
    teslimAdres: item.teslim_adres || item.delivery_address || '-',
    aracAtamaTarihi: item.arac_atama_tarihi || item.assigned_at || '',
    yuklemeTarihi: item.yukleme_talep_tarihi || item.pickup_date || '',
    terminTarihi: item.delivery_date || item.istenen_teslim_tarihi || '',
    randevuluTeslim: item.delivery_date || item.istenen_teslim_tarihi ? 'Randevulu' : 'Standart',
    teslimNoktasiSayisi,
    ugrmaSayisi: ekDurakSayisi,
    tersLojistik: Number(item.delivery_warehouse_id || 0) > 0 && !Number(item.pickup_warehouse_id || 0) ? 'Evet' : 'Hayır',
    navlun,
    bazFiyat: Number(item.base_price_without_stop_fee || item.baz_fiyat || item.base_price || 0),
    durakBedeli,
    beklemeBedeli,
    yatmaGunSayisi: item.yatma_gun_sayisi != null ? Number(item.yatma_gun_sayisi) : undefined,
    demurrageManualEdit: item.demurrage_manual_edit != null ? Number(item.demurrage_manual_edit) : 0,
    seferKm,
    tlKm: seferKm > 0 ? Number((navlun / seferKm).toFixed(2)) : 0,
    hammaliye,
    toplamBedel: navlun + durakBedeli + beklemeBedeli + hammaliye,
    lojistikTuru: item.arac_tipi || item.transport_model || '-',
    hizmetTuru: item.yuk_tipi || item.load_type || '-',
    istenenKasaTipi: item.arac_turu || item.vehicle_spec_type || item.aracTipi || item.vehicle_type || '-',
    yukAgirlik: Number(item.yuk_agirlik || item.weight_kg || 0),
    yukHacim: Number(item.yuk_hacim || item.volume_m3 || 0),
    palletCount: Number(item.pallet_count || 0),
    operasyonNot: sanitizeShipmentNote(item.operasyon_not || item.aciklama || item.notlar || item.notes),
    araNoktalar: item.ara_noktalar || item.intermediate_stops || '-',
    yuklemeEvraklari: '',
    teslimEvraklari: '',
    iadeTutanaklari: '',
    faturalar: item.invoice_file_name || item.invoiceFileName || item.invoice_file_path || item.invoiceFilePath || item.invoice_code || item.invoiceCode || '',
    islemDurum: item.islem_durum || item.process_status || item.durum || item.status || '-',
    durum: item.durum || item.status || item.process_status || '-',
    accountingStatus: item.accounting_status || 'none',
    invoiceGroupKey: item.invoice_group_key || item.invoiceGroupKey || null,
    invoiceGroupCount: item.invoice_group_count != null ? Number(item.invoice_group_count) : item.invoiceGroupCount != null ? Number(item.invoiceGroupCount) : null,
    mailGonderimTarihi: item.signed_pdf_emailed_at || item.signedPdfEmailedAt || '',
    createdAt: item.created_at || item.createdAt || '',
    updatedAt: item.updated_at || item.updatedAt || '',
    pickupLatitude: item.pickup_latitude != null ? Number(item.pickup_latitude) : null,
    pickupLongitude: item.pickup_longitude != null ? Number(item.pickup_longitude) : null,
    activeShipmentCount: item.active_shipment_count != null ? Number(item.active_shipment_count) : 0,
    driverId: item.driver_id || item.driverId || null,
    driverUserId: item.driver_user_id || item.driverUserId || null,
    hasTemporaryAssignment: Number(item.has_temporary_assignment || 0) > 0,
    temporaryAssignmentStartDate: item.temporary_assignment_start_date || '',
    temporaryAssignmentEndDate: item.temporary_assignment_end_date || '',
    temporaryAssignmentNotes: item.temporary_assignment_notes || '',
    permanentDriverName: item.permanent_driver_name || '',
    temporaryDriverName: item.temporary_driver_name || '',
  };
};

const buildFinancialPatch = (row: OperationRow, patch: Partial<OperationRow>): Partial<OperationRow> => {
  const nextNavlun = Number(patch.navlun ?? row.navlun ?? 0);
  const nextBekleme = Number(patch.beklemeBedeli ?? row.beklemeBedeli ?? 0);
  const nextHammaliye = Number(patch.hammaliye ?? row.hammaliye ?? 0);
  return {
    ...patch,
    toplamBedel: nextNavlun + nextBekleme + nextHammaliye,
  };
};

const resolveCostValue = (row: any, field: 'beklemeBedeli' | 'hammaliye') => {
  const candidates = field === 'beklemeBedeli'
    ? [row?.beklemeBedeli, row?.bekleme_bedeli, row?.beklemeFee, row?.bekleme_fee, row?.waitingFee, row?.waiting_fee]
    : [row?.hammaliye, row?.hammaliyeBedeli, row?.hammaliye_bedeli, row?.hammaliyeFee, row?.hammaliye_fee];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue;
    const numeric = Number(String(candidate).replace(',', '.'));
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
};

const ACCOUNTING_LOCKED_STATUSES = new Set(['payment-pending', 'payment-completed']);

const isAccountingLockedForRow = (row: Pick<OperationRow, 'accountingStatus'> | null | undefined) => {
  return ACCOUNTING_LOCKED_STATUSES.has(String(row?.accountingStatus || '').trim());
};

const getAccountingLockedMessage = () => 'Bu siparis muhasebeye aktarildigi icin detay guncellenemez. Once imza/muhasebe asamasindan geri alinmalidir.';

export function FlexibleTracking({
  dataService = supplyService,
  dataParams,
  settingsKeyScope = 'flexible_tracking',
  title = 'Operasyon Takip',
  subtitle = 'Filtre ve gruplamalar kullanıcı bazlı hızlı arama tabı olarak kaydedilir.',
  defaultVisibleColumns = DEFAULT_VISIBLE_COLUMNS,
  externalRows,
  externalLoading,
  onRowSelect,
  selectedExternalRowKey,
  customColumns,
  flowStatusOptions,
  hideArchiveTab = false,
  hideCreateActions = false,
  onBulkRequestESign,
  onBulkResetESign,
  onBulkSendSignedPdfEmail,
  onBulkAccountingStatusUpdate,
  onBulkRefreshPreApprovalPdf,
  onRefreshPreApprovalPdf,
  forceBlackTableText = false,
  constrainTableToViewport = true,
  externalRowActions,
  disableDetailPanel = false,
  onExternalRefresh,
}: FlexibleTrackingProps = {}) {
  const { user, impersonateUser, hasPermission } = useAuth();

  const [rows, setRows] = useState<OperationRow[]>([]);
  const [rowOverrides, setRowOverrides] = useState<Record<string, Partial<OperationRow>>>({});
  const [loading, setLoading] = useState(false);
  const [loadTimeMs, setLoadTimeMs] = useState<number | null>(null);
  const [savingView, setSavingView] = useState(false);
  const [savingTabs, setSavingTabs] = useState(false);
  const [syncingPoints, setSyncingPoints] = useState(false);
  const [viewLoaded, setViewLoaded] = useState(false);

  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailTab, setDetailTab] = useState<'info' | 'goods' | 'driver' | 'tracking' | 'documents' | 'audit'>('info');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(defaultVisibleColumns);

  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [cityFilterOptions, setCityFilterOptions] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<GroupByKey>('none');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [activeFlowTab, setActiveFlowTab] = useState<FlowTabValue>('all');

  const [privateTabs, setPrivateTabs] = useState<SavedFilterTab[]>([]);
  const [publicTabs, setPublicTabs] = useState<SavedFilterTab[]>([]);
  const [activeTabId, setActiveTabId] = useState(MAIN_TAB_ID);
  const [newTabName, setNewTabName] = useState('');
  const [newTabVisibility, setNewTabVisibility] = useState<'private' | 'public'>('private');

  const [sortKey, setSortKey] = useState<ColumnKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const [editingInvoiceRowId, setEditingInvoiceRowId] = useState<string | null>(null);
  const [editingInvoiceValue, setEditingInvoiceValue] = useState('');
  const [editingCostCell, setEditingCostCell] = useState<{ rowKey: string; field: 'beklemeBedeli' | 'hammaliye' } | null>(null);
  const [editingCostValue, setEditingCostValue] = useState('');
  const [savingCostCell, setSavingCostCell] = useState<{ rowKey: string; field: 'beklemeBedeli' | 'hammaliye' } | null>(null);
  const [unitStopFee, setUnitStopFee] = useState(0);
  const [autoCalculateStopFees, setAutoCalculateStopFees] = useState(true);
  const [demurrageLocked, setDemurrageLocked] = useState(false);
  const [demurrageTransitionDate, setDemurrageTransitionDate] = useState('');
  const [demurrageSettingsLoaded, setDemurrageSettingsLoaded] = useState(false);
  const [archiveRows, setArchiveRows] = useState<any[]>([]);
  const [archiveRecordByShipmentId, setArchiveRecordByShipmentId] = useState<Record<string, any>>({});
  const effectiveRows = useMemo(() => {
    const sourceRows = externalRows || rows;
    return sourceRows.map((row) => {
      const overriddenRow = rowOverrides[row.rowKey] ? { ...row, ...rowOverrides[row.rowKey] } : row;
      const archiveRecord = archiveRecordByShipmentId[String(overriddenRow.id)];
      if (!archiveRecord) return overriddenRow;
      return {
        ...overriddenRow,
        archiveCode: archiveRecord.archiveCode || overriddenRow.archiveCode || '-',
        folderName: archiveRecord.folderName || overriddenRow.folderName || '-',
        folderNumber: archiveRecord.folderNumber != null ? String(archiveRecord.folderNumber) : (overriddenRow.folderNumber || ''),
        evrakSiraNo: archiveRecord.evrakSiraNo != null ? String(archiveRecord.evrakSiraNo) : (overriddenRow.evrakSiraNo || ''),
        invoiceNumber: overriddenRow.invoiceNumber || archiveRecord.invoiceNumber || '-',
      };
    });
  }, [archiveRecordByShipmentId, externalRows, rowOverrides, rows]);
  const effectiveRowsWithLkm = effectiveRows;
  const effectiveLoading = externalLoading !== undefined ? externalLoading : loading;
  const detailRow = useMemo<OperationRow | null>(() => {
    if (!showDetailPanel || !selectedRowKey) return null;
    return effectiveRowsWithLkm.find((r) => r.rowKey === selectedRowKey) || null;
  }, [showDetailPanel, selectedRowKey, effectiveRowsWithLkm]);
  const handledShipmentParamRef = useRef<string>('');
  const handledSearchQParamRef = useRef<string>('');
  const [cellFilterMenu, setCellFilterMenu] = useState<CellFilterMenuState | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingWizardShipment, setEditingWizardShipment] = useState<any | null>(null);
  const [loadingEditShipmentId, setLoadingEditShipmentId] = useState<string | null>(null);
  const [unassigningShipmentId, setUnassigningShipmentId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignRow, setAssignRow] = useState<OperationRow | null>(null);
  const [assignType, setAssignType] = useState<'own-fleet' | 'spot'>('own-fleet');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [internalDrivers, setInternalDrivers] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [externalDrivers, setExternalDrivers] = useState<any[]>([]);
  const [corporateCarrierAccounts, setCorporateCarrierAccounts] = useState<any[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedSpotCompanyId, setSelectedSpotCompanyId] = useState('all');
  const [sendingAssignmentTestEmailId, setSendingAssignmentTestEmailId] = useState<string | null>(null);
  const [agreedPrice, setAgreedPrice] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDemurrageToggling, setBulkDemurrageToggling] = useState<string | null>(null);
  const [bulkSelectPasteOpen, setBulkSelectPasteOpen] = useState(false);
  const [bulkSelectPasteValue, setBulkSelectPasteValue] = useState('');
  const [showOnlySelectedRows, setShowOnlySelectedRows] = useState(false);
  const [locationReminderModalOpen, setLocationReminderModalOpen] = useState(false);
  const [bulkServiceTypeModalOpen, setBulkServiceTypeModalOpen] = useState(false);
  const [bulkProcessStatusModalOpen, setBulkProcessStatusModalOpen] = useState(false);
  const [bulkProcessStatusValue, setBulkProcessStatusValue] = useState<string>(MANUAL_PROCESS_STATUS_OPTIONS[0].value);
  const [bulkUpdatingProcessStatus, setBulkUpdatingProcessStatus] = useState(false);

  const [bulkAccountingModalOpen, setBulkAccountingModalOpen] = useState(false);
  const [bulkAccountingTargetStatus, setBulkAccountingTargetStatus] = useState<string>('invoice-approval-pending');
  const [bulkUpdatingAccounting, setBulkUpdatingAccounting] = useState(false);

  const [paymentApprovalModalOpen, setPaymentApprovalModalOpen] = useState(false);
  const [paymentApprovalRow, setPaymentApprovalRow] = useState<OperationRow | null>(null);
  const [paymentApprovalRows, setPaymentApprovalRows] = useState<OperationRow[]>([]);
  const [paymentApprovalMode, setPaymentApprovalMode] = useState<'controller' | 'approver'>('controller');
  const [paymentApprovalUsers, setPaymentApprovalUsers] = useState<any[]>([]);
  const [paymentApprovalSelectedUserId, setPaymentApprovalSelectedUserId] = useState('');
  const [paymentApprovalSaving, setPaymentApprovalSaving] = useState(false);
  const paymentApprovalSignatureRef = useRef<HTMLCanvasElement>(null);
  const [paymentApprovalSignatureAvailable, setPaymentApprovalSignatureAvailable] = useState(false);

  const [duplicatingShipmentId, setDuplicatingShipmentId] = useState<string | null>(null);
  const [freightModalRow, setFreightModalRow] = useState<OperationRow | null>(null);
  const [savingFreightUpdate, setSavingFreightUpdate] = useState(false);
  const [freightUpdateForm, setFreightUpdateForm] = useState({ amount: '', reasonCode: 'waiting', reasonText: 'Bekleme oluştu', note: '' });
  const [assignmentSourcesLoading, setAssignmentSourcesLoading] = useState(false);
  const [fleetSearch, setFleetSearch] = useState('');
  const [activeBidRequestShipmentIds, setActiveBidRequestShipmentIds] = useState<string[]>([]);
  const [offerCollectionLoading, setOfferCollectionLoading] = useState(false);
  const [offerCollectionLoadingCode, setOfferCollectionLoadingCode] = useState('');
  const [offerCollectionConfirmRows, setOfferCollectionConfirmRows] = useState<OperationRow[]>([]);
  const [eligibleDrivers, setEligibleDrivers] = useState<Array<{ id: string; full_name: string | null; phone: string | null; distance_km: number | null }>>([]);
  const [eligibleDriversMeta, setEligibleDriversMeta] = useState<{ radiusKm: number; recencyMinutes: number; totalEligible: number; totalBase: number; whitelistActive: boolean } | null>(null);
  const [eligibleDriversLoading, setEligibleDriversLoading] = useState(false);
  const [locationCheckingRowId, setLocationCheckingRowId] = useState<string | null>(null);
  const [movingProcessRowId, setMovingProcessRowId] = useState<string | null>(null);
  const [recalculatingNearestHubs, setRecalculatingNearestHubs] = useState(false);
  const [calculatingRouteKms, setCalculatingRouteKms] = useState(false);
  const [routeDistanceCache, setRouteDistanceCache] = useState<Record<string, number | null>>({});
  const [showCreateSpotDriverModal, setShowCreateSpotDriverModal] = useState(false);
  const [creatingSpotDriver, setCreatingSpotDriver] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveModalMode, setArchiveModalMode] = useState<'create' | 'edit'>('create');
  const [archiveShipmentRow, setArchiveShipmentRow] = useState<OperationRow | null>(null);
  const [activeArchiveRecord, setActiveArchiveRecord] = useState<any | null>(null);
  const [archiveForm, setArchiveForm] = useState({ folderName: '', evrakSiraNo: '', invoiceNumber: '', hasFuelPurchase: false });
  const [archivePreviewCode, setArchivePreviewCode] = useState('');
  const [archiveFolderStartFrom, setArchiveFolderStartFrom] = useState('');
  const [archiveFolderOptions, setArchiveFolderOptions] = useState<string[]>([]);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [modalArchivePdfDocs, setModalArchivePdfDocs] = useState<any[]>([]);
  const [uploadingModalArchivePdf, setUploadingModalArchivePdf] = useState(false);
  const [pendingArchiveFile, setPendingArchiveFile] = useState<File | null>(null);
  const archiveModalRequestRef = useRef<string>('');
  const [uploadingShipmentDocumentKey, setUploadingShipmentDocumentKey] = useState<string | null>(null);
  const [deletingSliderDocument, setDeletingSliderDocument] = useState(false);
  const [newSpotDriverForm, setNewSpotDriverForm] = useState({
    full_name: '',
    phone: '',
    tc_kimlik_no: '',
    vehicle_type: '',
    cekici_plaka: '',
    dorse_plaka: '',
    city: '',
    district: '',
  });
  const [spotVehicleTypeOptions, setSpotVehicleTypeOptions] = useState<string[]>(DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS);
  const [kmCosts, setKmCosts] = useState({ ozmal: 12, spot: 18 });
  const [updatingRowStatusId, setUpdatingRowStatusId] = useState<string | null>(null);
  const [updatingRowSupplyStatusId, setUpdatingRowSupplyStatusId] = useState<string | null>(null);
  const [rowDocumentPreviews, setRowDocumentPreviews] = useState<Record<string, Partial<Record<TrackingDocumentBucket, TrackingDocumentPreview[]>>>>({});
  const [shipmentAuditLogs, setShipmentAuditLogs] = useState<any[]>([]);
  const [shipmentAuditLogsLoading, setShipmentAuditLogsLoading] = useState(false);
  const [documentSlider, setDocumentSlider] = useState<DocumentSliderState | null>(null);
  useEffect(() => {
    if (!documentSlider) return;
    const handler = (e: KeyboardEvent) => {
      if (documentSlider.docs.length <= 1) return;
      if (e.key === 'ArrowLeft') {
        setDocumentSlider(prev => prev ? { ...prev, currentIndex: Math.max(0, prev.currentIndex - 1) } : null);
      } else if (e.key === 'ArrowRight') {
        setDocumentSlider(prev => prev ? { ...prev, currentIndex: Math.min(prev.docs.length - 1, prev.currentIndex + 1) } : null);
      } else if (e.key === 'Escape') {
        setDocumentSlider(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [documentSlider]);
  const [columnWidthOverrides, setColumnWidthOverrides] = useState<Partial<Record<ColumnKey, number>>>({});
  const dragColumnRef = useRef<ColumnKey | null>(null);
  const resizeRef = useRef<{ key: ColumnKey; startX: number; startWidth: number } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarInnerRef = useRef<HTMLDivElement>(null);
  const [stickyScrollbarMetrics, setStickyScrollbarMetrics] = useState({ scrollWidth: 0, clientWidth: 0 });
  const [stickyScrollbarViewportStyle, setStickyScrollbarViewportStyle] = useState<{ left: number; width: number; visible: boolean }>({ left: 0, width: 0, visible: false });
  useEffect(() => {
    const tableEl = tableContainerRef.current;
    const stickyEl = stickyScrollbarRef.current;
    if (!tableEl || !stickyEl) return;

    let syncingFromTable = false;
    let syncingFromSticky = false;

    const syncMetrics = () => {
      setStickyScrollbarMetrics({
        scrollWidth: tableEl.scrollWidth,
        clientWidth: tableEl.clientWidth,
      });
      const rect = tableEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visible = rect.bottom > 0 && rect.top < viewportHeight && tableEl.scrollWidth > tableEl.clientWidth;
      setStickyScrollbarViewportStyle({
        left: Math.max(12, rect.left),
        width: Math.max(0, rect.width),
        visible,
      });
      stickyEl.scrollLeft = tableEl.scrollLeft;
    };

    const handleTableScroll = () => {
      if (syncingFromSticky) return;
      syncingFromTable = true;
      stickyEl.scrollLeft = tableEl.scrollLeft;
      syncingFromTable = false;
    };

    const handleStickyScroll = () => {
      if (syncingFromTable) return;
      syncingFromSticky = true;
      tableEl.scrollLeft = stickyEl.scrollLeft;
      syncingFromSticky = false;
    };

    syncMetrics();
    tableEl.addEventListener('scroll', handleTableScroll, { passive: true });
    stickyEl.addEventListener('scroll', handleStickyScroll, { passive: true });
    window.addEventListener('resize', syncMetrics);
    window.addEventListener('scroll', syncMetrics, { passive: true });

    return () => {
      tableEl.removeEventListener('scroll', handleTableScroll);
      stickyEl.removeEventListener('scroll', handleStickyScroll);
      window.removeEventListener('resize', syncMetrics);
      window.removeEventListener('scroll', syncMetrics);
    };
  }, [rows.length, externalRows?.length, visibleColumns.length, page, pageSize, showDetailPanel, constrainTableToViewport]);
  const [showActiveShipmentsModal, setShowActiveShipmentsModal] = useState(false);
  const [activeShipmentsDriverId, setActiveShipmentsDriverId] = useState<string | null>(null);
  const [activeShipmentsList, setActiveShipmentsList] = useState<any[]>([]);
  const [activeShipmentsLoading, setActiveShipmentsLoading] = useState(false);
  const [selectedActiveShipmentId, setSelectedActiveShipmentId] = useState<string | null>(null);
  const makeRouteDistanceKey = useCallback((
    lat1?: number | null,
    lon1?: number | null,
    lat2?: number | null,
    lon2?: number | null,
  ) => {
    if ([lat1, lon1, lat2, lon2].some((v) => v === null || v === undefined || Number.isNaN(Number(v)))) return '';

    return [
      Number(lat1).toFixed(6),
      Number(lon1).toFixed(6),
      Number(lat2).toFixed(6),
      Number(lon2).toFixed(6),
    ].join(',');
  }, []);

  const getCachedRouteKm = useCallback((
    lat1?: number | null,
    lon1?: number | null,
    lat2?: number | null,
    lon2?: number | null,
  ) => {
    const key = makeRouteDistanceKey(lat1, lon1, lat2, lon2);
    if (!key) return null;
    return routeDistanceCache[key] ?? null;
  }, [makeRouteDistanceKey, routeDistanceCache]);

  useEffect(() => {
    if (!assignRow) return;

    const pickupLat = assignRow.pickupLatitude;
    const pickupLon = assignRow.pickupLongitude;

    if (pickupLat == null || pickupLon == null) return;

    const targets: Array<{
      key: string;
      lat1: number | null | undefined;
      lon1: number | null | undefined;
      lat2: number | null | undefined;
      lon2: number | null | undefined;
    }> = [];

    vehicles.forEach((vehicle: any) => {
      const lat = vehicle.latitude != null ? Number(vehicle.latitude) : null;
      const lon = vehicle.longitude != null ? Number(vehicle.longitude) : null;
      const key = makeRouteDistanceKey(pickupLat, pickupLon, lat, lon);
      if (key && routeDistanceCache[key] === undefined) targets.push({ key, lat1: pickupLat, lon1: pickupLon, lat2: lat, lon2: lon });
    });

    applications.forEach((app: any) => {
      const lat = app.latest_latitude != null ? Number(app.latest_latitude) : null;
      const lon = app.latest_longitude != null ? Number(app.latest_longitude) : null;
      const key = makeRouteDistanceKey(pickupLat, pickupLon, lat, lon);
      if (key && routeDistanceCache[key] === undefined) targets.push({ key, lat1: pickupLat, lon1: pickupLon, lat2: lat, lon2: lon });
    });

    externalDrivers.forEach((driver: any) => {
      const lat = driver.latest_latitude != null ? Number(driver.latest_latitude) : null;
      const lon = driver.latest_longitude != null ? Number(driver.latest_longitude) : null;
      const key = makeRouteDistanceKey(pickupLat, pickupLon, lat, lon);
      if (key && routeDistanceCache[key] === undefined) targets.push({ key, lat1: pickupLat, lon1: pickupLon, lat2: lat, lon2: lon });
    });

    const uniqueTargets = Array.from(new Map(targets.map((item) => [item.key, item])).values());
    if (uniqueTargets.length === 0) return;

    let cancelled = false;

    const loadRouteDistances = async () => {
      const results = await Promise.all(
        uniqueTargets.map(async (item) => {
          const km = await haversineKm(item.lat1, item.lon1, item.lat2, item.lon2);
          return [item.key, km] as const;
        })
      );

      if (cancelled) return;

      setRouteDistanceCache((current) => {
        const next = { ...current };
        results.forEach(([key, km]) => {
          next[key] = km;
        });
        return next;
      });
    };

    loadRouteDistances();

    return () => {
      cancelled = true;
    };
  }, [assignRow, vehicles, applications, externalDrivers, makeRouteDistanceKey, routeDistanceCache]);

  const effectiveFlowStatusOptions = useMemo(() => (Array.isArray(flowStatusOptions) && flowStatusOptions.length > 0 ? flowStatusOptions : MANUAL_PROCESS_STATUS_OPTIONS), [flowStatusOptions]);

  const isStatusListScope = settingsKeyScope === 'general_list_2_fleet' || settingsKeyScope === 'general_list_2_spot';
  const isAccountingScope = settingsKeyScope === 'accounting_workflow';
  const isSpotLikeRow = (row: OperationRow) => {
    const logisticsType = String(row?.lojistikTuru || '').trim().toLowerCase();
    const externalFlag = String(row?.disNakliye || '').trim().toLowerCase();
    return logisticsType === 'spot' || externalFlag === 'evet';
  };

  const isDemurrageLockedForRow = (row: OperationRow | null | undefined) => {
    if (!row) return false;
    if (Number(row.demurrageManualEdit || 0) === 1) return false;
    if (isAccountingScope && !demurrageSettingsLoaded) return true;
    if (!demurrageLocked) return false;
    if (!demurrageTransitionDate) return true;
    const transitionStart = new Date(`${demurrageTransitionDate}T00:00:00`);
    if (Number.isNaN(transitionStart.getTime())) return true;
    const requestDate = new Date(String(row.yuklemeTarihi || row.createdAt || ''));
    if (Number.isNaN(requestDate.getTime())) return true;
    return requestDate >= transitionStart;
  };

  const savedTabs = useMemo(() => [MAIN_FILTER_TAB, ...(isStatusListScope ? [] : [FLOW_FILTER_TAB]), ...publicTabs, ...privateTabs], [isStatusListScope, publicTabs, privateTabs]);
  const activeSavedTab = useMemo(() => savedTabs.find((tab) => tab.id === activeTabId) || null, [savedTabs, activeTabId]);
  const canManageActiveTab = !!(activeSavedTab && activeSavedTab.id !== MAIN_TAB_ID && (activeSavedTab.visibility !== 'public' || String(activeSavedTab.ownerUserId || '') === String(user?.id || '')));
  const canManageAssignments = !externalRows;
  const canPaymentControlApprove = Boolean((user as any)?.isPaymentPreapprovalController);
  const canPaymentFinalApprove = Boolean((user as any)?.isPaymentFinalApprover);

  useEffect(() => {
    if (isStatusListScope && activeTabId === FLOW_TAB_ID) {
      setActiveTabId(MAIN_TAB_ID);
    }
  }, [activeTabId, isStatusListScope]);

  const getEffectiveWidth = (key: ColumnKey): number | undefined => {
    if (columnWidthOverrides[key] !== undefined && columnWidthOverrides[key]! > 0) return columnWidthOverrides[key];
    return getColumn(key)?.width;
  };

  const updateColumnWidth = (key: ColumnKey, width: number) => {
    setColumnWidthOverrides((current) => ({
      ...current,
      [key]: width > 0 ? width : undefined,
    }));
  };

  const handleResizeStart = (key: ColumnKey, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const startWidth = getEffectiveWidth(key) || 100;
    resizeRef.current = { key, startX: event.clientX, startWidth };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeRef.current) return;
      const delta = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(30, resizeRef.current.startWidth + delta);
      updateColumnWidth(resizeRef.current.key, newWidth);
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const activeColumns = useMemo(() => {
    return visibleColumns
      .filter((key) => key !== 'eSignAction' || ['invoice-approval-pending', 'invoice-check-approved'].includes(activeFlowTab))
      .map((key) => getColumn(key))
      .filter(Boolean) as ColumnDef[];
  }, [activeFlowTab, visibleColumns]);

  const applyRowPatch = (rowKey: string, patch: Partial<OperationRow>) => {
    setRows((prev) => prev.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
    setRowOverrides((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        ...patch,
      },
    }));
  };

  const commitCostEdit = async (row: OperationRow, field: 'beklemeBedeli' | 'hammaliye') => {
    if (savingCostCell?.rowKey === row.rowKey && savingCostCell.field === field) return;
    if (isAccountingLockedForRow(row)) {
      setEditingCostCell(null);
      setEditingCostValue('');
      toast.info(getAccountingLockedMessage());
      return;
    }
    if (field === 'beklemeBedeli' && isDemurrageLockedForRow(row)) {
      setEditingCostCell(null);
      setEditingCostValue('');
      toast.info('Geçiş tarihinden itibaren bekleme bedeli otomatik hesaplanır, manuel giriş yapılamaz');
      return;
    }

    const normalizedValue = editingCostValue.trim().replace(',', '.');
    const numericValue = Number(normalizedValue);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      toast.error('Geçerli bir bedel girin');
      return;
    }

    const roundedValue = Math.round(numericValue * 100) / 100;
    const payload = field === 'beklemeBedeli'
      ? { beklemeBedeli: roundedValue, genelToplam: Number(row.navlun || 0) + roundedValue + Number(row.hammaliye || 0) }
      : { hammaliyeBedeli: roundedValue, genelToplam: Number(row.navlun || 0) + Number(row.beklemeBedeli || 0) + roundedValue };

    try {
      setSavingCostCell({ rowKey: row.rowKey, field });
      const resp = await supplyService.update(row.id, payload);
      if (!resp?.success) {
        toast.error(resp?.message || 'Bedel güncellenemedi');
        return;
      }
      applyRowPatch(row.rowKey, buildFinancialPatch(row, field === 'beklemeBedeli' ? { beklemeBedeli: roundedValue } : { hammaliye: roundedValue }));
      toast.success('Bedel güncellendi');
      setEditingCostCell(null);
      setEditingCostValue('');
    } catch (error: any) {
      toast.error(error?.message || 'Bedel güncellenirken hata oluştu');
    } finally {
      setSavingCostCell(null);
    }
  };

  const commitInvoiceEdit = async (row: OperationRow, rawValue: string) => {
    const trimmed = String(rawValue || '').trim();
    const isExternalTransport = String(row.disNakliye || '').trim().toLocaleLowerCase('tr-TR') === 'evet';
    const invoiceStatus = trimmed ? (isExternalTransport ? 'faturalandirildi' : 'invoice-approval-pending') : row.islemDurum;
    const accountingStatus = trimmed ? 'invoice-approval-pending' : row.accountingStatus;

    try {
      const resp = await supplyService.update(row.id, {
        fatura_no: trimmed,
        processStatus: invoiceStatus,
      });

      if (resp?.success) {
        applyRowPatch(row.rowKey, {
          invoiceNumber: trimmed || '-',
          durum: trimmed ? 'faturalandirildi' : row.durum,
          islemDurum: invoiceStatus,
          accountingStatus,
        });
        toast.success(trimmed ? 'Fatura no kaydedildi ve durum güncellendi' : 'Fatura no temizlendi');
        return;
      }

      toast.error(resp?.message || 'Fatura no güncellenemedi');
    } catch (error: any) {
      toast.error(error?.message || 'Fatura no güncellenirken hata oluştu');
    } finally {
      setEditingInvoiceRowId(null);
      setEditingInvoiceValue('');
    }
  };

  const loadRows = async () => {
    setLoading(true);
    const startTime = Date.now();
    try {
      const [response, bidRequestResponse] = await Promise.all([
        dataService.getAll(dataParams),
        externalRows ? Promise.resolve(null) : bidService.getBidRequests({ status: 'open' }).catch(() => null),
      ]);
      const dbMs = (response as any)?._timing?.dbMs;
      const totalMs = (response as any)?._timing?.totalMs;
      const list = Array.isArray(response?.data) ? response.data : [];
      const responseUnitStopFee = Number((response as any)?.unitStopFee ?? NaN);
      const effectiveUnitStopFee = Number.isFinite(responseUnitStopFee) && responseUnitStopFee >= 0 ? responseUnitStopFee : unitStopFee;
      if (Number.isFinite(responseUnitStopFee) && responseUnitStopFee >= 0) {
        setUnitStopFee(responseUnitStopFee);
      }
      const mapped = list.map((item: any, index: number) => mapSupplyToRow(item, index, effectiveUnitStopFee, autoCalculateStopFees));

      const openBidRequests = Array.isArray((bidRequestResponse as any)?.data) ? (bidRequestResponse as any).data : [];
      setActiveBidRequestShipmentIds(openBidRequests.map((item: any) => String(item.shipment_id || item.shipmentId || '')).filter(Boolean));

      mapped.sort((a: OperationRow, b: OperationRow) => {
        const left = new Date(b.createdAt || b.updatedAt || b.aracAtamaTarihi || b.yuklemeTarihi || 0).getTime();
        const right = new Date(a.createdAt || a.updatedAt || a.aracAtamaTarihi || a.yuklemeTarihi || 0).getTime();
        return left - right;
      });

      setRows(mapped);
      const elapsed = Date.now() - startTime;
      setLoadTimeMs(elapsed);
      console.log(`[FlexibleTracking] Loaded ${mapped.length} rows in ${elapsed}ms (DB: ${dbMs}ms, Server: ${totalMs}ms)`);
    } catch (error) {
      console.error('Tracking basic load failed:', error);
      toast.error('Operasyon verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const loadArchiveRows = async () => {
    try {
      const res: any = await archiveService.getAll();
      const items = Array.isArray(res?.data) ? res.data : [];
      setArchiveRows(items);
      setArchiveRecordByShipmentId(items.reduce((acc: Record<string, any>, item: any) => {
        if (item?.shipmentId && !acc[String(item.shipmentId)]) acc[String(item.shipmentId)] = item;
        return acc;
      }, {}));
    } catch {
      setArchiveRows([]);
    }
  };

  const archiveDisplayRows = useMemo<OperationRow[]>(() => {
    const shipmentMap = new Map(effectiveRowsWithLkm.map((row) => [String(row.id), row]));
    return archiveRows
      .map((item: any) => {
        const shipmentRow = shipmentMap.get(String(item.shipmentId));
        if (!shipmentRow) return null;
        return {
          ...shipmentRow,
          rowKey: `archive-${item.id}-${shipmentRow.rowKey}`,
          archiveCode: item.archiveCode || '-',
          folderName: item.folderName || '-',
          invoiceNumber: item.invoiceNumber || '-',
          evrakSiraNo: item.evrakSiraNo ? String(item.evrakSiraNo) : item.folderNumber ? String(item.folderNumber) : '',
          hasFuelPurchase: item.hasFuelPurchase ? 'Var' : 'Yok',
        };
      })
      .filter(Boolean) as OperationRow[];
  }, [archiveRows, effectiveRowsWithLkm]);

  useEffect(() => {
    if (Object.keys(archiveRecordByShipmentId).length === 0) return;
    setRows((current) => current.map((row) => {
      const archiveRecord = archiveRecordByShipmentId[String(row.id)];
      if (!archiveRecord) return row;
      return {
        ...row,
        archiveCode: archiveRecord.archiveCode || row.archiveCode || '-',
        folderName: archiveRecord.folderName || row.folderName || '-',
        folderNumber: archiveRecord.folderNumber != null ? String(archiveRecord.folderNumber) : (row.folderNumber || ''),
        evrakSiraNo: archiveRecord.evrakSiraNo != null ? String(archiveRecord.evrakSiraNo) : (row.evrakSiraNo || ''),
        invoiceNumber: archiveRecord.invoiceNumber || row.invoiceNumber || '-',
      };
    }));
  }, [archiveRecordByShipmentId]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const qParam = String(searchParams.get('q') || '').trim();
    if (!qParam) return;
    if (handledSearchQParamRef.current === qParam) return;
    handledSearchQParamRef.current = qParam;
    setSearchInput(qParam);
    setSearch(qParam);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    const shipmentParam = String(searchParams.get('shipment') || '').trim();
    if (!shipmentParam) return;
    if (handledShipmentParamRef.current === shipmentParam) return;

    const archiveMatch = archiveDisplayRows.find((row) => String(row.id) === shipmentParam || String(row.sevkKodu) === shipmentParam);
    const activeMatch = archiveMatch || effectiveRowsWithLkm.find((row) => String(row.id) === shipmentParam || String(row.sevkKodu) === shipmentParam);
    if (!activeMatch) return;

    handledShipmentParamRef.current = shipmentParam;
    if (search) setSearch('');
    if (!showDetailPanel) setShowDetailPanel(true);
    if (detailTab !== 'info') setDetailTab('info');
    if (!detailRow || detailRow.rowKey !== activeMatch.rowKey) setSelectedRowKey(activeMatch.rowKey);
  }, [searchParams, effectiveRowsWithLkm, archiveDisplayRows, search, showDetailPanel, detailTab, detailRow]);

  useEffect(() => {
    if (!detailRow?.id || activeFlowTab !== 'all' || archiveRecordByShipmentId[String(detailRow.id)] || hideArchiveTab) return;
    archiveService.checkArchive(String(detailRow.id))
      .then((res: any) => {
        if (res?.data) {
          setArchiveRecordByShipmentId((current) => ({ ...current, [String(detailRow.id)]: res.data }));
        }
      })
      .catch(() => {});
  }, [detailRow?.id, activeFlowTab, archiveRecordByShipmentId]);

  const applyArchiveRecordToLocalRows = useCallback((shipmentId: string, record: any) => {
    if (externalRows) return;
    setRows((current) => current.map((row) => {
      if (String(row.id) !== String(shipmentId)) return row;
      return {
        ...row,
        archiveCode: record?.archiveCode || row.archiveCode || '-',
        folderName: record?.folderName || '-',
        invoiceNumber: record?.invoiceNumber || '-',
        folderNumber: record?.folderNumber != null ? String(record.folderNumber) : row.folderNumber,
        evrakSiraNo: record?.evrakSiraNo != null ? String(record.evrakSiraNo) : row.evrakSiraNo,
      };
    }));
  }, [externalRows]);

  const clearArchiveRecordFromLocalRows = useCallback((shipmentId: string) => {
    if (externalRows) return;
    setRows((current) => current.map((row) => {
      if (String(row.id) !== String(shipmentId)) return row;
      return {
        ...row,
        archiveCode: '-',
        folderName: '-',
        folderNumber: undefined,
        evrakSiraNo: '',
      };
    }));
    setArchiveRows((current) => current.filter((item: any) => String(item?.shipmentId) !== String(shipmentId)));
    setArchiveRecordByShipmentId((current) => {
      const next = { ...current };
      delete next[String(shipmentId)];
      return next;
    });
  }, [externalRows]);

  const getOptimisticNextArchiveCode = useCallback(() => {
    const numericCodes = archiveRows
      .map((item: any) => Number(item?.archiveCode || 0))
      .filter((value) => Number.isFinite(value) && value > 0);
    const next = (numericCodes.length > 0 ? Math.max(...numericCodes) : 0) + 1;
    return String(next);
  }, [archiveRows]);

  const openArchiveCreateModal = (row: OperationRow) => {
    const requestKey = String(row.id);
    setArchiveModalMode('create');
    setArchiveShipmentRow(row);
    setActiveArchiveRecord(null);
    setArchiveForm({ folderName: '', evrakSiraNo: '', invoiceNumber: row.invoiceNumber === '-' ? '' : (row.invoiceNumber || ''), hasFuelPurchase: false });
    setArchivePreviewCode(getOptimisticNextArchiveCode());
    setArchiveFolderStartFrom('');
    setPendingArchiveFile(null);
    setArchiveModalOpen(true);
    archiveService.getNextCode().then((response: any) => {
      if (archiveModalRequestRef.current !== requestKey) return;
      setArchivePreviewCode(response?.data?.archiveCode || '');
    }).catch(() => {
      if (archiveModalRequestRef.current === requestKey) setArchivePreviewCode(getOptimisticNextArchiveCode());
    });
    archiveService.getNextFolderNumber().then((response: any) => {
      if (archiveModalRequestRef.current !== requestKey) return;
      setArchiveForm((current) => ({ ...current, evrakSiraNo: String(response?.data?.folderNumber || '') }));
    }).catch(() => null);
  };

  const openArchiveEditModal = (row: OperationRow, archiveRecord: any) => {
    const resolvedArchiveRecord = {
      ...archiveRecord,
      evrakSiraNo: archiveRecord?.evrakSiraNo != null && String(archiveRecord.evrakSiraNo).trim() !== ''
        ? String(archiveRecord.evrakSiraNo)
        : archiveRecord?.folderNumber != null
          ? String(archiveRecord.folderNumber)
          : '',
    };
    setArchiveModalMode('edit');
    setArchiveShipmentRow(row);
    setActiveArchiveRecord(resolvedArchiveRecord);
    setArchiveForm({
      folderName: resolvedArchiveRecord?.folderName || '',
      evrakSiraNo: resolvedArchiveRecord.evrakSiraNo,
      invoiceNumber: resolvedArchiveRecord?.invoiceNumber || '',
      hasFuelPurchase: Boolean(resolvedArchiveRecord?.hasFuelPurchase),
    });
    setArchivePreviewCode(resolvedArchiveRecord?.archiveCode || row.archiveCode || '');
    setArchiveFolderStartFrom('');
    setPendingArchiveFile(null);
    setArchiveModalOpen(true);
  };

  const handleOpenArchiveModal = (row: OperationRow) => {
    archiveModalRequestRef.current = String(row.id);
    archiveService.checkArchive(String(row.id)).then((response: any) => {
      if (archiveModalRequestRef.current !== String(row.id)) return;
      if (response?.data) {
        openArchiveEditModal(row, response.data);
        return;
      }
      openArchiveCreateModal(row);
    }).catch(() => {
      if (archiveModalRequestRef.current !== String(row.id)) return;
      openArchiveCreateModal(row);
    });
  };

  const handleRestoreArchive = async (row: OperationRow) => {
    if (!row?.id) return;
    if (!window.confirm(`#${row.sevkKodu || row.id} arşivden geri alınsın mı?`)) return;
    try {
      const response: any = await archiveService.restore(String(row.id));
      clearArchiveRecordFromLocalRows(String(row.id));
      if (archiveShipmentRow && String(archiveShipmentRow.id) === String(row.id)) {
        setArchiveModalOpen(false);
        setArchiveShipmentRow(null);
        setActiveArchiveRecord(null);
      }
      toast.success(response?.message || 'Kayıt arşivden geri alındı');
    } catch (error: any) {
      toast.error(error?.message || 'Arşivden geri alma başarısız');
    }
  };

  const handleArchivePdfUpload = async () => {
    if (!activeArchiveRecord?.id) {
      toast.error('Arşiv kaydı bulunamadı');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingModalArchivePdf(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'archive');
        formData.append('entityId', String(activeArchiveRecord.id));
        formData.append('documentType', 'archive-pdf');
        formData.append('name', file.name);
        await documentService.uploadDocument(formData);
        const response: any = await documentService.getDocuments({ entityType: 'archive', entityId: String(activeArchiveRecord.id) });
        setModalArchivePdfDocs(Array.isArray(response?.data) ? response.data : []);
        toast.success('PDF yüklendi');
      } catch {
        toast.error('PDF yüklenemedi');
      } finally {
        setUploadingModalArchivePdf(false);
      }
    };
    input.click();
  };

  const handleArchiveSubmit = async () => {
    if (!archiveShipmentRow) return;
    if (!archiveForm.folderName) {
      toast.error('Klasör no seçiniz');
      return;
    }
    if (!archiveForm.evrakSiraNo) {
      toast.error('Evrak sıra no giriniz');
      return;
    }
    setArchiveSaving(true);
    try {
      if (archiveModalMode === 'edit' && activeArchiveRecord?.id) {
        const response: any = await archiveService.update(String(activeArchiveRecord.id), {
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          invoiceNumber: archiveForm.invoiceNumber,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        });
        if (!response?.success) throw new Error(response?.message || 'Arşiv güncelleme başarısız');
        const updatedRecord = {
          ...(activeArchiveRecord || {}),
          archiveCode: activeArchiveRecord?.archiveCode || archivePreviewCode,
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          invoiceNumber: archiveForm.invoiceNumber,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        };
        applyArchiveRecordToLocalRows(String(archiveShipmentRow.id), updatedRecord);
        setActiveArchiveRecord(updatedRecord);
        toast.success(response?.message || 'Arşiv kaydı güncellendi');
      } else {
        const response: any = await archiveService.create({
          shipmentId: archiveShipmentRow.id,
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          invoiceNumber: archiveForm.invoiceNumber,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        });
        if (!response?.success) throw new Error(response?.message || 'Arşive gönderme başarısız');
        const createdRecord = {
          id: response?.data?.id,
          archiveCode: response?.data?.archiveCode || archivePreviewCode,
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          invoiceNumber: archiveForm.invoiceNumber,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        };
        applyArchiveRecordToLocalRows(String(archiveShipmentRow.id), createdRecord);
        setActiveArchiveRecord(createdRecord);
        if (pendingArchiveFile && createdRecord.id) {
          try {
            const formData = new FormData();
            formData.append('file', pendingArchiveFile);
            formData.append('entityType', 'archive');
            formData.append('entityId', String(createdRecord.id));
            formData.append('documentType', 'archive-pdf');
            formData.append('name', pendingArchiveFile.name);
            await documentService.uploadDocument(formData);
            toast.success('PDF yüklendi');
          } catch {
            toast.error('PDF yüklenemedi, sonra tekrar deneyin');
          }
        }
        toast.success('Fiziksel arşive taşındı');
      }
      setArchiveModalOpen(false);
      setPendingArchiveFile(null);
      if (!externalRows) await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Arşiv işlemi başarısız');
    } finally {
      setArchiveSaving(false);
    }
  };

  useEffect(() => {
    if (externalRows) return;
    if (!demurrageSettingsLoaded) return;
    void loadRows();
    if (!hideArchiveTab) void loadArchiveRows();
  }, [externalRows, hideArchiveTab, unitStopFee, autoCalculateStopFees, demurrageSettingsLoaded, dataService, dataParams]);

  useEffect(() => {
    settingsService.getSettingValue('settings_system_preferences', { extraStopFee: '0', autoCalculateStopFees: true, demurrageEnabled: true, demurrageTransitionDate: '' }).then((val: any) => {
      setUnitStopFee(Number(val?.extraStopFee || 0));
      setAutoCalculateStopFees(val?.autoCalculateStopFees !== false);
      const demurrageEnabled = val?.demurrageEnabled !== false;
      const transitionDate = String(val?.demurrageTransitionDate || '').trim();
      setDemurrageTransitionDate(transitionDate);
      let locked = false;
      if (demurrageEnabled && transitionDate) {
        try {
          locked = new Date() > new Date(`${transitionDate}T23:59:59`);
        } catch {
          locked = false;
        }
      }
      setDemurrageLocked(locked);
    }).catch(() => {}).finally(() => setDemurrageSettingsLoaded(true));
  }, []);

  useEffect(() => {
    settingsService.getSettingValue('settings_optimization', { ozmalKmCost: '12', spotKmCost: '18' }).then((val: any) => {
      setKmCosts({
        ozmal: Number(val?.ozmalKmCost || 12),
        spot: Number(val?.spotKmCost || 18),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isStatusListScope) {
      setSpotVehicleTypeOptions(DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS);
      return;
    }
    staticDefinitionService.getByKey('shipment_vehicle_type')
      .then((response: any) => {
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        const values = items.map((item: any) => normalizeUpper(String(item.item_value || item.value || '').trim())).filter(Boolean);
        if (values.length > 0) setSpotVehicleTypeOptions(values);
      })
      .catch(() => {
        setSpotVehicleTypeOptions(DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS);
      });
  }, [isStatusListScope]);

  useEffect(() => {
    staticDefinitionService.getByKey('archive_folder_numbers')
      .then((response: any) => {
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        setArchiveFolderOptions(items.map((item: any) => String(item.value || item.item_value || '').trim()).filter(Boolean));
      })
      .catch(() => setArchiveFolderOptions([]));
  }, []);

  useEffect(() => {
    citiesService.getAll()
      .then((response: any) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setCityFilterOptions(
          list
            .map((city: any) => String(city.name || city.name_ascii || '').trim())
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'tr-TR'))
        );
      })
      .catch(() => setCityFilterOptions([]));
  }, []);

  const loadAssignmentSources = async () => {
    try {
      setAssignmentSourcesLoading(true);
      const [vehiclesRes, internalDriversRes, applicationsRes, externalDriversRes, corporateAccountsRes] = await Promise.all([
        vehicleService.getVehicles().catch(() => ({ data: [] })),
        driverService.getDrivers({ type: 'internal', status: 'active' }).catch(() => ({ data: [] })),
        driverService.getApplications({ status: 'approved', all: true }).catch(() => ({ data: [] })),
        driverService.getDrivers({ type: 'external', status: 'active' }).catch(() => ({ data: [] })),
        userService.getUsers({ role: 'corporate-external-transport', status: 'active' }).catch(() => ({ data: [] })),
      ]);
      setVehicles(Array.isArray(vehiclesRes?.data) ? vehiclesRes.data : []);
      setInternalDrivers(Array.isArray(internalDriversRes?.data) ? internalDriversRes.data : []);
      setApplications(Array.isArray(applicationsRes?.data) ? applicationsRes.data : []);
      setExternalDrivers(Array.isArray(externalDriversRes?.data) ? externalDriversRes.data : []);
      setCorporateCarrierAccounts(Array.isArray(corporateAccountsRes?.data) ? corporateAccountsRes.data : []);
    } catch (error) {
      console.error('Assignment sources load failed:', error);
      toast.error('Özmal araç ve sürücüler yüklenemedi');
    } finally {
      setAssignmentSourcesLoading(false);
    }
  };

  useEffect(() => {
    if (!showAssignModal || !canManageAssignments) return;
    void loadAssignmentSources();
  }, [showAssignModal, canManageAssignments]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!cellFilterMenu) return;
    const closeMenu = () => setCellFilterMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('resize', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('resize', closeMenu);
    };
  }, [cellFilterMenu]);

  useEffect(() => {
    if (!archiveModalOpen || !activeArchiveRecord?.id) {
      setModalArchivePdfDocs([]);
      return;
    }
    documentService.getDocuments({ entityType: 'archive', entityId: String(activeArchiveRecord.id) })
      .then((response: any) => setModalArchivePdfDocs(Array.isArray(response?.data) ? response.data : []))
      .catch(() => setModalArchivePdfDocs([]));
  }, [archiveModalOpen, activeArchiveRecord?.id]);

  useEffect(() => {
    if (!user?.id) {
      setViewLoaded(true);
      return;
    }

    let cancelled = false;

    const loadSavedViewAndTabs = async () => {
      try {
        const [savedView, savedFilterTabs, sharedFilterTabs] = await Promise.all([
          settingsService.getMySettingValue<SavedTableView | null>(getFlexibleTrackingBasicViewSettingKey(user.id, settingsKeyScope), null).catch(() => null),
          settingsService.getMySettingValue<SavedFilterTab[] | null>(getFlexibleTrackingSavedTabsSettingKey(user.id, settingsKeyScope), null).catch(() => null),
          settingsService.getSharedSettingValue<SavedFilterTab[] | null>(getFlexibleTrackingSharedTabsSettingKey(settingsKeyScope), null).catch(() => null),
        ]);

        if (cancelled) return;

        if (savedView) {
          setVisibleColumns(sanitizeVisibleColumns(savedView.visibleColumns, defaultVisibleColumns));
          setPageSize(Number(savedView.pageSize || 25));
          setSortKey(savedView.sortKey && getColumn(savedView.sortKey) ? savedView.sortKey : null);
          setSortDirection(savedView.sortDirection === 'desc' ? 'desc' : 'asc');
          if (savedView.groupBy) setGroupBy(sanitizeGroupBy(savedView.groupBy));
          if (savedView.columnWidths) {
            const valid: Partial<Record<ColumnKey, number>> = {};
            for (const [key, width] of Object.entries(savedView.columnWidths)) {
              if (getColumn(key as ColumnKey) && typeof width === 'number' && width > 0) {
                valid[key as ColumnKey] = width;
              }
            }
            setColumnWidthOverrides(valid);
          }
        }

        const userTabs = Array.isArray(savedFilterTabs)
          ? savedFilterTabs
              .filter((tab) => tab && tab.id !== MAIN_TAB_ID && String(tab.name || '').trim())
              .map((tab) => normalizeSavedTab(tab, 'private', user.id, user.name || user.email || ''))
          : [];

        const commonTabs = Array.isArray(sharedFilterTabs)
          ? sharedFilterTabs
              .filter((tab) => tab && tab.id !== MAIN_TAB_ID && String(tab.name || '').trim())
              .map((tab) => normalizeSavedTab(tab, 'public'))
          : [];

        setPrivateTabs(userTabs);
        setPublicTabs(commonTabs);
      } finally {
        if (!cancelled) setViewLoaded(true);
      }
    };

    void loadSavedViewAndTabs();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const renderCell = (row: OperationRow, column: ColumnDef) => {
    const rawValue = row[column.key];
    const value = column.format ? column.format(rawValue, row) : rawValue;
    return value == null || value === '' ? '-' : String(value);
  };

  const searchableRows = useMemo(() => {
    return effectiveRowsWithLkm.map((row) => ({
      ...row,
      __searchText: normalizeSearchText(
        [
          row.sevkKodu,
          row.planKodu,
          row.durum,
          getDurumText(row.durum),
          row.islemDurum,
          formatSlugLabel(row.islemDurum),
          row.cekici,
          row.dorse,
          row.surucuAdi,
          row.irtibatTelefon,
          row.surucuTelefon,
          row.tcKimlikNo,
          row.faturaBasligi,
          row.disNakliye,
          row.yuklemeMusterisi,
          row.bosaltmaMusterisi,
          row.yuklemeIli,
          row.yuklemeIlcesi,
          row.bosaltmaIli,
          row.teslimIlcesi,
          row.yuklemeAdres,
          row.teslimAdres,
          row.lojistikTuru,
          row.hizmetTuru,
          row.istenenKasaTipi,
          row.operasyonNot,
          row.araNoktalar,
          row.durakBedeli,
          row.accountingStatus,
          getAccountingStatusText(row.accountingStatus),
          row.mailAtildi,
          row.mailGonderimTarihi,
          row.spotKurumsalFirma,
          row.spotSource,
          row.spotEmail,
          row.spotEhliyetSinifi,
          row.spotSrcNo,
          row.spotDeneyimYil,
          row.spotSehirIlce,
          row.spotKontrolSorumlusu,
          row.spotIkametKonum,
        ].join(' '),
      ),
    }));
  }, [effectiveRowsWithLkm]);

  const searchableArchiveRows = useMemo(() => {
    return archiveDisplayRows.map((row) => ({
      ...row,
      __searchText: normalizeSearchText(
        [
          row.archiveCode,
          row.folderName,
          row.folderNumber,
          row.evrakSiraNo,
          row.invoiceNumber,
          row.sevkKodu,
          row.planKodu,
          row.durum,
          getDurumText(row.durum),
          row.islemDurum,
          formatSlugLabel(row.islemDurum),
          row.cekici,
          row.dorse,
          row.surucuAdi,
          row.surucuTelefon,
          row.tcKimlikNo,
          row.faturaBasligi,
          row.disNakliye,
          row.yuklemeMusterisi,
          row.bosaltmaMusterisi,
          row.yuklemeIli,
          row.yuklemeIlcesi,
          row.bosaltmaIli,
          row.teslimIlcesi,
          row.yuklemeAdres,
          row.teslimAdres,
          row.lojistikTuru,
          row.hizmetTuru,
          row.istenenKasaTipi,
          row.operasyonNot,
          row.araNoktalar,
          row.accountingStatus,
          getAccountingStatusText(row.accountingStatus),
          row.mailAtildi,
          row.mailGonderimTarihi,
        ].join(' '),
      ),
    }));
  }, [archiveDisplayRows]);

  const getFilterComparableCellValue = (row: OperationRow, key: ColumnKey) => {
    const bucket = getDocumentBucketForColumn(key);
    if (bucket) {
      const docs = rowDocumentPreviews[row.rowKey]?.[bucket] || [];
      return docs.length > 0 ? `${docs.length} belge` : '';
    }
    return getComparableCellValue(row, key);
  };

  const evaluateFilter = (row: OperationRow, rule: FilterRule) => {
    if (rule.column === 'surucuSonKonum' && rule.operator === 'minutesOver') {
      const minimumMinutes = Number(rule.value);
      if (!Number.isFinite(minimumMinutes) || minimumMinutes < 0) return true;
      const locationTimestamp = new Date(String(row.surucuSonKonum || '')).getTime();
      if (!Number.isFinite(locationTimestamp)) return true;
      return (Date.now() - locationTimestamp) / 60000 >= minimumMinutes;
    }

    if (DATE_FILTER_COLUMNS.has(rule.column)) {
      const cellDate = getDateFilterComparableValue(row[rule.column]);
      const ruleDate = getDateFilterComparableValue(rule.value);

      switch (rule.operator) {
        case 'dateEquals':
          return !ruleDate || !cellDate ? true : cellDate === ruleDate;
        case 'dateOnOrAfter':
          return !ruleDate || !cellDate ? true : cellDate >= ruleDate;
        case 'dateOnOrBefore':
          return !ruleDate || !cellDate ? true : cellDate <= ruleDate;
        case 'dateRange': {
          const [startRaw, endRaw] = String(rule.value).split(',');
          const start = getDateFilterComparableValue(startRaw);
          const end = getDateFilterComparableValue(endRaw);
          if (!start && !end) return true;
          if (!cellDate) return true;
          if (start && end) return cellDate >= start && cellDate <= end;
          return start ? cellDate >= start : cellDate <= end;
        }
        case 'dateToday':
        case 'dateThisWeek':
        case 'dateThisMonth':
        case 'dateLastMonth': {
          const range = getDatePresetRange(rule.operator);
          return !range || !cellDate ? true : cellDate >= range.start && cellDate <= range.end;
        }
        default:
          break;
      }
    }

    const cell = normalizeSearchText(getFilterComparableCellValue(row, rule.column));
    const value = normalizeSearchText(rule.value);

    if (CITY_FILTER_COLUMNS.has(rule.column) && value.includes(',')) {
      const cities = value.split(',').map((item) => item.trim()).filter(Boolean);
      if (cities.length === 0) return true;
      const inList = cities.some((city) => cell === city);
      switch (rule.operator) {
        case 'contains':
        case 'equals':
          return inList;
        case 'notContains':
        case 'notEquals':
          return !inList;
        default:
          return true;
      }
    }

    switch (rule.operator) {
      case 'contains':
        return value ? cell.includes(value) : true;
      case 'notContains':
        return value ? !cell.includes(value) : true;
      case 'equals':
        return value ? cell === value : true;
      case 'notEquals':
        return value ? cell !== value : true;
      case 'empty':
        return cell === '' || cell === '-';
      case 'notEmpty':
        return cell !== '' && cell !== '-';
      default:
        return true;
    }
  };

  const filteredRows = useMemo(() => {
    let result = searchableRows;
    const q = normalizeSearchText(search);

    if (q) {
      result = result.filter((row) => row.__searchText.includes(q));
    }

    const activeFilters = filters.filter((rule) => ['empty', 'notEmpty', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth'].includes(rule.operator) || normalizeSearchText(rule.value));

    if (activeFilters.length > 0) {
      result = result.filter((row) => {
        let accepted = evaluateFilter(row, activeFilters[0]);

        for (let index = 1; index < activeFilters.length; index += 1) {
          const rule = activeFilters[index];
          const next = evaluateFilter(row, rule);
          accepted = rule.join === 'or' ? accepted || next : accepted && next;
        }

        return accepted;
      });
    }

    if (showOnlySelectedRows && selectedRowKeys.length > 0) {
      const selectedSet = new Set(selectedRowKeys);
      result = result.filter((row) => selectedSet.has(row.rowKey));
    }

    return result;
  }, [searchableRows, search, filters, showOnlySelectedRows, selectedRowKeys, rowDocumentPreviews]);

  const filteredArchiveRows = useMemo(() => {
    let result = searchableArchiveRows;
    const q = normalizeSearchText(search);

    if (q) {
      result = result.filter((row) => row.__searchText.includes(q));
    }

    const activeFilters = filters.filter((rule) => ['empty', 'notEmpty', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth'].includes(rule.operator) || normalizeSearchText(rule.value));

    if (activeFilters.length > 0) {
      result = result.filter((row) => {
        let accepted = evaluateFilter(row, activeFilters[0]);

        for (let index = 1; index < activeFilters.length; index += 1) {
          const rule = activeFilters[index];
          const next = evaluateFilter(row, rule);
          accepted = rule.join === 'or' ? accepted || next : accepted && next;
        }

        return accepted;
      });
    }

    if (showOnlySelectedRows && selectedRowKeys.length > 0) {
      const selectedSet = new Set(selectedRowKeys);
      result = result.filter((row) => selectedSet.has(row.rowKey));
    }

    return result;
  }, [searchableArchiveRows, search, filters, showOnlySelectedRows, selectedRowKeys, rowDocumentPreviews]);

  const flowStats = useMemo(() => {
    const stats: Record<string, number> = { all: filteredRows.length };
    for (const option of effectiveFlowStatusOptions) {
      stats[option.value] = filteredRows.filter((row) => String(getEffectiveFlowStatus(row)) === option.value).length;
    }
    if (!hideArchiveTab) stats.arsiv = filteredArchiveRows.length;
    return stats;
  }, [filteredRows, effectiveFlowStatusOptions, hideArchiveTab, filteredArchiveRows]);

  const flowFilteredRows = useMemo(() => {
    if (activeFlowTab === 'all') return filteredRows;
    if (!hideArchiveTab && activeFlowTab === 'arsiv') return filteredArchiveRows;
    return filteredRows.filter((row) => String(getEffectiveFlowStatus(row)) === activeFlowTab);
  }, [filteredRows, activeFlowTab, filteredArchiveRows, hideArchiveTab]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return prioritizeSharedInvoiceRows(flowFilteredRows);

    return prioritizeSharedInvoiceRows([...flowFilteredRows].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aDate = Date.parse(String(aValue || ''));
      const bDate = Date.parse(String(bValue || ''));
      const bothDates = !Number.isNaN(aDate) && !Number.isNaN(bDate);

      if (bothDates) {
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      const result = String(aValue ?? '').localeCompare(String(bValue ?? ''), 'tr-TR', {
        numeric: true,
        sensitivity: 'base',
      });

      return sortDirection === 'asc' ? result : -result;
    }));
  }, [flowFilteredRows, sortKey, sortDirection]);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', rows: sortedRows }];

    const map = new Map<string, OperationRow[]>();
    const column = getColumn(groupBy);

    for (const row of sortedRows) {
      const rawValue = row[groupBy];
      let label = String(column?.format ? column.format(rawValue, row) : rawValue || '-');
      if (groupBy === 'durum') label = getDurumText(String(rawValue));
      if (groupBy === 'islemDurum') label = formatSlugLabel(String(rawValue));
      if (groupBy === 'accountingStatus') label = getAccountingStatusText(String(rawValue));
      if (!map.has(label)) map.set(label, []);
      map.get(label)?.push(row);
    }

    return Array.from(map.entries()).map(([key, groupRows]) => ({
      key,
      label: key,
      rows: groupRows,
    }));
  }, [groupBy, sortedRows]);

  useEffect(() => {
    if (groupBy === 'none') {
      setCollapsedGroups((current) => {
        const next = { ...current };
        const invoiceGroupKeys = new Set(
          sortedRows
            .filter((row) => String(row.invoiceGroupKey || '').trim() && Number(row.invoiceGroupCount || 0) > 1)
            .map((row) => `invoice-group:${String(row.invoiceGroupKey || '').trim()}`)
        );
        for (const key of invoiceGroupKeys) {
          if (!(key in next)) next[key] = true;
        }
        return next;
      });
      return;
    }
    setCollapsedGroups((current) => {
      const next: Record<string, boolean> = {};
      for (const group of groupedRows) {
        next[group.key] = current[group.key] ?? true;
      }
      for (const row of sortedRows) {
        const invoiceGroupKey = String(row.invoiceGroupKey || '').trim();
        if (invoiceGroupKey && Number(row.invoiceGroupCount || 0) > 1) {
          const key = `invoice-group:${invoiceGroupKey}`;
          next[key] = current[key] ?? true;
        }
      }
      return next;
    });
  }, [groupBy, groupedRows, sortedRows]);

  const toggleGroupCollapse = (groupKey: string) => {
    setCollapsedGroups((current) => ({
      ...current,
      [groupKey]: !(current[groupKey] ?? true),
    }));
  };

  const flatRowsForPaging = useMemo(() => {
    return groupedRows.flatMap((group) => {
      if (groupBy === 'none') return buildInvoiceGroupItems(group.rows, collapsedGroups);
      const isCollapsed = collapsedGroups[group.key] ?? true;
      if (isCollapsed) return [{ __group: true, group }];
      return [{ __group: true, group }, ...buildInvoiceGroupItems(group.rows, collapsedGroups)];
    });
  }, [groupBy, groupedRows, collapsedGroups]);

  const totalPages = Math.max(1, Math.ceil(flatRowsForPaging.length / pageSize));

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return flatRowsForPaging.slice(start, start + pageSize);
  }, [flatRowsForPaging, page, pageSize, totalPages]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);


  useEffect(() => {
    const activeDocumentBuckets = activeColumns
      .map((column) => getDocumentBucketForColumn(column.key))
      .filter(Boolean) as TrackingDocumentBucket[];

    if (activeDocumentBuckets.length === 0) return;

    const pageRows = paginatedItems.filter((item: any) => !item.__group && !item.__invoiceGroup) as OperationRow[];
    const rowsToLoad = pageRows.filter((row) => {
      const cached = rowDocumentPreviews[row.rowKey] || {};
      return activeDocumentBuckets.some((bucket) => !cached[bucket]);
    });

    if (rowsToLoad.length === 0) return;

    let cancelled = false;

    const run = async () => {
      const entries = await Promise.all(
        rowsToLoad.map(async (row) => {
          const response: any = await documentService.getDocuments({ entityType: 'shipment', entityId: row.id }).catch(() => ({ data: [] }));
          const items = Array.isArray(response?.data) ? response.data : [];
          const bucketEntries = activeDocumentBuckets.map((bucket) => {
            const docs = uniqueDocuments(
              items
                .filter((doc: any) => matchesDocumentBucket(doc, bucket))
                .map(normalizeDocument),
            );
            return [bucket, docs] as const;
          });
          return [row.rowKey, Object.fromEntries(bucketEntries)] as const;
        }),
      );

      if (cancelled) return;

      setRowDocumentPreviews((current) => {
        const next = { ...current };
        for (const [rowKey, bucketMap] of entries) {
          next[rowKey] = { ...(next[rowKey] || {}), ...bucketMap };
        }
        return next;
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [activeColumns, paginatedItems]);

  const toggleSort = (key: ColumnKey) => {
    setPage(1);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortKey(null);
      setSortDirection('asc');
    }
  };

  const moveColumn = (key: ColumnKey, direction: 'left' | 'right') => {
    setVisibleColumns((current) => {
      const index = current.indexOf(key);
      if (index < 0) return current;
      const targetIndex = direction === 'left' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) return current;

      const next = [...current];
      const [removed] = next.splice(index, 1);
      next.splice(targetIndex, 0, removed);
      return next;
    });
  };

  const toggleColumnVisibility = (key: ColumnKey) => {
    setVisibleColumns((current) => {
      if (current.includes(key)) {
        if (current.length === 1) {
          toast.warning('En az bir sütun görünür kalmalı');
          return current;
        }
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  };

  const addFilterRule = () => {
    setFilters((current) => [
      ...current,
      {
        id: makeId(),
        join: current.length === 0 ? 'and' : 'and',
        column: 'durum',
        operator: 'contains',
        value: '',
      },
    ]);
  };

  const updateFilterRule = (id: string, patch: Partial<FilterRule>) => {
    setFilters((current) => current.map((rule) => {
      if (rule.id !== id) return rule;
      const next = { ...rule, ...patch };
      if (patch.column && DATE_FILTER_COLUMNS.has(patch.column)) {
        next.operator = 'dateEquals';
        next.value = '';
      }
      if (patch.operator && ['dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth', 'empty', 'notEmpty'].includes(patch.operator)) {
        next.value = '';
      }
      return next;
    }));
  };

  const openCellFilterMenu = (event: React.MouseEvent, column: ColumnDef, value: string) => {
    const trimmed = String(value || '').trim();
    if (!trimmed || trimmed === '-') return;
    event.preventDefault();
    event.stopPropagation();
    setCellFilterMenu({
      x: event.clientX,
      y: event.clientY,
      column: column.key,
      columnLabel: column.label,
      value: DATE_FILTER_COLUMNS.has(column.key) ? getDateFilterComparableValue(trimmed) : trimmed,
    });
  };

  const applyFilterFromCell = () => {
    if (!cellFilterMenu) return;
    setFilters((current) => ([
      ...current,
      {
        id: makeId(),
        join: current.length === 0 ? 'and' : 'and',
        column: cellFilterMenu.column,
        operator: DATE_FILTER_COLUMNS.has(cellFilterMenu.column) ? 'dateEquals' : 'contains',
        value: cellFilterMenu.value,
      },
    ]));
    setShowFilterPanel(true);
    setPage(1);
    setCellFilterMenu(null);
  };

  const removeFilterRule = (id: string) => {
    setFilters((current) => current.filter((rule) => rule.id !== id));
  };

  const clearFiltersAndGroup = () => {
    setFilters([]);
    setGroupBy('none');
    setActiveFlowTab('all');
    setActiveTabId(MAIN_TAB_ID);
    setPage(1);
  };

  const applyTab = (tab: SavedFilterTab) => {
    setActiveTabId(tab.id);
    setFilters(sanitizeFilters(tab.filters));
    setGroupBy(sanitizeGroupBy(tab.groupBy));
    setActiveFlowTab(tab.flowTab || 'all');
    setSortKey(tab.sortKey && getColumn(tab.sortKey) ? tab.sortKey : null);
    setSortDirection(tab.sortDirection === 'desc' ? 'desc' : 'asc');
    setPage(1);
  };

  const persistPrivateTabs = async (nextUserTabs: SavedFilterTab[]) => {
    if (!user?.id) {
      toast.error('Kullanıcı bilgisi alınamadı');
      return false;
    }

    await settingsService.saveMySetting(getFlexibleTrackingSavedTabsSettingKey(user.id, settingsKeyScope), nextUserTabs, {
      type: 'json',
      category: 'ui',
      description: 'User saved Flexible Tracking filter tabs',
    });

    return true;
  };

  const persistPublicTabs = async (nextPublicTabs: SavedFilterTab[]) => {
    await settingsService.saveSharedSetting(getFlexibleTrackingSharedTabsSettingKey(settingsKeyScope), nextPublicTabs, {
      type: 'json',
      category: 'ui_shared',
      description: 'Shared Flexible Tracking filter tabs',
    });

    return true;
  };

  const saveCurrentFiltersAsTab = async () => {
    const name = newTabName.trim();
    if (!name) {
      toast.warning('Tab adı giriniz');
      return;
    }

    const newTab: SavedFilterTab = {
      id: makeId(),
      name,
      filters: sanitizeFilters(filters),
      groupBy,
      flowTab: activeFlowTab,
      sortKey,
      sortDirection,
      visibility: newTabVisibility,
      ownerUserId: user?.id ?? null,
      ownerName: user?.name || user?.email || '',
    };

    setSavingTabs(true);
    try {
      const ok = newTabVisibility === 'public'
        ? await persistPublicTabs([...publicTabs, newTab])
        : await persistPrivateTabs([...privateTabs, newTab]);
      if (!ok) return;
      if (newTabVisibility === 'public') {
        setPublicTabs((current) => [...current, newTab]);
      } else {
        setPrivateTabs((current) => [...current, newTab]);
      }
      setActiveTabId(newTab.id);
      setNewTabName('');
      toast.success(newTabVisibility === 'public' ? 'Public tab kaydedildi' : 'Hızlı arama tabı kaydedildi');
    } catch (error) {
      console.error('Saved tab failed:', error);
      toast.error('Tab kaydedilemedi');
    } finally {
      setSavingTabs(false);
    }
  };

  const deleteActiveTab = async () => {
    if (activeTabId === MAIN_TAB_ID || activeTabId === FLOW_TAB_ID || !activeSavedTab) return;
    if (!canManageActiveTab) {
      toast.warning('Bu public tab sadece oluşturan kullanıcı tarafından yönetilebilir');
      return;
    }

    setSavingTabs(true);
    try {
      const ok = activeSavedTab.visibility === 'public'
        ? await persistPublicTabs(publicTabs.filter((tab) => tab.id !== activeTabId))
        : await persistPrivateTabs(privateTabs.filter((tab) => tab.id !== activeTabId));
      if (!ok) return;
      if (activeSavedTab.visibility === 'public') {
        setPublicTabs((current) => current.filter((tab) => tab.id !== activeTabId));
      } else {
        setPrivateTabs((current) => current.filter((tab) => tab.id !== activeTabId));
      }
      applyTab(MAIN_FILTER_TAB);
      toast.success('Tab silindi');
    } catch (error) {
      console.error('Delete tab failed:', error);
      toast.error('Tab silinemedi');
    } finally {
      setSavingTabs(false);
    }
  };

  const overwriteActiveTab = async () => {
    if (activeTabId === MAIN_TAB_ID || activeTabId === FLOW_TAB_ID) {
      toast.warning(activeTabId === MAIN_TAB_ID ? 'Ana Tablo güncellenemez. Yeni tab olarak kaydedin.' : 'Akış Yönetimi güncellenemez.');
      return;
    }
    if (!activeSavedTab || !canManageActiveTab) {
      toast.warning('Bu public tab sadece oluşturan kullanıcı tarafından güncellenebilir');
      return;
    }

    const nextTabs = (activeSavedTab.visibility === 'public' ? publicTabs : privateTabs).map((tab) =>
      tab.id === activeTabId
        ? {
            ...tab,
            filters: sanitizeFilters(filters),
            groupBy,
            flowTab: activeFlowTab,
            sortKey,
            sortDirection,
          }
        : tab,
    );

    setSavingTabs(true);
    try {
      const ok = activeSavedTab.visibility === 'public'
        ? await persistPublicTabs(nextTabs)
        : await persistPrivateTabs(nextTabs);
      if (!ok) return;
      if (activeSavedTab.visibility === 'public') {
        setPublicTabs(nextTabs);
      } else {
        setPrivateTabs(nextTabs);
      }
      toast.success('Tab güncellendi');
    } catch (error) {
      console.error('Overwrite tab failed:', error);
      toast.error('Tab güncellenemedi');
    } finally {
      setSavingTabs(false);
    }
  };

  const resetView = () => {
    setVisibleColumns(defaultVisibleColumns);
    setPageSize(25);
    setSortKey(null);
    setSortDirection('asc');
    setSearchInput('');
    setSearch('');
    setActiveFlowTab('all');
    setPage(1);
    setColumnWidthOverrides({});
    toast.success('Görünüm varsayılana döndürüldü');
  };

  const saveView = async () => {
    if (!user?.id) {
      toast.error('Kullanıcı bilgisi alınamadı');
      return;
    }

    setSavingView(true);
    try {
      const columnWidths: Partial<Record<ColumnKey, number>> = {};
      for (const key of ALL_COLUMNS.map((c) => c.key)) {
        const w = getEffectiveWidth(key);
        if (w !== undefined && w !== getColumn(key)?.width) {
          columnWidths[key] = w;
        }
      }

      await settingsService.saveMySetting(
        getFlexibleTrackingBasicViewSettingKey(user.id, settingsKeyScope),
        {
          visibleColumns,
          pageSize,
          sortKey,
          sortDirection,
          groupBy,
          columnWidths: Object.keys(columnWidths).length > 0 ? columnWidths : undefined,
        },
        {
          type: 'json',
          category: 'ui',
          description: 'Flexible Tracking Basic table view',
        },
      );

      toast.success('Tablo görünümü kaydedildi');
    } catch (error) {
      console.error('Table view save failed:', error);
      toast.error('Görünüm kaydedilemedi');
    } finally {
      setSavingView(false);
    }
  };

  const getDurumStyle = (durum: string) => {
    switch (durum) {
      case 'tedarik-bekliyor': return 'background:#f43f5e;color:#fff;font-weight:700;text-align:center';
      case 'filo-tedarik-edildi': return 'background:#3b82f6;color:#fff;font-weight:700;text-align:center';
      case 'spot-tedarik-edildi': return 'background:#7dd3fc;color:#020617;font-weight:700;text-align:center';
      case 'teklif-topla': return 'background:#8b5cf6;color:#fff;font-weight:700;text-align:center';
      case 'faturalandirildi': return 'background:#10b981;color:#fff;font-weight:700;text-align:center';
      case 'iptal': return 'background:#334155;color:#fff;font-weight:700;text-align:center';
      default: return 'background:#e2e8f0;color:#1e293b;font-weight:700;text-align:center';
    }
  };

  const getFlowStatusStyle = (value: string) => {
    const normalized = normalizeSearchText(value);
    if (normalized.includes('tamam') || normalized.includes('teslim') || normalized.includes('onay') || normalized.includes('fatura')) {
      return 'background:#ecfdf5;color:#047857;font-weight:600;text-align:center;border-radius:6px;padding:2px 8px';
    }
    if (normalized.includes('bekli') || normalized.includes('tedarik') || normalized.includes('topla')) {
      return 'background:#fffbeb;color:#b45309;font-weight:600;text-align:center;border-radius:6px;padding:2px 8px';
    }
    if (normalized.includes('yol') || normalized.includes('devam') || normalized.includes('yukleme') || normalized.includes('yükleme')) {
      return 'background:#eff6ff;color:#1d4ed8;font-weight:600;text-align:center;border-radius:6px;padding:2px 8px';
    }
    if (normalized.includes('iptal') || normalized.includes('red')) {
      return 'background:#fff1f2;color:#be123c;font-weight:600;text-align:center;border-radius:6px;padding:2px 8px';
    }
    return 'background:#f1f5f9;color:#334155;font-weight:600;text-align:center;border-radius:6px;padding:2px 8px';
  };

  const getAccountingStatusStyle = (status: string) => {
    switch (status) {
      case 'none': return 'background:#fef9c3;color:#713f12;font-weight:600;text-align:center';
      case 'delivery-approval-pending': return 'background:#fef3c7;color:#78350f;font-weight:600;text-align:center';
      case 'delivery-rejected': return 'background:#fecaca;color:#450a0a;font-weight:600;text-align:center';
      case 'invoice-waiting': return 'background:#e0f2fe;color:#0c4a6e;font-weight:600;text-align:center';
      case 'invoice-rejected': return 'background:#ffe4e6;color:#881337;font-weight:600;text-align:center';
      case 'invoice-approval-pending': return 'background:#f3e8ff;color:#3b0764;font-weight:600;text-align:center';
      case 'payment-pre-approval': return 'background:#eef2ff;color:#312e81;font-weight:600;text-align:center';
      case 'payment-pending': return 'background:#ffedd5;color:#7c2d12;font-weight:600;text-align:center';
      case 'payment-completed': return 'background:#dcfce7;color:#052e16;font-weight:600;text-align:center';
      default: return 'background:#fef9c3;color:#713f12;font-weight:600;text-align:center';
    }
  };

  const copyTableToClipboard = async () => {
    if (sortedRows.length === 0) {
      toast.warning('Kopyalanacak kayıt bulunamadı');
      return;
    }

    const headers = activeColumns.map((column) => column.label);

    const tsvLines = sortedRows.map((row) =>
      activeColumns
        .map((column) => {
          const rawValue = row[column.key];
          if (column.key === 'durum') return getDurumText(String(rawValue));
          if (column.key === 'islemDurum') return formatSlugLabel(String(rawValue));
          if (column.key === 'accountingStatus') return getAccountingStatusText(String(rawValue));
          if (getDocumentBucketForColumn(column.key)) return '';
          const value = column.format ? column.format(rawValue, row) : String(rawValue ?? '-');
          return String(value ?? '').replace(/[\t\n\r]+/g, ' ').trim();
        })
        .join('\t'),
    );

    const tsvPayload = [headers.join('\t'), ...tsvLines].join('\n');

    const htmlRows = sortedRows
      .map((row, rowIndex) => {
        const rowBg = rowIndex % 2 === 0 ? 'background:#ffffff' : 'background:#f8fafc';
        return `<tr style="${rowBg}">${activeColumns
          .map((column) => {
            const rawValue = row[column.key];
            let displayValue: string;
            let cellStyle = 'border:1px solid #d1d5db;padding:4px 8px;font-size:13px;color:#1e293b';

            if (column.key === 'durum') {
              displayValue = getDurumText(String(rawValue));
              cellStyle += ';' + getDurumStyle(String(rawValue));
            } else if (column.key === 'islemDurum') {
              displayValue = formatSlugLabel(String(rawValue));
              cellStyle += ';' + getFlowStatusStyle(String(rawValue));
            } else if (column.key === 'accountingStatus') {
              displayValue = getAccountingStatusText(String(rawValue));
              cellStyle += ';' + getAccountingStatusStyle(String(rawValue));
            } else if (getDocumentBucketForColumn(column.key)) {
              displayValue = '';
            } else {
              displayValue = column.format ? column.format(rawValue, row) : String(rawValue ?? '-');
              if (column.align === 'right') cellStyle += ';text-align:right';
              else if (column.align === 'center') cellStyle += ';text-align:center';
            }

            const escaped = String(displayValue ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return `<td style="${cellStyle}">${escaped}</td>`;
          })
          .join('')}</tr>`;
      })
      .join('\n');

    const htmlPayload = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px"><thead><tr>${headers.map((h) => `<th style="border:1px solid #d1d5db;padding:4px 8px;font-size:13px;font-weight:700;text-align:left;background:#f3f4f6;color:#374151;white-space:nowrap">${h.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</th>`).join('')}</tr></thead><tbody>${htmlRows}</tbody></table>`;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([tsvPayload], { type: 'text/plain' }),
          'text/html': new Blob([htmlPayload], { type: 'text/html' }),
        }),
      ]);
      toast.success(`${sortedRows.length} satır panoya kopyalandı (e-posta uyumlu)`);
    } catch (error) {
      console.error('Copy table to clipboard failed:', error);
      toast.error('Tablo panoya kopyalanamadı');
    }
  };

  const loadHtml2Canvas = useCallback(async (): Promise<any> => {
    if ((window as any).html2canvas) return (window as any).html2canvas;
    const urls = [
      'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
      'https://unpkg.com/html2canvas@1.4.1/dist/html2canvas.min.js',
      'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
    ];
    for (const url of urls) {
      try {
        const script = document.createElement('script');
        script.src = url;
        script.crossOrigin = 'anonymous';
        const loaded = new Promise<void>((resolve, reject) => {
          script.onload = () => resolve();
          script.onerror = () => reject();
        });
        document.head.appendChild(script);
        await loaded;
        if ((window as any).html2canvas) return (window as any).html2canvas;
      } catch {
        continue;
      }
    }
    return null;
  }, []);

  const renderTableToCanvas = (): HTMLCanvasElement | null => {
    const headers = activeColumns.map((column) => column.label);
    const colWidths = activeColumns.map((col) => Math.max(80, getEffectiveWidth(col.key) || col.width || 120));
    const rowHeight = 28;
    const headerHeight = 32;
    const padding = 6;
    const totalWidth = colWidths.reduce((a, b) => a + b, 0) + 2;
    const totalHeight = headerHeight + sortedRows.length * rowHeight + 2;

    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 1600 / Math.max(totalWidth, 1));
    canvas.width = totalWidth * scale;
    canvas.height = totalHeight * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(scale, scale);
    ctx.textBaseline = 'middle';

    // header bg
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, totalWidth, headerHeight);

    let x = 1;
    ctx.font = 'bold 13px Arial, sans-serif';
    ctx.fillStyle = '#374151';
    headers.forEach((h, i) => {
      ctx.fillText(h, x + padding, headerHeight / 2);
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, 0, colWidths[i], headerHeight);
      x += colWidths[i];
    });

    // rows
    ctx.font = '13px Arial, sans-serif';
    sortedRows.forEach((row, rowIndex) => {
      const y = headerHeight + rowIndex * rowHeight;
      ctx.fillStyle = rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
      ctx.fillRect(0, y, totalWidth, rowHeight);

      x = 1;
      activeColumns.forEach((column, colIndex) => {
        const rawValue = row[column.key];
        let displayValue: string;
        let bgColor = '';
        let textColor = '#1e293b';
        let fontWeight = 'normal';
        let textAlign: CanvasTextAlign = 'left';

        if (column.key === 'durum') {
          displayValue = getDurumText(String(rawValue));
          textAlign = 'center';
          fontWeight = 'bold';
          const durumStyle = getDurumStyle(String(rawValue));
          if (durumStyle.includes('#f43f5e')) bgColor = '#f43f5e';
          else if (durumStyle.includes('#3b82f6')) bgColor = '#3b82f6';
          else if (durumStyle.includes('#7dd3fc')) bgColor = '#7dd3fc';
          else if (durumStyle.includes('#8b5cf6')) bgColor = '#8b5cf6';
          else if (durumStyle.includes('#10b981')) bgColor = '#10b981';
          else if (durumStyle.includes('#334155')) bgColor = '#334155';
          else bgColor = '#e2e8f0';
          textColor = (durumStyle.includes('fff') || durumStyle.includes('020617')) ? '#ffffff' : '#1e293b';
        } else if (column.key === 'islemDurum') {
          displayValue = formatSlugLabel(String(rawValue));
          textAlign = 'center';
          fontWeight = '600';
          const fs = getFlowStatusStyle(String(rawValue));
          if (fs.includes('#ecfdf5')) bgColor = '#ecfdf5';
          else if (fs.includes('#fffbeb')) bgColor = '#fffbeb';
          else if (fs.includes('#eff6ff')) bgColor = '#eff6ff';
          else if (fs.includes('#fff1f2')) bgColor = '#fff1f2';
          else bgColor = '#f1f5f9';
          if (fs.includes('#047857')) textColor = '#047857';
          else if (fs.includes('#b45309')) textColor = '#b45309';
          else if (fs.includes('#1d4ed8')) textColor = '#1d4ed8';
          else if (fs.includes('#be123c')) textColor = '#be123c';
          else textColor = '#334155';
        } else if (column.key === 'accountingStatus') {
          displayValue = getAccountingStatusText(String(rawValue));
          textAlign = 'center';
          fontWeight = '600';
          const as = getAccountingStatusStyle(String(rawValue));
          if (as.includes('#fef9c3')) bgColor = '#fef9c3';
          else if (as.includes('#fef3c7')) bgColor = '#fef3c7';
          else if (as.includes('#fecaca')) bgColor = '#fecaca';
          else if (as.includes('#e0f2fe')) bgColor = '#e0f2fe';
          else if (as.includes('#ffe4e6')) bgColor = '#ffe4e6';
          else if (as.includes('#f3e8ff')) bgColor = '#f3e8ff';
          else if (as.includes('#eef2ff')) bgColor = '#eef2ff';
          else if (as.includes('#ffedd5')) bgColor = '#ffedd5';
          else if (as.includes('#dcfce7')) bgColor = '#dcfce7';
          if (as.includes('#713f12')) textColor = '#713f12';
          else if (as.includes('#78350f')) textColor = '#78350f';
          else if (as.includes('#450a0a')) textColor = '#450a0a';
          else if (as.includes('#0c4a6e')) textColor = '#0c4a6e';
          else if (as.includes('#881337')) textColor = '#881337';
          else if (as.includes('#3b0764')) textColor = '#3b0764';
          else if (as.includes('#312e81')) textColor = '#312e81';
          else if (as.includes('#7c2d12')) textColor = '#7c2d12';
          else if (as.includes('#052e16')) textColor = '#052e16';
        } else if (getDocumentBucketForColumn(column.key)) {
          displayValue = '';
        } else {
          displayValue = column.format ? column.format(rawValue, row) : String(rawValue ?? '-');
          if (column.align === 'right') textAlign = 'right';
          else if (column.align === 'center') textAlign = 'center';
        }

        const cw = colWidths[colIndex];
        ctx.strokeStyle = '#d1d5db';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, cw, rowHeight);

        if (bgColor) {
          ctx.fillStyle = bgColor;
          ctx.fillRect(x + 1, y + 1, cw - 2, rowHeight - 2);
        }

        ctx.fillStyle = textColor;
        if (fontWeight === 'bold') ctx.font = 'bold 13px Arial, sans-serif';
        else if (fontWeight === '600') ctx.font = '600 13px Arial, sans-serif';
        else ctx.font = '13px Arial, sans-serif';
        ctx.textAlign = textAlign;

        const textX = textAlign === 'center' ? x + cw / 2 : textAlign === 'right' ? x + cw - padding : x + padding;
        const text = String(displayValue ?? '').substring(0, Math.floor(cw / 7));
        ctx.fillText(text, textX, y + rowHeight / 2);

        x += cw;
      });
    });

    // bottom border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, totalWidth, totalHeight);

    return canvas;
  };

  const copyTableAsImage = async () => {
    if (!tableContainerRef.current || sortedRows.length === 0) {
      toast.warning('Kopyalanacak tablo bulunamadı');
      return;
    }

    try {
      const html2canvasLib = await loadHtml2Canvas();
      let canvas: HTMLCanvasElement | null = null;

      if (html2canvasLib) {
        try {
          canvas = await html2canvasLib(tableContainerRef.current, {
            scale: 1,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
          });
        } catch {
          canvas = null;
        }
      }

      if (!canvas) {
        canvas = renderTableToCanvas();
      }

      if (!canvas) throw new Error('Canvas oluşturulamadı');

      // downscale large images for email
      const maxDim = 1200;
      let finalCanvas = canvas;
      if (canvas.width > maxDim * 2 || canvas.height > maxDim * 2) {
        finalCanvas = document.createElement('canvas');
        const scale = Math.min(maxDim / (canvas.width / 2), maxDim / (canvas.height / 2), 1);
        finalCanvas.width = Math.round(canvas.width * scale);
        finalCanvas.height = Math.round(canvas.height * scale);
        const fctx = finalCanvas.getContext('2d');
        if (fctx) fctx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
      }

      const blob = await new Promise<Blob | null>((resolve) => finalCanvas!.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Blob oluşturulamadı');

      // build html with embedded base64 image for email clients
      const dataUrl = finalCanvas.toDataURL('image/png');
      const htmlWithImage = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px"><tr><td><img src="${dataUrl}" alt="Tablo" style="max-width:100%;height:auto;width:100%"/></td></tr></table>`;

      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob,
            'text/html': new Blob([htmlWithImage], { type: 'text/html' }),
          }),
        ]);
        toast.success('Tablo resim olarak panoya kopyalandı');
      } catch {
        // clipboard write failed — offer download instead
        const link = document.createElement('a');
        link.download = `tablo-${new Date().toISOString().slice(0, 10)}.png`;
        link.href = dataUrl;
        link.click();
        toast.success('Tablo resim olarak indirildi');
      }
    } catch (error) {
      console.error('Copy table as image failed:', error);
      toast.error('Tablo resim olarak kopyalanamadı');
    }
  };

  const exportExcel = () => {
    const DURUM_COLORS: Record<string, { bg: string; fg: string }> = {
      'tedarik-bekliyor': { bg: 'E11D48', fg: 'FFFFFF' },
      'filo-tedarik-edildi': { bg: '3B82F6', fg: 'FFFFFF' },
      'spot-tedarik-edildi': { bg: 'BAE6FD', fg: '0C0A09' },
      'teklif-topla': { bg: '8B5CF6', fg: 'FFFFFF' },
      'faturalandirildi': { bg: '10B981', fg: 'FFFFFF' },
      'iptal': { bg: '334155', fg: 'FFFFFF' },
    };
    const FLOW_COLORS: Record<string, { bg: string; fg: string }> = {
      'tedarik-bekleniyor': { bg: 'FFF1F2', fg: '881337' },
      'spot-teklif-bekleniyor': { bg: 'F5F3FF', fg: '4C1D95' },
      'filo-tedarik-edildi': { bg: 'EFF6FF', fg: '1E3A8A' },
      'spot-tedarik-edildi': { bg: 'F0F9FF', fg: '0C4A6E' },
      'atandi-islem-bekliyor': { bg: 'EEF2FF', fg: '312E81' },
      'yukleme-bekliyor': { bg: 'FFFBEB', fg: '78350F' },
      'yukleme-devam': { bg: 'FFF7ED', fg: '7C2D12' },
      'yukleme-tamamlandi': { bg: 'F7FEE7', fg: '365314' },
      'tasima-yolunda': { bg: 'ECFEFF', fg: '164E63' },
      'teslim-noktasina-ulasildi': { bg: 'F0FDFA', fg: '134E4A' },
      'bosaltma-bekliyor': { bg: 'FDF2F8', fg: '831843' },
      'teslim-edildi': { bg: 'ECFDF5', fg: '065F46' },
      'kapatildi': { bg: 'F0FDF4', fg: '14532D' },
      'iptal': { bg: 'F8FAFC', fg: '334155' },
    };
    const ACCOUNTING_COLORS: Record<string, { bg: string; fg: string }> = {
      'none': { bg: 'FEF9C3', fg: '713F12' },
      'invoice-approval-pending': { bg: 'F3E8FF', fg: '581C87' },
      'payment-pending': { bg: 'FFF7ED', fg: '9A3412' },
      'payment-completed': { bg: 'F0FDF4', fg: '166534' },
    };
    const STATUS_FONT: Record<string, string> = {};

  const NUMERIC_KEYS = new Set(['navlun', 'bazFiyat', 'durakBedeli', 'beklemeBedeli', 'hammaliye', 'toplamBedel', 'seferKm', 'tlKm', 'yukAgirlik', 'yukHacim', 'palletCount', 'ugrmaSayisi', 'gunlukKm', 'hedefFiyat', 'activeShipmentCount']);

    const headerStyle = {
      font: { bold: true, sz: 11, name: 'Calibri', color: { rgb: '1E293B' } },
      fill: { patternType: 'solid' as const, fgColor: { rgb: 'F1F5F9' } },
      alignment: { vertical: 'center' as const, horizontal: 'left' as const, wrapText: true },
      border: { bottom: { style: 'thin' as const, color: { rgb: 'CBD5E1' } } },
    };
    const cellStyle = {
      font: { sz: 10, name: 'Calibri', color: { rgb: '1E293B' } },
      alignment: { vertical: 'center' as const },
      border: { bottom: { style: 'thin' as const, color: { rgb: 'E2E8F0' } } },
    };
    const altCellStyle = {
      ...cellStyle,
      fill: { patternType: 'solid' as const, fgColor: { rgb: 'F8FAFC' } },
    };
    const numericStyle = {
      ...cellStyle,
      alignment: { vertical: 'center' as const, horizontal: 'right' as const },
      numFmt: '#,##0.00',
    };
    const numericAltStyle = {
      ...altCellStyle,
      alignment: { vertical: 'center' as const, horizontal: 'right' as const },
      numFmt: '#,##0.00',
    };

    const headers = activeColumns.map((column) => ({ v: column.label, t: 's' as const, s: headerStyle }));

    const bodyRows = sortedRows.map((row, rowIndex) => {
      return activeColumns.map((column) => {
        const rawValue = row[column.key];
        let displayValue: string;
        if (column.key === 'durum') {
          displayValue = getDurumText(String(rawValue));
        } else if (column.key === 'islemDurum') {
          displayValue = getFlowStatusLabel(getEffectiveFlowStatus(row));
        } else if (column.key === 'accountingStatus') {
          displayValue = getAccountingStatusText(String(rawValue));
        } else if (NUMERIC_KEYS.has(column.key)) {
          const num = Number(rawValue);
          const numValue = Number.isFinite(num) ? num : 0;
          const isAlt = rowIndex % 2 === 1;
          return { v: numValue, t: 'n' as const, s: isAlt ? numericAltStyle : numericStyle };
        } else {
          displayValue = column.format ? String(column.format(rawValue, row) ?? '-') : String(rawValue ?? '-');
        }

        const isAlt = rowIndex % 2 === 1;
        let style = isAlt ? { ...altCellStyle } : { ...cellStyle };

        if (column.key === 'durum') {
          const colors = DURUM_COLORS[String(rawValue)] || null;
          if (colors) style = { ...style, font: { ...style.font, bold: true, color: { rgb: colors.fg } }, fill: { patternType: 'solid' as const, fgColor: { rgb: colors.bg } } };
        } else if (column.key === 'islemDurum') {
          const flowStatus = String(getEffectiveFlowStatus(row));
          const colors = FLOW_COLORS[flowStatus] || null;
          if (colors) style = { ...style, font: { ...style.font, bold: true, color: { rgb: colors.fg } }, fill: { patternType: 'solid' as const, fgColor: { rgb: colors.bg } } };
        } else if (column.key === 'accountingStatus') {
          const colors = ACCOUNTING_COLORS[String(rawValue)] || null;
          if (colors) style = { ...style, font: { ...style.font, bold: true, color: { rgb: colors.fg } }, fill: { patternType: 'solid' as const, fgColor: { rgb: colors.bg } } };
        }

        return { v: displayValue, t: 's' as const, s: style };
      });
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);
    ws['!cols'] = activeColumns.map((column) => ({ wch: Math.max(column.label.length, Math.floor((column.width || 120) / 7)) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tracking');
    XLSX.writeFile(wb, `tracking-basic-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const renderSortIcon = (key: ColumnKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-3.5 w-3.5" />;
    return <ArrowDown className="h-3.5 w-3.5" />;
  };

  const toggleRowSelection = (rowKey: string) => {
    setSelectedRowKeys((current) => (current.includes(rowKey) ? current.filter((key) => key !== rowKey) : [...current, rowKey]));
  };


  const loadDocumentsForRow = async (row: OperationRow, bucket: TrackingDocumentBucket) => {
    const response: any = await documentService.getDocuments({ entityType: 'shipment', entityId: row.id }).catch(() => ({ data: [] }));
    const items = Array.isArray(response?.data) ? response.data : [];
    return uniqueDocuments(
      items
        .filter((doc: any) => matchesDocumentBucket(doc, bucket))
        .map(normalizeDocument),
    );
  };

  useEffect(() => {
    if (detailTab !== 'documents' || !detailRow?.id) return;

    const missingBuckets = DETAIL_DOCUMENT_BUCKETS.filter((bucket) => !rowDocumentPreviews[detailRow.rowKey]?.[bucket]);
    if (missingBuckets.length === 0) return;

    let cancelled = false;

    const run = async () => {
      const entries = await Promise.all(missingBuckets.map(async (bucket) => [bucket, await loadDocumentsForRow(detailRow, bucket)] as const));
      if (cancelled) return;
      setRowDocumentPreviews((current) => ({
        ...current,
        [detailRow.rowKey]: {
          ...(current[detailRow.rowKey] || {}),
          ...Object.fromEntries(entries),
        },
      }));
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [detailTab, detailRow?.id, detailRow?.rowKey, rowDocumentPreviews]);

  useEffect(() => {
    if (detailTab !== 'audit' || !detailRow?.id) return;
    let cancelled = false;
    const run = async () => {
      try {
        setShipmentAuditLogsLoading(true);
        const response: any = await auditService.getByShipment(detailRow.id, 200).catch(() => ({ data: [] }));
        if (!cancelled) {
          setShipmentAuditLogs(Array.isArray(response?.data) ? response.data : []);
        }
      } finally {
        if (!cancelled) setShipmentAuditLogsLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [detailTab, detailRow?.id]);

  const renderDocumentThumbs = (row: OperationRow, bucket: TrackingDocumentBucket) => {
    const documents = rowDocumentPreviews[row.rowKey]?.[bucket] || [];
    const sharedInvoiceCount = bucket === 'invoice' ? Number(row.invoiceGroupCount || 0) : 0;
    const canUpload = bucket === 'loading'
      || bucket === 'delivery'
      || bucket === 'invoice';
    const uploadKey = `${row.rowKey}:${bucket}`;

    if (!documents.length) {
      if (!canUpload) {
        return <span className="text-[12px] text-slate-400">-</span>;
      }
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void handleShipmentDocumentUpload(row, bucket);
          }}
          disabled={uploadingShipmentDocumentKey === uploadKey}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200"
        >
          <Upload className="h-3 w-3" />
          <span>{uploadingShipmentDocumentKey === uploadKey ? '...' : 'Yükle'}</span>
        </button>
      );
    }

    return (
      <div className="flex max-w-[180px] items-center gap-1 overflow-hidden">
        {sharedInvoiceCount > 1 ? (
          <span
            className="inline-flex h-8 shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-bold text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
            title={`Bu fatura ${sharedInvoiceCount} sevkiyat için tek fatura olarak yüklenmiştir; ödeme tek fatura bazında kontrol edilmelidir.`}
          >
            Tek fatura/{sharedInvoiceCount}
          </span>
        ) : null}
        {documents.slice(0, 4).map((doc, index) => (
          <button
            key={`${doc.url}-${index}`}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setDocumentSlider({
                docs: documents,
                currentIndex: index,
                onDeleteCurrent: (currentDoc, currentIndex) => handleDeleteShipmentDocument(row, bucket, currentDoc, currentIndex),
              });
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-500 hover:ring-2 hover:ring-orange-400 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-300"
            title={doc.fileName}
          >
            {doc.isImage ? (
              <img src={doc.url} alt={doc.fileName} className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <span>{doc.fileName.split('.').pop()?.slice(0, 3).toLocaleUpperCase('tr-TR') || 'DOC'}</span>
            )}
          </button>
        ))}
        {documents.length > 4 && <span className="text-[11px] font-semibold text-slate-500">+{documents.length - 4}</span>}
        {canUpload ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              void handleShipmentDocumentUpload(row, bucket);
            }}
            disabled={uploadingShipmentDocumentKey === uploadKey}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:ring-2 hover:ring-orange-400 disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200"
            title={bucket === 'loading' ? 'Yükleme evrakı yükle' : bucket === 'invoice' ? 'Fatura yükle' : 'Boşaltma evrakı yükle'}
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    );
  };

  const handleShipmentDocumentUpload = async (row: OperationRow, bucket: Extract<TrackingDocumentBucket, 'loading' | 'delivery' | 'invoice'>) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.jpg,.jpeg,.png,.webp';
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      const uploadKey = `${row.rowKey}:${bucket}`;
      setUploadingShipmentDocumentKey(uploadKey);
      try {
        if (bucket === 'invoice') {
          const formData = new FormData();
          formData.append('shipmentIds', row.id);
          for (const file of files) {
            formData.append('files', file);
          }
          const result = await accountingService.bulkSubmitInvoice(formData);
          if (result?.data?.updated === 0) {
            toast.error('Fatura yuklenemedi: Sevkiyat fatura bekleyen asamada degil');
            return;
          }
          applyRowPatch(row.rowKey, { accountingStatus: 'invoice-approval-pending' });
          if (typeof onRefreshPreApprovalPdf === 'function') {
            Promise.resolve(onRefreshPreApprovalPdf({ ...row, accountingStatus: 'invoice-approval-pending' })).catch((error) => {
              console.error('Pre-approval PDF background refresh failed:', error);
            });
          }
          const documents = await loadDocumentsForRow(row, bucket);
          setRowDocumentPreviews((current) => ({
            ...current,
            [row.rowKey]: {
              ...(current[row.rowKey] || {}),
              [bucket]: documents,
            },
          }));
          toast.success('Fatura yuklendi ve kayit faturalandirildi asamasina alindi');
          return;
        }

        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entityType', 'shipment');
          formData.append('entityId', row.id);
          formData.append('documentType', bucket === 'loading' ? 'loading' : bucket === 'invoice' ? 'invoice' : 'delivery');
          formData.append('name', file.name);
          await documentService.uploadDocument(formData);
        }
        const documents = await loadDocumentsForRow(row, bucket);
        setRowDocumentPreviews((current) => ({
          ...current,
          [row.rowKey]: {
            ...(current[row.rowKey] || {}),
            [bucket]: documents,
          },
        }));
        toast.success(bucket === 'loading' ? 'Yükleme evrakları yüklendi' : bucket === 'invoice' ? 'Fatura yüklendi' : 'Boşaltma evrakları yüklendi');
      } catch (error) {
        console.error('Shipment document upload failed:', error);
        toast.error('Evrak yüklenemedi');
      } finally {
        setUploadingShipmentDocumentKey(null);
      }
    };
    input.click();
  };

  const handleDeleteShipmentDocument = async (row: OperationRow, bucket: TrackingDocumentBucket, currentDoc: any, currentIndex: number) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    const documentId = currentDoc?.id;
    if (!documentId) return;
    try {
      setDeletingSliderDocument(true);
      await documentService.deleteDocument(documentId);
      const documents = await loadDocumentsForRow(row, bucket);
      setRowDocumentPreviews((current) => ({
        ...current,
        [row.rowKey]: {
          ...(current[row.rowKey] || {}),
          [bucket]: documents,
        },
      }));
      setDocumentSlider((prev) => {
        if (!prev) return prev;
        if (documents.length === 0) return null;
        return {
          ...prev,
          docs: documents,
          currentIndex: Math.min(currentIndex, documents.length - 1),
        };
      });
      toast.success('Belge silindi');
    } catch (error: any) {
      console.error('Delete shipment document failed:', error);
      toast.error(error?.message || 'Belge silinemedi');
    } finally {
      setDeletingSliderDocument(false);
    }
  };

  const handleBulkInvoiceUpload = async () => {
    if (!externalRows) return;
    const shipmentIds = Array.from(new Set(selectedRows
      .filter((row) => ['invoice-waiting', 'invoice-rejected', 'invoice-approval-pending'].includes(String(row.accountingStatus || '').trim()))
      .map((row) => String(row.id || '').trim())
      .filter(Boolean)));
    const uploadTargetRows = selectedRows.filter((row) => shipmentIds.includes(String(row.id || '').trim()));
    if (shipmentIds.length === 0) {
      toast.error('Fatura yuklemeye uygun kayit secilmedi');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.jpg,.jpeg,.png,.webp';
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      setUploadingShipmentDocumentKey('__bulk_invoice__');
      try {
        const formData = new FormData();
        shipmentIds.forEach((shipmentId) => formData.append('shipmentIds', shipmentId));
        files.forEach((file) => formData.append('files', file));
        const response: any = await accountingService.bulkSubmitInvoice(formData);
        const updatedShipmentIds = Array.isArray(response?.data?.updatedShipmentIds)
          ? response.data.updatedShipmentIds.map((value: any) => String(value))
          : [];
        if (typeof onBulkRefreshPreApprovalPdf === 'function' && updatedShipmentIds.length > 0) {
          const updatedRows = uploadTargetRows
            .filter((row) => updatedShipmentIds.includes(String(row.id || '').trim()))
            .map((row) => ({ ...row, accountingStatus: 'invoice-approval-pending' }));
          if (updatedRows.length > 0) {
            Promise.resolve(onBulkRefreshPreApprovalPdf(updatedRows)).catch((error) => {
              console.error('Bulk pre-approval PDF background refresh failed:', error);
            });
          }
        }
        await loadRows();
        setSelectedRowKeys([]);
        toast.success(response?.message || 'Toplu fatura yukleme tamamlandi');
        if (response?.data?.sharedInvoiceGroup?.warning) {
          toast.warning(response.data.sharedInvoiceGroup.warning, { duration: 9000 });
        }
      } catch (error: any) {
        console.error('Bulk invoice upload failed:', error);
        toast.error(error?.message || 'Toplu fatura yukleme basarisiz');
      } finally {
        setUploadingShipmentDocumentKey(null);
      }
    };
    input.click();
  };

  const handleUndoInvoiceGroup = async (invoiceGroupKey: string) => {
    const normalizedKey = String(invoiceGroupKey || '').trim();
    if (!normalizedKey) return;
    if (!window.confirm('Bu tek fatura grubu çözülsün mü? Faturaları geri aldığınız işlemlerde tekrar yükleyip PDF\'leri yeniden üretmeniz gerekecektir.')) return;
    try {
      const response: any = await accountingService.undoInvoiceGroup(normalizedKey);
      window.location.reload();
      toast.success(response?.message || 'Fatura birleştirme geri alındı');
    } catch (error: any) {
      console.error('Undo invoice group failed:', error);
      toast.error(error?.message || 'Fatura birleştirme geri alınamadı');
    }
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete || selectedRows.length === 0 || bulkDeleting) return;
    if (!window.confirm(`${selectedRows.length} kayıt silinsin mi? Bu işlem geri alınamaz.`)) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(selectedRows.map((row) => (dataService as any).delete(row.id)));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount > 0) {
        toast.success(`${successCount} kayıt silindi`);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} kayıt silinemedi`);
      }
      setSelectedRowKeys([]);
      if (selectedRowKey && selectedRows.some((row) => row.rowKey === selectedRowKey)) {
        setSelectedRowKey(null);
        onRowSelect?.(null);
      }
      await loadRows();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Toplu silme başarısız');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDemurrageManualEdit = async (enabled: boolean) => {
    if (selectedRows.length === 0 || bulkDemurrageToggling) return;
    const ids = Array.from(new Set(selectedRows.map((row) => String(row.id || '').trim()).filter(Boolean)));
    if (ids.length === 0) {
      toast.error('Bekleme bedeli düzenlenecek sevkiyat bulunamadı');
      return;
    }
    setBulkDemurrageToggling(enabled ? 'enable' : 'disable');
    try {
      const resp = await supplyService.setDemurrageManualEdit(ids, enabled);
      if (!resp?.success) {
        toast.error(resp?.message || 'Manuel giriş durumu güncellenemedi');
        return;
      }
      const nextValue = enabled ? 1 : 0;
      selectedRows.forEach((row) => {
        if (row.id) applyRowPatch(row.rowKey, { demurrageManualEdit: nextValue });
      });
      toast.success(enabled ? 'Seçili sevkiyatlarda manuel bekleme bedeli girişi açıldı' : 'Seçili sevkiyatlarda manuel bekleme bedeli girişi kapatıldı');
      setSelectedRowKeys([]);
      if (selectedRowKey && selectedRows.some((row) => row.rowKey === selectedRowKey)) {
        setSelectedRowKey(null);
        onRowSelect?.(null);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Manuel giriş durumu güncellenirken hata oluştu');
    } finally {
      setBulkDemurrageToggling(null);
    }
  };

  const handleBulkSelectByShipmentNumbers = () => {
    const tokens = bulkSelectPasteValue
      .split(/[\n\r\t,; ]+/)
      .map((item) => String(item || '').trim().replace(/^#/, ''))
      .filter(Boolean);

    if (tokens.length === 0) {
      toast.error('Lutfen siparis numaralarini yapistirin');
      return;
    }

    const normalizedTokens = new Set(tokens.map((item) => item.toLocaleLowerCase('tr-TR')));
    const matchedRowKeys = effectiveRowsWithLkm
      .filter((row) => normalizedTokens.has(String(row.sevkKodu || '').trim().replace(/^#/, '').toLocaleLowerCase('tr-TR')))
      .map((row) => row.rowKey);

    if (matchedRowKeys.length === 0) {
      toast.error('Eslesen siparis bulunamadi');
      return;
    }

    setSelectedRowKeys(matchedRowKeys);
    setShowOnlySelectedRows(true);
    setBulkSelectPasteOpen(false);
    setBulkSelectPasteValue('');
    toast.success(`${matchedRowKeys.length} kayit secildi`);
  };

  const visiblePageRows = paginatedItems.filter((item: any) => !item.__group && !item.__invoiceGroup) as OperationRow[];
  const allVisibleSelected = visiblePageRows.length > 0 && visiblePageRows.every((row) => selectedRowKeys.includes(row.rowKey));
  const selectedRows = useMemo(() => {
    const rowByKey = new Map<string, OperationRow>();
    for (const row of searchableRows) {
      rowByKey.set(row.rowKey, row);
    }
    for (const row of searchableArchiveRows) {
      rowByKey.set(row.rowKey, row);
    }
    return selectedRowKeys.map((rowKey) => rowByKey.get(rowKey)).filter(Boolean) as OperationRow[];
  }, [searchableRows, searchableArchiveRows, selectedRowKeys]);
  const selectedRow = useMemo(() => sortedRows.find((row) => row.rowKey === selectedRowKey) || null, [sortedRows, selectedRowKey]);
  const editingInvoiceRow = useMemo(() => editingInvoiceRowId ? effectiveRowsWithLkm.find((row) => row.rowKey === editingInvoiceRowId) || null : null, [editingInvoiceRowId, effectiveRowsWithLkm]);
  const editingCostRow = useMemo(() => editingCostCell ? effectiveRowsWithLkm.find((row) => row.rowKey === editingCostCell.rowKey) || null : null, [editingCostCell, effectiveRowsWithLkm]);
  const selectedRowInOfferCollection = selectedRow ? (activeBidRequestShipmentIds.includes(String(selectedRow.id)) || String(getEffectiveFlowStatus(selectedRow) || '').trim() === 'spot-teklif-bekleniyor') : false;
  const selectedRowCanCollectOffers = selectedRow ? String(getEffectiveFlowStatus(selectedRow)) === 'tedarik-bekleniyor' : false;
  const canBulkDelete = !externalRows && typeof (dataService as any)?.delete === 'function';
  const canManageGeneralListRows = !externalRows;
  const isFleetStatusList = settingsKeyScope === 'general_list_2_fleet';
  const canBulkUpdateProcessStatus = settingsKeyScope === 'flexible_tracking' && !externalRows && hasPermission('shipment-monitoring', 'update');
  const canBulkUpdateAccountingStatus = Boolean(externalRows);

  const renderSelectedRowActions = (row: OperationRow) => {
    if (externalRows && externalRowActions) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {externalRowActions(row)}
        </div>
      );
    }
    const accountingLocked = isAccountingLockedForRow(row);
    const buttons: { key: string; content: ReactNode; visible: boolean }[] = [
      { key: 'teklif-cikar', visible: canManageGeneralListRows && canManageAssignments && selectedRowInOfferCollection, content: <button type="button" onClick={() => void handleRemoveFromOfferCollection(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">Tekliften Çıkar</button> },
      { key: 'teklif-topla', visible: canManageGeneralListRows && canManageAssignments && !selectedRowInOfferCollection && selectedRowCanCollectOffers, content: <button type="button" onClick={() => void handleSingleMoveToOfferCollection(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200">Teklif Topla</button> },
      { key: 'kopyala', visible: canManageGeneralListRows, content: <button type="button" onClick={() => void handleDuplicateRow(row)} disabled={duplicatingShipmentId === row.id} className="inline-flex items-center justify-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-60 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200">{duplicatingShipmentId === row.id ? 'Kopyalanıyor...' : 'Kopyala'}</button> },
      { key: 'navlun', visible: true, content: <button type="button" onClick={() => openFreightUpdateModal(row)} disabled={accountingLocked} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">Navlun Güncelle</button> },
      { key: 'yuk-evrak', visible: true, content: <button type="button" onClick={() => void handleShipmentDocumentUpload(row, 'loading')} disabled={accountingLocked || uploadingShipmentDocumentKey === `${row.rowKey}:loading`} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"><Upload className="h-3 w-3 shrink-0" /><span className="truncate">{uploadingShipmentDocumentKey === `${row.rowKey}:loading` ? 'Yükleniyor...' : 'Yük. Evrakı'}</span></button> },
      { key: 'bosalt-evrak', visible: true, content: <button type="button" onClick={() => void handleShipmentDocumentUpload(row, 'delivery')} disabled={accountingLocked || uploadingShipmentDocumentKey === `${row.rowKey}:delivery`} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-60 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200"><Upload className="h-3 w-3 shrink-0" /><span className="truncate">{uploadingShipmentDocumentKey === `${row.rowKey}:delivery` ? 'Yükleniyor...' : 'Boş. Evrakı'}</span></button> },
      { key: 'fatura-evrak', visible: !!(externalRows && ['invoice-waiting', 'invoice-rejected'].includes(String(row.accountingStatus || '').trim())), content: <button type="button" onClick={() => void handleShipmentDocumentUpload(row, 'invoice')} disabled={accountingLocked || uploadingShipmentDocumentKey === `${row.rowKey}:invoice`} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"><Upload className="h-3 w-3 shrink-0" /><span className="truncate">{uploadingShipmentDocumentKey === `${row.rowKey}:invoice` ? 'Yükleniyor...' : 'Fatura Evrakı'}</span></button> },
      { key: 'arsiv', visible: true, content: <button type="button" onClick={() => void handleOpenArchiveModal(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"><Archive className="h-3 w-3 shrink-0" /><span className="truncate">{row.archiveCode && row.archiveCode !== '-' ? 'Arşiv Güncelle' : 'Arşive Gönder'}</span></button> },
      { key: 'arsiv-geri-al', visible: !!(row.archiveCode && row.archiveCode !== '-'), content: <button type="button" onClick={() => void handleRestoreArchive(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-orange-200 bg-orange-50 px-2 py-1.5 text-[11px] font-semibold text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200">Arşivden Geri Al</button> },
      { key: 'sms', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleSendDriverMessage(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"><MessageCircle className="h-3 w-3 shrink-0" />Sürücüye SMS</button> },
      { key: 'rota-ac', visible: true, content: <button type="button" onClick={() => void handleOpenRoute(row)} disabled={routeLoadingRowId === row.rowKey} className="inline-flex items-center justify-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">{routeLoadingRowId === row.rowKey ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <Navigation className="h-3 w-3 shrink-0" />}Rota Aç</button> },
      { key: 'rota-ilet', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleSendRouteSms(row)} disabled={routeLoadingRowId === row.rowKey} className="inline-flex items-center justify-center gap-1 rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-1.5 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-60 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200">{routeLoadingRowId === row.rowKey ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <Send className="h-3 w-3 shrink-0" />}Rota İlet</button> },
      { key: 'atama-mail', visible: !!canManageAssignments && canSendAssignmentEmail(row), content: <button type="button" onClick={() => void handleSendAssignmentTestEmail(row)} disabled={sendingAssignmentTestEmailId === row.id} className="inline-flex items-center justify-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">{sendingAssignmentTestEmailId === row.id ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <Mail className="h-3 w-3 shrink-0" />}{sendingAssignmentTestEmailId === row.id ? 'Gönderiliyor...' : 'Atama Maili Gönder'}</button> },
      { key: 'not', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleAddShipmentNote(row)} disabled={accountingLocked} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"><FileText className="h-3 w-3 shrink-0" />Sevkiyat Notu</button> },
      { key: 'konum', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleLocationCheck(row)} disabled={locationCheckingRowId === row.id} className="inline-flex items-center justify-center gap-1 rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-1.5 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-60 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200"><MapPinned className="h-3 w-3 shrink-0" />{locationCheckingRowId === row.id ? 'Kontrol...' : 'Konum'}</button> },
      { key: 'onceki-surec', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleMoveProcess(row, 'prev')} disabled={movingProcessRowId === row.id || !getProcessMoveState(row).canRevert} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200">Önceki Süreç</button> },
      { key: 'sonraki-surec', visible: !!canManageAssignments, content: <button type="button" onClick={() => void handleMoveProcess(row, 'next')} disabled={movingProcessRowId === row.id || !getProcessMoveState(row).canAdvance} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">{movingProcessRowId === row.id ? 'Güncelleniyor...' : (getProcessMoveState(row).canMoveToDeliveryApproval ? 'Teslim Onayı' : 'Sonraki Süreç')}</button> },
      { key: 'pre-approval-pdf', visible: externalRows && typeof onRefreshPreApprovalPdf === 'function' && ['invoice-approval-pending', 'invoice-check-approved'].includes(String(row.accountingStatus || '').trim()), content: <button type="button" onClick={() => void onRefreshPreApprovalPdf(row)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200">Ön Onay PDF Güncelle</button> },
      { key: 'e-imzala', visible: externalRows && (row.accountingStatus === 'invoice-approval-pending' || row.accountingStatus === 'invoice-check-approved') && typeof row.__requestESign === 'function', content: <button type="button" onClick={() => row.__requestESign?.()} className="inline-flex items-center justify-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">E-İmzala</button> },
      { key: 'atama', visible: !!canManageAssignments, content: <button type="button" onClick={() => void openAssignModalForRow(row)} disabled={accountingLocked} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"><UserPlus className="h-3 w-3 shrink-0" />Atama Yap</button> },
      { key: 'duzenle', visible: true, content: <button type="button" onClick={() => void handleEditRow(row)} disabled={accountingLocked || loadingEditShipmentId === row.id} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">{loadingEditShipmentId === row.id ? 'Yükleniyor...' : 'Düzenle'}</button> },
      { key: 'atama-iptal', visible: true, content: <button type="button" onClick={() => void handleUnassignRow(row)} disabled={accountingLocked || unassigningShipmentId === row.id} title={accountingLocked ? getAccountingLockedMessage() : undefined} className="inline-flex items-center justify-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">{unassigningShipmentId === row.id ? 'İptal Ediliyor...' : 'Atamayı İptal Et'}</button> },
    ];

    return (
      <div className="flex flex-wrap gap-1.5">
        {buttons.filter((b) => b.visible).map((b) => <div key={b.key}>{b.content}</div>)}
      </div>
    );
  };

  const handleEditRow = async (row: OperationRow) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    try {
      setLoadingEditShipmentId(row.id);
      const response: any = await supplyService.getById(row.id);
      if (!response?.data) throw new Error('Sevk siparişi bulunamadı');
      setEditingWizardShipment(response.data);
      setWizardOpen(true);
    } catch (error: any) {
      toast.error(error?.message || 'Sevk siparişi açılamadı');
    } finally {
      setLoadingEditShipmentId(null);
    }
  };

  const buildClonePayloadFromShipment = (shipment: any) => {
    const routePoints = Array.isArray(shipment?.route_points) ? shipment.route_points : [];
    const pickupStops = routePoints
      .filter((point: any) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'loading')
      .sort((a: any, b: any) => Number(a?.stop_order || 0) - Number(b?.stop_order || 0))
      .map((point: any) => ({
        id: String(point?.id || ''),
        label: point?.customer_name || point?.location_name || '',
        city: point?.city || '',
        district: point?.district || '',
        address: point?.address || '',
        latitude: point?.latitude != null ? Number(point.latitude) : null,
        longitude: point?.longitude != null ? Number(point.longitude) : null,
      }));
    const deliveryStops = routePoints
      .filter((point: any) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'unloading')
      .sort((a: any, b: any) => Number(a?.stop_order || 0) - Number(b?.stop_order || 0))
      .map((point: any) => ({
        id: String(point?.id || ''),
        label: point?.customer_name || point?.location_name || '',
        city: point?.city || '',
        district: point?.district || '',
        address: point?.address || '',
        latitude: point?.latitude != null ? Number(point.latitude) : null,
        longitude: point?.longitude != null ? Number(point.longitude) : null,
      }));

    return {
      planKodu: null,
      yuklemeTalepTarihi: shipment?.yukleme_talep_tarihi || shipment?.pickup_date || null,
      hizliArama: shipment?.hizli_arama || shipment?.quick_search || null,
      durum: 'tedarik-bekleniyor',
      yuklemeMusterisiAd: shipment?.yukleme_musterisi_ad || null,
      yuklemeIl: shipment?.yukleme_il || null,
      yuklemeIlce: shipment?.yukleme_ilce || null,
      yuklemeAdres: shipment?.yukleme_adres || null,
      teslimMusterisiAd: shipment?.teslim_musterisi_ad || null,
      teslimIl: shipment?.teslim_il || null,
      teslimIlce: shipment?.teslim_ilce || null,
      teslimAdres: shipment?.teslim_adres || null,
      faturaBasligi: shipment?.fatura_basligi || null,
      invoiceCustomerId: shipment?.invoice_customer_id || null,
      aracTipi: '',
      aracTuru: shipment?.arac_turu || null,
      yukTipi: shipment?.yuk_tipi || null,
      yukAgirlik: Number(shipment?.yuk_agirlik || 0),
      yukHacim: Number(shipment?.yuk_hacim || 0),
      mesafe: Number(shipment?.mesafe || 0),
      sure: Number(shipment?.sure || 0),
      maliyet: Number(shipment?.maliyet || shipment?.navlun || 0),
      notlar: shipment?.notlar || null,
      komisyon: Number(shipment?.komisyon || 0),
      genelToplam: Number(shipment?.genel_toplam || 0),
      gerceklesenTlKm: Number(shipment?.gerceklesen_tl_km || 0),
      ugarmaBedeli: Number(shipment?.ugrama_bedeli || 0),
      beklemeBedeli: Number(shipment?.bekleme_bedeli || 0),
      hammaliyeBedeli: Number(shipment?.hammaliye_bedeli || 0),
      yatmaBedeli: Number(shipment?.yatma_bedeli || 0),
      movementType: shipment?.movement_type || 'warehouse-to-customer',
      pickupWarehouseId: shipment?.pickup_warehouse_id || null,
      deliveryWarehouseId: shipment?.delivery_warehouse_id || null,
      pickupCustomerId: shipment?.pickup_customer_id || null,
      deliveryCustomerId: shipment?.delivery_customer_id || null,
      pickupLatitude: shipment?.pickup_latitude != null ? Number(shipment.pickup_latitude) : null,
      pickupLongitude: shipment?.pickup_longitude != null ? Number(shipment.pickup_longitude) : null,
      deliveryLatitude: shipment?.delivery_latitude != null ? Number(shipment.delivery_latitude) : null,
      deliveryLongitude: shipment?.delivery_longitude != null ? Number(shipment.delivery_longitude) : null,
      pickupShiftId: shipment?.pickup_shift_id || null,
      deliveryShiftId: shipment?.delivery_shift_id || null,
      istenenTeslimTarihi: shipment?.istenen_teslim_tarihi || shipment?.delivery_date || null,
      palletCount: Number(shipment?.pallet_count || 0),
      vehicleCount: Number(shipment?.vehicle_count || 1),
      stopCount: Number(shipment?.stop_count || 1),
      requireReferenceWaybill: Boolean(shipment?.require_reference_waybill),
      phoneOnlyQuote: Boolean(shipment?.phone_only_quote),
      shipmentContactPhone: shipment?.shipment_contact_phone || null,
      kesinFiyat: Number(shipment?.navlun || shipment?.maliyet || 0) || null,
      pricingMode: shipment?.pricing_mode || 'fixed',
      targetPrice: shipment?.target_price != null ? Number(shipment.target_price) : null,
      nearestHubName: shipment?.nearest_hub_name || null,
      nearestHubKm: shipment?.nearest_hub_km != null ? Number(shipment.nearest_hub_km) : null,
      nearestHubLatitude: shipment?.nearest_hub_latitude != null ? Number(shipment.nearest_hub_latitude) : null,
      nearestHubLongitude: shipment?.nearest_hub_longitude != null ? Number(shipment.nearest_hub_longitude) : null,
      intermediateStops: {
        pickup: pickupStops,
        delivery: deliveryStops,
      },
    };
  };

  const handleDuplicateRow = async (row: OperationRow) => {
    try {
      setDuplicatingShipmentId(row.id);
      const response: any = await supplyService.getById(row.id);
      if (!response?.data) throw new Error('Sipariş bulunamadı');
      const payload = buildClonePayloadFromShipment(response.data);
      const createResponse: any = await supplyService.create(payload);
      if (!createResponse?.success) throw new Error(createResponse?.message || 'Sipariş kopyalanamadı');
      toast.success('Sipariş kopyalandı ve tedarik bekleniyor olarak oluşturuldu');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Sipariş kopyalanamadı');
    } finally {
      setDuplicatingShipmentId(null);
    }
  };

  const openFreightUpdateModal = (row: OperationRow) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    setFreightModalRow(row);
    setFreightUpdateForm({
      amount: String(Number(row.navlun || 0) || ''),
      reasonCode: 'waiting',
      reasonText: 'Bekleme oluştu',
      note: '',
    });
  };

  const submitFreightUpdate = async () => {
    if (!freightModalRow) return;
    try {
      setSavingFreightUpdate(true);
      const amount = Number(freightUpdateForm.amount || 0);
      const response: any = await supplyService.updateFreight(freightModalRow.id, {
        freightAmount: amount,
        reasonCode: freightUpdateForm.reasonCode,
        reasonText: freightUpdateForm.reasonText,
        note: freightUpdateForm.note || undefined,
      });
      if (!response?.success) throw new Error(response?.message || 'Navlun güncellenemedi');
      toast.success(response?.message || 'Navlun güncellendi');
      setFreightModalRow(null);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Navlun güncellenemedi');
    } finally {
      setSavingFreightUpdate(false);
    }
  };

  const handleUnassignRow = async (row: OperationRow) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    if (!window.confirm(`#${row.sevkKodu} ataması geri alınsın mı?`)) return;
    try {
      setUnassigningShipmentId(row.id);
      const response: any = await supplyService.unassignSupply(row.id);
      if (!response?.success) throw new Error(response?.message || 'Atama geri alınamadı');
      toast.success(response.message || 'Atama geri alındı');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Atama geri alınamadı');
    } finally {
      setUnassigningShipmentId(null);
    }
  };

  const openAssignModalForRow = async (row: OperationRow) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    setAssignRow(row);
    setAssignType('own-fleet');
    setSelectedVehicleId('');
    setSelectedApplicationId('');
    setAgreedPrice('');
    setFleetSearch('');
    setShowCreateSpotDriverModal(false);
    setShowAssignModal(true);
    await loadAssignmentSources();
  };

  const getSuggestedSpotAgreedPrice = (row?: OperationRow | null) => {
    const suggested = Number(row?.navlun || 0);
    return suggested > 0 ? String(suggested) : '';
  };

  const resetNewSpotDriverForm = () => {
    setNewSpotDriverForm({
      full_name: '',
      phone: '',
      tc_kimlik_no: '',
      vehicle_type: '',
      cekici_plaka: '',
      dorse_plaka: '',
      city: '',
      district: '',
    });
  };

  const submitNewSpotDriver = async () => {
    if (!String(newSpotDriverForm.full_name || '').trim() || !String(newSpotDriverForm.phone || '').trim()) {
      toast.error('Ad soyad ve telefon zorunlu');
      return;
    }
    if (!isValidSpotPlate(String(newSpotDriverForm.cekici_plaka || ''))) return toast.error('Cekici plaka formati gecersiz');
    if (String(newSpotDriverForm.dorse_plaka || '').trim() && !isValidSpotPlate(String(newSpotDriverForm.dorse_plaka || ''))) return toast.error('Dorse plaka formati gecersiz');
    try {
      setCreatingSpotDriver(true);
      const response: any = await driverService.createApplication({
        ...newSpotDriverForm,
        full_name: normalizeUpper(String(newSpotDriverForm.full_name || '').trim()),
        phone: normalizePhoneInput(String(newSpotDriverForm.phone || '').trim()),
        tc_number: String(newSpotDriverForm.tc_kimlik_no || '').trim() || null,
        vehicle_type: normalizeUpper(String(newSpotDriverForm.vehicle_type || '').trim()) || null,
        cekici_plaka: normalizePlate(String(newSpotDriverForm.cekici_plaka || '').trim()) || null,
        dorse_plaka: normalizePlate(String(newSpotDriverForm.dorse_plaka || '').trim()) || null,
        city: normalizeUpper(String(newSpotDriverForm.city || '').trim()) || null,
        district: normalizeUpper(String(newSpotDriverForm.district || '').trim()) || null,
      });
      await loadAssignmentSources();
      const createdId = String(response?.data?.id || '');
      if (createdId) setSelectedApplicationId(createdId);
      setShowCreateSpotDriverModal(false);
      resetNewSpotDriverForm();
      toast.success(response?.message || 'Spot sürücü eklendi');
    } catch (error: any) {
      toast.error(error?.message || 'Spot sürücü eklenemedi');
    } finally {
      setCreatingSpotDriver(false);
    }
  };

  const closeOpenBidRequestsForShipment = async (shipmentId: string) => {
    try {
      await bidService.resetBidRequestsByShipment(shipmentId);
      setActiveBidRequestShipmentIds((current) => current.filter((id) => id !== String(shipmentId)));
    } catch (error) {
      console.error('Close bid requests failed:', error);
      throw error;
    }
  };

  const createOfferCollectionForRow = async (row: OperationRow) => {
    if (activeBidRequestShipmentIds.includes(String(row.id))) return { success: false, message: 'Kayit zaten teklif toplama surecinde' };

    try {
      const bidRequestResponse: any = await bidService.createBidRequest({
        shipmentId: row.id,
        sevkKodu: row.sevkKodu,
        loadingPoint: row.yuklemeMusterisi,
        loadingCity: row.yuklemeIli,
        loadingAddress: row.yuklemeAdres,
        deliveryPoint: row.bosaltmaMusterisi,
        deliveryCity: row.bosaltmaIli,
        deliveryAddress: row.teslimAdres,
        cargoType: row.hizmetTuru,
        cargoWeight: row.yukAgirlik,
        cargoVolume: row.yukHacim,
        distanceKm: row.seferKm,
        vehicleType: row.istenenKasaTipi || row.lojistikTuru,
        targetPrice: row.navlun,
        plannedLoadingTime: row.yuklemeTarihi,
        bidDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        minimumBid: row.navlun || 0,
        shipmentContactPhone: row.shipmentContactPhone || '',
        notes: row.operasyonNot,
        createdBy: 1,
        eligibleDriverIds: eligibleDrivers.map((driver) => Number(driver.id)).filter((id) => id > 0),
      });

      const bidRequestId = bidRequestResponse?.data?.id || bidRequestResponse?.id;
      if (!bidRequestId) {
        return { success: false, message: bidRequestResponse?.message || 'Teklif talebi oluşturulamadı' };
      }

      await supplyService.updateStatus(row.id, 'spot-teklif-bekleniyor');
      setActiveBidRequestShipmentIds((current) => [...new Set([...current, String(row.id)])]);

      return { success: true };
    } catch (error: any) {
      console.error('Create offer collection failed:', error);
      return { success: false, message: error?.message || 'Kayıt teklif toplamaya alınamadı' };
    }
  };

  const handleSingleMoveToOfferCollection = async (row: OperationRow) => {
    setOfferCollectionConfirmRows([row]);
  };

  const executeSingleOfferCollection = async (row: OperationRow) => {
    try {
      setOfferCollectionLoading(true);
      setOfferCollectionLoadingCode(row.sevkKodu || '');
      const result = await createOfferCollectionForRow(row);
      if (!result?.success) {
        toast.error(result?.message || 'Kayıt teklif toplamaya alınamadı');
        return;
      }

      toast.success(result?.message || 'Kayıt teklif toplamaya alındı');
      setOfferCollectionConfirmRows([]);
      await loadRows();
    } finally {
      setOfferCollectionLoading(false);
      setOfferCollectionLoadingCode('');
    }
  };

  const handleBulkMoveToOfferCollection = async () => {
    if (selectedRows.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }
    setOfferCollectionConfirmRows(selectedRows);
    return;

  };

  const confirmOfferCollection = async () => {
    const selectedRows = offerCollectionConfirmRows;
    if (selectedRows.length === 0) return;

    try {
      setOfferCollectionLoading(true);
      setOfferCollectionLoadingCode(selectedRows.length === 1 ? (selectedRows[0].sevkKodu || '') : `${selectedRows.length} kayıt`);
      let successCount = 0;
      let firstErrorMessage = '';
      for (const row of selectedRows) {
        const result = await createOfferCollectionForRow(row);
        if (result?.success) successCount += 1;
        else if (!firstErrorMessage) firstErrorMessage = result?.message || '';
      }

      if (successCount > 0) {
        toast.success(`${successCount} kayıt teklif toplamaya alındı`);
        setOfferCollectionConfirmRows([]);
        await loadRows();
      } else {
        toast.error(firstErrorMessage || 'Kayıtlar teklif toplamaya alınamadı');
      }
    } finally {
      setOfferCollectionLoading(false);
      setOfferCollectionLoadingCode('');
    }
  };

  useEffect(() => {
    const singleRow = offerCollectionConfirmRows.length === 1 ? offerCollectionConfirmRows[0] : null;
    if (!singleRow) {
      setEligibleDrivers([]);
      setEligibleDriversMeta(null);
      return;
    }
    let cancelled = false;
    const fetchEligible = async () => {
      setEligibleDriversLoading(true);
      try {
        const res: any = await bidService.previewEligibleDrivers(singleRow.pickupLatitude, singleRow.pickupLongitude);
        if (!cancelled) {
          setEligibleDrivers(Array.isArray(res?.data) ? res.data : []);
          setEligibleDriversMeta(res?.meta || null);
        }
      } catch {
        if (!cancelled) {
          setEligibleDrivers([]);
          setEligibleDriversMeta(null);
        }
      } finally {
        if (!cancelled) setEligibleDriversLoading(false);
      }
    };
    void fetchEligible();
    return () => { cancelled = true; };
  }, [offerCollectionConfirmRows]);

  const handleBulkProcessStatusUpdate = async () => {
    if (!canBulkUpdateProcessStatus || selectedRows.length === 0 || bulkUpdatingProcessStatus) return;

    const ids = Array.from(new Set(selectedRows.map((row) => String(row.id || '').trim()).filter(Boolean)));
    if (ids.length === 0) {
      toast.error('Güncellenecek kayıt bulunamadı');
      return;
    }

    try {
      setBulkUpdatingProcessStatus(true);
      const response: any = await supplyService.bulkUpdateProcessStatus(ids, bulkProcessStatusValue);
      if (!response?.success) throw new Error(response?.message || 'Toplu durum güncelleme başarısız');
      toast.success(response?.message || `${ids.length} kayıt güncellendi`);
      setBulkProcessStatusModalOpen(false);
      await loadRows();
    } catch (error: any) {
      console.error('Bulk process status update failed:', error);
      toast.error(error?.message || 'Toplu durum güncelleme başarısız');
    } finally {
      setBulkUpdatingProcessStatus(false);
    }
  };

  const handleBulkAccountingStatusUpdate = async () => {
    if (selectedRows.length === 0 || bulkUpdatingAccounting) return;

    const ids = Array.from(new Set(selectedRows.map((row) => String(row.id || '').trim()).filter(Boolean)));
    if (ids.length === 0) {
      toast.error('Güncellenecek kayıt bulunamadı');
      return;
    }

    try {
      setBulkUpdatingAccounting(true);
      let successCount = 0;
      for (const id of ids) {
        try {
          const response: any = await accountingService.setAccountingStatus(id, bulkAccountingTargetStatus);
          if (response?.success) successCount++;
        } catch (error) {
          console.error(`Accounting status update failed for shipment ${id}:`, error);
        }
      }
      toast.success(`${successCount}/${ids.length} kayıt ${getAccountingStatusText(bulkAccountingTargetStatus)} durumuna alındı`);
      setBulkAccountingModalOpen(false);
      setSelectedRowKeys([]);
      if (externalRows && typeof onBulkAccountingStatusUpdate === 'function') {
        await onBulkAccountingStatusUpdate();
      } else {
        await loadRows();
      }
    } catch (error: any) {
      console.error('Bulk accounting status update failed:', error);
      toast.error(error?.message || 'Toplu güncelleme başarısız');
    } finally {
      setBulkUpdatingAccounting(false);
    }
  };

  const handleRemoveFromOfferCollection = async (row: OperationRow) => {
    if (!window.confirm(`#${row.sevkKodu} teklif sürecinden çıkarılsın mı?`)) return;

    try {
      await closeOpenBidRequestsForShipment(row.id);
      await supplyService.updateStatus(row.id, 'tedarik-bekleniyor');
      toast.success('Kayıt teklif sürecinden çıkarıldı');
      await loadRows();
    } catch (error) {
      console.error('Remove from offer collection failed:', error);
      toast.error('Kayıt teklif sürecinden çıkarılamadı');
    }
  };

  const handleSendDriverMessage = async (row: OperationRow) => {
    const message = window.prompt(`Sürücüye gönderilecek SMS mesajını girin (#${row.sevkKodu})`);
    if (!message?.trim()) return;

    try {
      const response: any = await shipmentService.sendDriverMessage(row.id, message.trim());
      if (!response?.success) throw new Error(response?.message || 'Mesaj gönderilemedi');
      toast.success(response.message || 'Sürücüye mesaj gönderildi');
      await loadRows();
    } catch (error: any) {
      console.error('Send driver message failed:', error);
      toast.error(error?.message || 'Sürücüye mesaj gönderilemedi');
    }
  };

  const [routeLoadingRowId, setRouteLoadingRowId] = useState<string | null>(null);
  const [routeSheet, setRouteSheet] = useState<{ row: OperationRow; route: any } | null>(null);
  const [showFullscreenRouteMap, setShowFullscreenRouteMap] = useState(false);

  const fetchShipmentRoute = async (row: OperationRow) => {
    try {
      const response: any = await shipmentService.getRoute(row.id);
      if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Rota alınamadı');
      }
      return response.data;
    } catch (error: any) {
      console.error('Fetch shipment route failed:', error);
      toast.error(error?.message || 'Rota hesaplanırken hata oluştu');
      return null;
    }
  };

  const handleOpenRoute = async (row: OperationRow) => {
    setRouteLoadingRowId(row.rowKey);
    try {
      const route = await fetchShipmentRoute(row);
      if (!route) return;
      // TIR rotası uygulama içinde çizilir. Google Maps URL API'sinde TIR profili
      // bulunmadığı için önceden olduğu gibi dışarı açmak her zaman otomobil
      // rotası veriyordu.
      setRouteSheet({ row, route });
    } finally {
      setRouteLoadingRowId(null);
    }
  };

  const handleSendRouteSms = async (row: OperationRow) => {
    setRouteLoadingRowId(row.rowKey);
    try {
      const route = await fetchShipmentRoute(row);
      if (!route) return;
      const parts: string[] = [`#${row.sevkKodu} TIR rotası`];
      if (route.distance_km != null) parts.push(`mesafe ${route.distance_km} km`);
      if (route.duration_h != null) parts.push(`tahmini ${route.duration_h} saat`);
      const params = route.truckParams || {};
      if (params.weight != null) parts.push(`tonaj ${params.weight} t`);
      const stops = Array.isArray(route.stops) ? route.stops : [];
      if (stops.length) {
        parts.push(`duraklar: ${stops.map((stop: any, index: number) => `${index + 1}-${stop.label}`).join(' | ')}`);
      }
      if (route.google_maps_url) parts.push(`harita: ${route.google_maps_url}`);
      const response: any = await shipmentService.sendDriverMessage(row.id, parts.join('\n'));
      if (!response?.success) throw new Error(response?.message || 'Mesaj gönderilemedi');
      toast.success('Rota sürücüye SMS olarak gönderildi');
      await loadRows();
    } catch (error: any) {
      console.error('Send route SMS failed:', error);
      toast.error(error?.message || 'Rota SMS iletilemedi');
    } finally {
      setRouteLoadingRowId(null);
    }
  };

  const handleAddShipmentNote = async (row: OperationRow) => {
    if (isAccountingLockedForRow(row)) {
      toast.info(getAccountingLockedMessage());
      return;
    }
    const note = window.prompt(`Sevkiyat notunu girin (#${row.sevkKodu})`, row.sonNot && row.sonNot !== '-' ? row.sonNot : '');
    if (!note?.trim()) return;

    try {
      const response: any = await shipmentService.addShipmentNote(row.id, note.trim());
      if (!response?.success) throw new Error(response?.message || 'Not kaydedilemedi');
      toast.success(response.message || 'Sevkiyat notu kaydedildi');
      await loadRows();
    } catch (error: any) {
      console.error('Add shipment note failed:', error);
      toast.error(error?.message || 'Sevkiyat notu kaydedilemedi');
    }
  };

  const openPaymentApprovalModal = async (row: OperationRow, mode: 'controller' | 'approver') => {
    setPaymentApprovalRow(row);
    setPaymentApprovalRows([row]);
    setPaymentApprovalMode(mode);
    setPaymentApprovalSelectedUserId(String(user?.id || ''));
    setPaymentApprovalSignatureAvailable(false);
    setPaymentApprovalModalOpen(true);
    try {
      const resp: any = await userService.getUsers({ status: 'active' });
      const users = Array.isArray(resp?.data) ? resp.data : [];
      const filteredUsers = users.filter((item: any) => mode === 'controller'
        ? Boolean(item?.is_payment_preapproval_controller ?? item?.isPaymentPreapprovalController)
        : Boolean(item?.is_payment_final_approver ?? item?.isPaymentFinalApprover));
      setPaymentApprovalUsers(filteredUsers);
    } catch (e) {
      console.error('Failed to load users:', e);
      setPaymentApprovalUsers([]);
    }
  };

  const openBulkPaymentApprovalModal = async (mode: 'controller' | 'approver') => {
    if (selectedRows.length === 0) {
      toast.error('Lutfen en az bir kayit secin');
      return;
    }
    setPaymentApprovalRows(selectedRows);
    setPaymentApprovalRow(selectedRows[0] || null);
    setPaymentApprovalMode(mode);
    setPaymentApprovalSelectedUserId(String(user?.id || ''));
    setPaymentApprovalSignatureAvailable(false);
    setPaymentApprovalModalOpen(true);
    try {
      const resp: any = await userService.getUsers({ status: 'active' });
      const users = Array.isArray(resp?.data) ? resp.data : [];
      const filteredUsers = users.filter((item: any) => mode === 'controller'
        ? Boolean(item?.is_payment_preapproval_controller ?? item?.isPaymentPreapprovalController)
        : Boolean(item?.is_payment_final_approver ?? item?.isPaymentFinalApprover));
      setPaymentApprovalUsers(filteredUsers);
    } catch (e) {
      console.error('Failed to load users:', e);
      setPaymentApprovalUsers([]);
    }
  };

  const clearPaymentSignature = () => {
    const canvas = paymentApprovalSignatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setPaymentApprovalSignatureAvailable(false);
  };

  useEffect(() => {
    if (!paymentApprovalModalOpen) return;
    const canvas = paymentApprovalSignatureRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    canvas.width = parent.clientWidth - 8;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
  }, [paymentApprovalModalOpen]);

  const handlePaymentApprovalConfirm = async () => {
    const rowsToUpdate = paymentApprovalRows.length > 0 ? paymentApprovalRows : (paymentApprovalRow ? [paymentApprovalRow] : []);
    if (rowsToUpdate.length === 0 || !paymentApprovalSelectedUserId) {
      toast.error('Lütfen bir kullanıcı seçin');
      return;
    }
    if (!paymentApprovalSignatureAvailable) {
      toast.error('Lütfen imza atın');
      return;
    }
    setPaymentApprovalSaving(true);
    try {
      const canvas = paymentApprovalSignatureRef.current;
      const signatureData = canvas ? canvas.toDataURL('image/png') : '';
      const field = paymentApprovalMode === 'controller' ? 'payment_controller_id' : 'payment_approver_id';
      const sigField = paymentApprovalMode === 'controller' ? 'payment_controller_signature' : 'payment_approver_signature';
      const dateField = paymentApprovalMode === 'controller' ? 'payment_controller_at' : 'payment_approved_at';
      let successCount = 0;
      for (const row of rowsToUpdate) {
        const updateData: any = {
          [field]: Number(paymentApprovalSelectedUserId),
          [sigField]: signatureData,
          [dateField]: new Date().toISOString(),
        };
        const resp: any = await supplyService.update(row.id, updateData);
        if (resp?.success) successCount += 1;
      }
      if (successCount === 0) throw new Error('Kayit basarisiz');
      toast.success(paymentApprovalMode === 'controller'
        ? `${successCount}/${rowsToUpdate.length} kayitta kontrol eden kaydedildi ve fatura onaya alindi`
        : `${successCount}/${rowsToUpdate.length} kayitta onaylayan kaydedildi`);
      setPaymentApprovalModalOpen(false);
      setPaymentApprovalRows([]);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'İşlem başarısız');
    } finally {
      setPaymentApprovalSaving(false);
    }
  };

  const getAccountingAdvanceAction = (accountingStatus: string, shipmentId: string | number) => {
    const map: Record<string, { call: () => Promise<any> }> = {
      'delivery-approval-pending': { call: () => accountingService.approveDelivery(shipmentId) },
      'invoice-waiting': { call: () => { throw new Error('Fatura yüklenmeden ilerlenemez'); } },
      'invoice-approval-pending': { call: () => accountingService.approveInvoice(shipmentId) },
      'payment-pre-approval': { call: () => { throw new Error('Fatura kontrol için controller e-imzası bekliyor'); } },
      'invoice-check-approved': { call: () => { throw new Error('Fatura onay için e-imza bekliyor'); } },
      'payment-pending': { call: () => accountingService.setAccountingStatus(shipmentId, 'payment-completed') },
    };
    return map[accountingStatus] || null;
  };

  const handleMoveProcess = async (row: OperationRow, direction: 'prev' | 'next') => {
    try {
      setMovingProcessRowId(row.id);
      const processStatus = String(row.islemDurum || '').trim();
      const accountingStatus = String(row.accountingStatus || '').trim();
      const isInAccountingFlow = ACCOUNTING_ONLY_FLOW.includes(accountingStatus as any);

      const isDeliveryCompletionTransition = direction === 'next' && processStatus === 'bosaltma-tamamlandi' && !isInAccountingFlow;

      let response: any;
      if (isDeliveryCompletionTransition) {
        response = await accountingService.completeDelivery(row.id);
      } else if (direction === 'next' && isInAccountingFlow) {
        const action = getAccountingAdvanceAction(accountingStatus, row.id);
        if (!action) throw new Error('İleri süreç adımı bulunamadı');
        response = await action.call();
      } else if (direction === 'next') {
        response = await supplyService.advanceProcessStatus(row.id);
      } else {
        response = await supplyService.revertProcessStatus(row.id);
      }

      if (!response?.success) throw new Error(response?.message || 'Süreç güncellenemedi');
      toast.success(response.message || 'Süreç güncellendi');
      await loadRows();
    } catch (error: any) {
      console.error('Process move failed:', error);
      toast.error(error?.message || 'Süreç güncellenemedi');
    } finally {
      setMovingProcessRowId(null);
    }
  };

  const handleLocationCheck = async (row: OperationRow) => {
    try {
      setLocationCheckingRowId(row.id);
      const effectiveStatus = String(getEffectiveFlowStatus(row));
      const target = effectiveStatus.startsWith('bosaltma') || effectiveStatus === 'tasima-yolunda' || effectiveStatus === 'teslim-noktasina-ulasildi' ? 'delivery' : 'pickup';
      const response: any = await supplyService.checkLocationStatus(row.id, target as any);
      const data = response?.data || response;
      if (!response?.success && !data?.statusLabel) throw new Error(response?.message || 'Konum kontrolü başarısız');
      const message = `${data.statusLabel}${data.distanceKm != null ? ` • Mesafe: ${data.distanceKm} km` : ''}${data.currentAddress ? ` • Konum: ${data.currentAddress}` : ''}`;
      toast.success(message);
    } catch (error: any) {
      console.error('Location check failed:', error);
      toast.error(error?.message || 'Konum kontrolü yapılamadı');
    } finally {
      setLocationCheckingRowId(null);
    }
  };

  const getProcessMoveState = (row?: OperationRow | null) => {
    const processStatus = String(row?.islemDurum || '').trim();
    const accountingStatus = String(row?.accountingStatus || '').trim();
    const isInAccountingFlow = ACCOUNTING_ONLY_FLOW.includes(accountingStatus as any);
    const canMoveToDeliveryApproval = processStatus === 'bosaltma-tamamlandi' && !isInAccountingFlow;

    const currentIndex = isInAccountingFlow
      ? PROCESS_FLOW_ORDER.indexOf(accountingStatus as typeof PROCESS_FLOW_ORDER[number])
      : PROCESS_FLOW_ORDER.indexOf(processStatus as typeof PROCESS_FLOW_ORDER[number]);

    const isLastStep = currentIndex >= PROCESS_FLOW_ORDER.length - 1;
    const isFirstStep = currentIndex <= 0;

    return {
      canAdvance: canMoveToDeliveryApproval || (currentIndex !== -1 && !isLastStep),
      canRevert: currentIndex !== -1 && !isFirstStep,
      canMoveToDeliveryApproval,
    };
  };

  const handleOpenActiveShipments = async (row: OperationRow) => {
    const driverId = row.driverId;
    if (!driverId) return;
    setActiveShipmentsDriverId(driverId);
    setSelectedActiveShipmentId(null);
    setShowActiveShipmentsModal(true);
    setActiveShipmentsLoading(true);
    try {
      const response: any = await generalList2Service.getDriverActiveShipments(driverId);
      const list = Array.isArray(response?.data) ? response.data : [];
      setActiveShipmentsList(list);
    } catch (error) {
      console.error('Active shipments fetch failed:', error);
      toast.error('Aktif sevkiyatlar alınamadı');
      setActiveShipmentsList([]);
    } finally {
      setActiveShipmentsLoading(false);
    }
  };

  const handleSelectActiveShipment = (shipment: any) => {
    setShowActiveShipmentsModal(false);
    const shipmentNo = shipment.shipment_no || shipment.id || '';
    navigate(`/tracking-list?q=${encodeURIComponent(String(shipmentNo))}`);
  };

  const handleRecalculateNearestHubs = async () => {
    try {
      setRecalculatingNearestHubs(true);
      const response: any = await supplyService.recalculateNearestHubs(true);
      toast.success(response?.message || 'En yakın hub bilgileri güncellendi');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'En yakın hub hesaplanamadı');
    } finally {
      setRecalculatingNearestHubs(false);
    }
  };

  const handleCalculateRouteKms = async () => {
    try {
      setCalculatingRouteKms(true);
      const response: any = await supplyService.recalculateRouteDistances();
      const data = response?.data;
      let msg = response?.message || '';
      if (data?.failedCount > 0 && Array.isArray(data?.results)) {
        const errors = data.results.filter((r: any) => r.error).slice(0, 5);
        if (errors.length > 0) {
          msg += ' | Hatalar: ' + errors.map((e: any) => `#${e.shipmentNo || e.id}: ${e.error}`).join(', ');
          if (data.results.filter((r: any) => r.error).length > 5) msg += '...';
        }
      }
      toast.success(msg);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'KM hesaplanamadı');
    } finally {
      setCalculatingRouteKms(false);
    }
  };

  const handleSyncPointsText = async () => {
    if (!confirm('Tüm sevkiyatların yükleme/teslim noktaları metinleri arka planda yeniden hesaplanacak. Devam edilsin mi?')) return;
    try {
      setSyncingPoints(true);
      const result: any = await generalList2Service.syncPointsText();
      toast.success(result?.message || 'Nokta bilgileri arka planda güncelleniyor');
    } catch (err: any) {
      toast.error(err?.message || 'Güncelleme başarısız');
    } finally {
      setSyncingPoints(false);
    }
  };

  const deferredFleetSearch = useDeferredValue(fleetSearch);

  const fleetOptionsBase = useMemo(() => {
    const internalDriversById = new Map(
      internalDrivers
        .filter((driver: any) => driver?.id != null)
        .map((driver: any) => [String(driver.id), driver])
    );
    const activeAssignments = effectiveRowsWithLkm.filter((row) => String(row.id) !== String(assignRow?.id || '') && ACTIVE_ASSIGNMENT_STATUSES.has(String(row.islemDurum || '').trim()));
    const activeAssignmentsByPlate = new Map<string, Array<typeof activeAssignments[number]>>();
    activeAssignments.forEach((row) => {
      const plate = normalizePlate(row.cekici);
      if (!plate) return;
      const rows = activeAssignmentsByPlate.get(plate);
      if (rows) {
        rows.push(row);
      } else {
        activeAssignmentsByPlate.set(plate, [row]);
      }
    });

    const enriched = vehicles
      .filter((vehicle: any) => {
        const ownerType = String(vehicle.owner_type || 'own').trim().toLocaleLowerCase('tr-TR');
        return ownerType === 'own' || ownerType === 'özmal' || ownerType === 'ozmal';
      })
      .map((vehicle: any) => {
        const effectiveDriverId = vehicle.effective_driver_id || vehicle.assigned_driver_id;
        const driver = effectiveDriverId != null ? internalDriversById.get(String(effectiveDriverId)) : undefined;
        const distanceToPickupKm = assignRow
          ? getCachedRouteKm(assignRow.pickupLatitude, assignRow.pickupLongitude, vehicle.latitude != null ? Number(vehicle.latitude) : null, vehicle.longitude != null ? Number(vehicle.longitude) : null)
          : null;
        const tractorPlate = vehicle.cekici_plaka || vehicle.plate_number || '-';
        const activeTaskRows = activeAssignmentsByPlate.get(normalizePlate(tractorPlate)) || [];
        const hasActiveTask = activeTaskRows.length > 0;
        const vehicleStatus = String(vehicle.status || '').trim().toLocaleLowerCase('tr-TR');
        const driverUserStatus = String(vehicle.effective_driver_user_status || driver?.user_status || 'active').trim().toLocaleLowerCase('tr-TR');
        const driverOperationalStatus = String(vehicle.effective_driver_status || driver?.status || 'available').trim().toLocaleLowerCase('tr-TR');
        const isVehicleActive = vehicleStatus === '' || vehicleStatus === 'active' || vehicleStatus === 'aktif';
        const isDriverActive = (driverUserStatus === '' || driverUserStatus === 'active' || driverUserStatus === 'aktif') && driverOperationalStatus !== 'unavailable';
        const vehicleType = vehicle.linked_trailer_type || vehicle.vehicle_type || vehicle.assigned_vehicle_type || driver?.vehicle_type || '-';
        const isCompatible = matchesRequestedVehicleTypes(assignRow?.istenenKasaTipi, vehicleType);
        return {
          vehicleId: String(vehicle.id),
          effectiveDriverId: effectiveDriverId ? String(effectiveDriverId) : '',
          driverName: vehicle.driver_name || driver?.full_name || '-',
          driverPhone: vehicle.effective_driver_phone || driver?.phone || vehicle.driver_phone || '',
          tractorPlate,
          trailerPlate: vehicle.linked_trailer_plate || vehicle.dorse_plaka || driver?.dorse_plaka || '-',
          vehicleType,
          currentAddress: vehicle.current_address || '-',
          remainingWorkTime: vehicle.available_drive_period || driver?.available_drive_period || '-',
          workDuration: vehicle.work_duration || driver?.work_duration || vehicle.daily_work_time || '-',
          currentWeekHours: vehicle.current_week_hours || driver?.current_week_hours || '-',
          twoWeekHours: vehicle.two_week_hours || driver?.two_week_hours || '-',
          distanceToPickupKm,
          isVehicleActive,
          isDriverActive,
          activeTaskCount: activeTaskRows.length,
          activeTaskCodes: activeTaskRows.map((row) => row.sevkKodu || row.planKodu || row.id).filter(Boolean),
          hasActiveTask,
          isCompatible,
          isAssignable: isCompatible && isVehicleActive && isDriverActive && !hasActiveTask,
          hasTemporaryAssignment: Number(vehicle.has_temporary_assignment || 0) > 0,
          temporaryAssignmentStartDate: vehicle.temporary_assignment_start_date || '',
          temporaryAssignmentEndDate: vehicle.temporary_assignment_end_date || '',
          temporaryAssignmentNotes: vehicle.temporary_assignment_notes || '',
          permanentDriverName: vehicle.permanent_driver_name || '',
          temporaryDriverName: vehicle.temporary_driver_name || '',
        };
      });

    return [...enriched].sort((a, b) => {
      if (a.isAssignable !== b.isAssignable) return a.isAssignable ? -1 : 1;
      if (a.hasActiveTask !== b.hasActiveTask) return a.hasActiveTask ? 1 : -1;
      if (a.isVehicleActive !== b.isVehicleActive) return a.isVehicleActive ? -1 : 1;
      if (a.distanceToPickupKm == null && b.distanceToPickupKm == null) return 0;
      if (a.distanceToPickupKm == null) return 1;
      if (b.distanceToPickupKm == null) return -1;
      return a.distanceToPickupKm - b.distanceToPickupKm;
    });
  }, [vehicles, internalDrivers, assignRow, effectiveRowsWithLkm, getCachedRouteKm]);

  const fleetOptions = useMemo(() => {
    const searchValue = normalizeSearchText(deferredFleetSearch);
    const phoneSearch = String(deferredFleetSearch || '').replace(/\D/g, '');
    const plateSearch = normalizePlate(deferredFleetSearch);
    if (!searchValue && !phoneSearch && !plateSearch) return fleetOptionsBase;
    return fleetOptionsBase.filter((item) => {
      const haystack = normalizeSearchText([item.driverName, item.driverPhone, item.tractorPlate, item.trailerPlate, item.vehicleType, item.currentAddress, item.activeTaskCodes.join(' ')].join(' '));
      const normalizedPhone = String(item.driverPhone || '').replace(/\D/g, '');
      const tractorPlate = normalizePlate(item.tractorPlate);
      const trailerPlate = normalizePlate(item.trailerPlate);
      return haystack.includes(searchValue) || (phoneSearch && normalizedPhone.includes(phoneSearch)) || (plateSearch && (tractorPlate.includes(plateSearch) || trailerPlate.includes(plateSearch)));
    });
  }, [fleetOptionsBase, deferredFleetSearch]);

  const spotOptionsBase = useMemo(() => {
    const externalDriverByUserId = new Map(
      externalDrivers
        .filter((driver: any) => driver?.user_id != null || driver?.userId != null)
        .map((driver: any) => [String(driver.user_id ?? driver.userId), driver])
    );

    const externalDriverByDriverId = new Map(
      externalDrivers
        .filter((driver: any) => driver?.id != null)
        .map((driver: any) => [String(driver.id), driver])
    );

    const corporateCarrierMap = new Map(
      corporateCarrierAccounts.map((item: any) => [String(item.id), item.full_name || item.fullName || item.username || `Firma #${item.id}`])
    );

    const applicationOptions = applications.map((app: any) => {
      const matchedExternalDriver =
        (app.user_id != null ? externalDriverByUserId.get(String(app.user_id)) : null)
        || (app.driver_id != null ? externalDriverByDriverId.get(String(app.driver_id)) : null)
        || null;
      const lat = app.latest_latitude != null ? Number(app.latest_latitude) : null;
      const lng = app.latest_longitude != null ? Number(app.latest_longitude) : null;
      const resolvedLat = lat != null ? lat : (matchedExternalDriver?.latest_latitude != null ? Number(matchedExternalDriver.latest_latitude) : null);
      const resolvedLng = lng != null ? lng : (matchedExternalDriver?.latest_longitude != null ? Number(matchedExternalDriver.latest_longitude) : null);
      const distanceToPickupKm = assignRow ? getCachedRouteKm(assignRow.pickupLatitude, assignRow.pickupLongitude, resolvedLat, resolvedLng) : null;
      return {
        applicationId: String(app.id),
        driverId: app.driver_id != null ? String(app.driver_id) : '',
        userId: app.user_id != null ? String(app.user_id) : app.userId != null ? String(app.userId) : '',
        driverName: app.full_name || app.name || '-',
        phone: app.phone || '-',
        email: app.email || '-',
        city: app.city || '-',
        district: app.district || '-',
        externalTransportAccountUserId: app.external_transport_account_user_id != null ? String(app.external_transport_account_user_id) : '',
        externalTransportAccountName: app.external_transport_account_user_id != null ? (corporateCarrierMap.get(String(app.external_transport_account_user_id)) || `Firma #${app.external_transport_account_user_id}`) : '',
        tcNumber: app.tc_number || '-',
        invoiceTitle: app.invoice_title || '-',
        tractorPlate: app.cekici_plaka || '-',
        trailerPlate: app.dorse_plaka || '-',
        vehicleType: app.vehicle_type || '-',
        currentAddress: resolvedLat != null && resolvedLng != null ? `${resolvedLat.toFixed(5)}, ${resolvedLng.toFixed(5)}` : '-',
        distanceToPickupKm,
      };
    });

    const applicationKeys = new Set(applicationOptions.map((item) => item.userId ? `user:${item.userId}` : item.driverId ? `driver:${item.driverId}` : `application:${item.applicationId}`));

    const externalDriverOptions = externalDrivers
      .filter((driver: any) => {
        const key = driver.user_id != null || driver.userId != null
          ? `user:${String(driver.user_id ?? driver.userId)}`
          : driver.id != null
            ? `driver:${String(driver.id)}`
            : null;
        return key ? !applicationKeys.has(key) : true;
      })
      .map((driver: any) => {
        const lat = driver.latest_latitude != null ? Number(driver.latest_latitude) : null;
        const lng = driver.latest_longitude != null ? Number(driver.latest_longitude) : null;
        const distanceToPickupKm = assignRow ? getCachedRouteKm(assignRow.pickupLatitude, assignRow.pickupLongitude, lat, lng) : null;
        return {
          applicationId: `driver-${driver.id}`,
          driverId: String(driver.id || ''),
          userId: driver.user_id != null ? String(driver.user_id) : driver.userId != null ? String(driver.userId) : '',
          driverName: driver.full_name || driver.name || '-',
          phone: driver.phone || '-',
          email: driver.email || '-',
          city: driver.city || '-',
          district: driver.district || '-',
          externalTransportAccountUserId: driver.external_transport_account_user_id != null ? String(driver.external_transport_account_user_id) : '',
          externalTransportAccountName: driver.external_transport_account_user_id != null ? (corporateCarrierMap.get(String(driver.external_transport_account_user_id)) || `Firma #${driver.external_transport_account_user_id}`) : '',
          tcNumber: driver.tc_number || '-',
          invoiceTitle: driver.invoice_title || '-',
          tractorPlate: driver.cekici_plaka || '-',
          trailerPlate: driver.dorse_plaka || '-',
          vehicleType: driver.vehicle_type || '-',
          currentAddress: lat != null && lng != null ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '-',
          distanceToPickupKm,
        };
      });

    const options = [...applicationOptions, ...externalDriverOptions]
      .sort((a, b) => {
        if (a.distanceToPickupKm == null && b.distanceToPickupKm == null) return 0;
        if (a.distanceToPickupKm == null) return 1;
        if (b.distanceToPickupKm == null) return -1;
        return a.distanceToPickupKm - b.distanceToPickupKm;
      })
      .map((item) => ({
        ...item,
        isCompatible: matchesRequestedVehicleTypes(assignRow?.istenenKasaTipi, item.vehicleType),
      }));

    return options.filter((item) => selectedSpotCompanyId === 'all'
      ? true
      : selectedSpotCompanyId === 'individual'
        ? !item.externalTransportAccountUserId
        : item.externalTransportAccountUserId === selectedSpotCompanyId);
  }, [applications, externalDrivers, corporateCarrierAccounts, assignRow, getCachedRouteKm, selectedSpotCompanyId]);

  const spotOptions = useMemo(() => {
    const searchValue = normalizeSearchText(deferredFleetSearch);
    const phoneSearch = String(deferredFleetSearch || '').replace(/\D/g, '');
    const plateSearch = normalizePlate(deferredFleetSearch);
    if (!searchValue && !phoneSearch && !plateSearch) return spotOptionsBase;
    return spotOptionsBase.filter((item) => {
      const haystack = normalizeSearchText([
        item.driverName,
        item.phone,
        item.email,
        item.city,
        item.district,
        item.tcNumber,
        item.invoiceTitle,
        item.tractorPlate,
        item.trailerPlate,
        item.vehicleType,
        item.currentAddress,
      ].join(' '));
      const normalizedPhone = String(item.phone || '').replace(/\D/g, '');
      const normalizedTc = String(item.tcNumber || '').replace(/\D/g, '');
      const tractorPlate = normalizePlate(item.tractorPlate);
      const trailerPlate = normalizePlate(item.trailerPlate);
      return haystack.includes(searchValue)
        || (phoneSearch && (normalizedPhone.includes(phoneSearch) || normalizedTc.includes(phoneSearch)))
        || (plateSearch && (tractorPlate.includes(plateSearch) || trailerPlate.includes(plateSearch)));
    });
  }, [spotOptionsBase, deferredFleetSearch]);

  const submitAssignment = async () => {
    if (!assignRow) return;
    if (assignType === 'own-fleet' && !selectedVehicleId) {
      toast.error('Araç seçmelisiniz');
      return;
    }
    if (assignType === 'spot' && !selectedApplicationId) {
      toast.error('Spot sürücü seçmelisiniz');
      return;
    }
    try {
      setAssigning(true);
      const selectedSpotDriver = assignType === 'spot'
        ? spotOptions.find((item) => item.applicationId === selectedApplicationId)
        : null;
      const selectedFleetVehicle = assignType === 'own-fleet'
        ? fleetOptions.find((item) => item.vehicleId === selectedVehicleId)
        : null;
      const response: any = await supplyService.assignSupply(assignRow.id, {
        assignmentType: assignType,
        vehicleId: assignType === 'own-fleet' ? selectedVehicleId : undefined,
        applicationId: assignType === 'spot' ? selectedApplicationId : undefined,
        driverId: assignType === 'spot' ? selectedSpotDriver?.driverId || undefined : selectedFleetVehicle?.effectiveDriverId || undefined,
        agreedPrice: assignType === 'spot' ? Number(agreedPrice || 0) : undefined,
      });
      if (!response?.success) throw new Error(response?.message || 'Atama yapılamadı');

      const notificationPhone = assignType === 'spot' ? selectedSpotDriver?.phone : selectedFleetVehicle?.driverPhone;

      await taskNotificationService.notifyAssignment({
        phoneNumber: notificationPhone,
        shipmentNo: assignRow.sevkKodu,
        loadingCustomer: assignRow.yuklemeMusterisi,
        loadingCity: assignRow.yuklemeIli,
        deliveryCustomer: assignRow.bosaltmaMusterisi,
        deliveryCity: assignRow.bosaltmaIli,
        vehicleType: assignType === 'spot' ? selectedSpotDriver?.vehicleType : selectedFleetVehicle?.vehicleType,
        weightKg: assignRow.yukAgirlik,
        distanceKm: assignRow.seferKm,
        agreedPrice: assignType === 'spot' ? Number(agreedPrice || 0) : assignRow.navlun,
        loadingDate: assignRow.yuklemeTarihi,
      }).catch((error) => console.error('Assignment notification failed:', error));

      await closeOpenBidRequestsForShipment(assignRow.id);

      toast.success(response.message || 'Atama tamamlandı');
      setShowAssignModal(false);
      setAssignRow(null);
      setSelectedVehicleId('');
      setSelectedApplicationId('');
      setAgreedPrice('');
      await loadRows();
    } catch (error: any) {
      console.error('Assign failed:', error);
      toast.error(error?.message || 'Atama yapılamadı');
    } finally {
      setAssigning(false);
    }
  };

  const canSendAssignmentEmail = (row: OperationRow) => {
    const flowStatus = String(getEffectiveFlowStatus(row) || '').trim();
    return ACTIVE_ASSIGNMENT_STATUSES.has(flowStatus)
      || flowStatus === 'filo-tedarik-edildi'
      || flowStatus === 'spot-tedarik-edildi'
      || String(row.cekici || '').trim() !== '-'
      || String(row.surucuAdi || '').trim() !== '-';
  };

  const handleSendAssignmentTestEmail = async (row: OperationRow) => {
    if (!row?.id) return;
    try {
      setSendingAssignmentTestEmailId(row.id);
      const response: any = await supplyService.sendAssignmentTestEmail(row.id);
      if (!response?.success) throw new Error(response?.message || 'Test maili gönderilemedi');
      toast.success(response.message || 'Test maili gönderildi');
    } catch (error: any) {
      console.error('sendAssignmentTestEmail failed:', error);
      toast.error(error?.message || 'Test maili gönderilemedi');
    } finally {
      setSendingAssignmentTestEmailId(null);
    }
  };

  useEffect(() => {
    if (!showAssignModal || assignType !== 'spot' || !assignRow) return;
    if (String(agreedPrice || '').trim()) return;
    setAgreedPrice(getSuggestedSpotAgreedPrice(assignRow));
  }, [showAssignModal, assignType, assignRow, agreedPrice]);

  const handleManualProcessStatusChange = async (row: OperationRow, nextStatus: (typeof MANUAL_PROCESS_STATUS_OPTIONS)[number]['value']) => {
    try {
      setUpdatingRowStatusId(row.id);
      const response: any = await supplyService.updateProcessStatus(row.id, nextStatus);
      if (!response?.success) throw new Error(response?.message || 'Durum güncellenemedi');
      toast.success(response.message || 'Durum güncellendi');
      await loadRows();
    } catch (error: any) {
      console.error('Manual process status update failed:', error);
      toast.error(error?.message || 'Durum güncellenemedi');
    } finally {
      setUpdatingRowStatusId(null);
    }
  };

  const handleSupplyStatusChange = async (row: OperationRow, nextStatus: (typeof SUPPLY_STATUS_OPTIONS)[number]['value']) => {
    try {
      setUpdatingRowSupplyStatusId(row.id);
      const response: any = await supplyService.updateStatus(row.id, nextStatus);
      if (!response?.success) throw new Error(response?.message || 'Durum güncellenemedi');
      toast.success(response.message || 'Durum güncellendi');
      await loadRows();
    } catch (error: any) {
      console.error('Supply status update failed:', error);
      toast.error(error?.message || 'Durum güncellenemedi');
    } finally {
      setUpdatingRowSupplyStatusId(null);
    }
  };

  const copySelectedRows = async () => {
    if (selectedRows.length === 0) return;

    const headers = activeColumns.map((column) => column.label);
    const lines = selectedRows.map((row) => activeColumns.map((column) => {
      const rawValue = row[column.key];
      if (column.key === 'durum') return getDurumText(String(rawValue));
      if (column.key === 'islemDurum') return getFlowStatusLabel(String(getEffectiveFlowStatus(row)));
      if (column.key === 'accountingStatus') return getAccountingStatusText(String(rawValue));
      if (getDocumentBucketForColumn(column.key)) return '';
      const value = column.format ? column.format(rawValue, row) : String(rawValue ?? '-');
      return String(value ?? '').replace(/[\t\n\r]+/g, ' ').trim();
    }).join('\t'));
    const payload = [headers.join('\t'), ...lines].join('\n');

    try {
      await navigator.clipboard.writeText(payload);
      toast.success(`${selectedRows.length} satır panoya kopyalandı`);
    } catch (error) {
      console.error('Copy selected rows failed:', error);
      toast.error('Seçili satırlar panoya kopyalanamadı');
    }
  };

  const toggleSelectVisibleRows = () => {
    if (allVisibleSelected) {
      setSelectedRowKeys((current) => current.filter((key) => !visiblePageRows.some((row) => row.rowKey === key)));
    } else {
      setSelectedRowKeys((current) => [...new Set([...current, ...visiblePageRows.map((row) => row.rowKey)])]);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCopy = (event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase('tr-TR') === 'c';
      if (!isCopy || selectedRows.length === 0) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || target?.isContentEditable) return;
      event.preventDefault();
      void copySelectedRows();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRows, activeColumns]);

  return (
    <>
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 dark:bg-[#0b0b0b] dark:text-slate-100 md:p-6">
      <div className="w-full space-y-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
          <div className="mb-3 border-b border-slate-200 pb-3 dark:border-slate-800">
            <div className="flex flex-wrap gap-1.5">
              {savedTabs.map((tab) => {
                const isActive = activeTabId === tab.id;
                const isFlowTab = tab.id === FLOW_TAB_ID;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => applyTab(tab)}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                      isActive && !isFlowTab
                        ? 'border-orange-300 bg-orange-50 text-orange-700 ring-2 ring-inset ring-orange-400 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200'
                        : isActive && isFlowTab
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700 ring-2 ring-inset ring-emerald-400 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                        : isFlowTab
                        ? 'border-slate-300 bg-slate-100/60 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#1a1a1a] dark:text-slate-400 dark:hover:bg-[#222]'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-300 dark:hover:bg-[#202020]'
                    }`}
                  >
                    {isFlowTab && <PanelRightOpen className="h-3.5 w-3.5" />}
                    {!isFlowTab && tab.id !== MAIN_TAB_ID && (
                      <span
                        aria-hidden="true"
                        className={`h-2 w-2 rounded-full ${tab.visibility === 'public' ? 'bg-sky-500 dark:bg-sky-400' : 'bg-slate-400 dark:bg-slate-500'}`}
                      />
                    )}
                    {tab.name}
                  </button>
                );
              })}
            </div>

            {activeTabId !== MAIN_TAB_ID && activeTabId !== FLOW_TAB_ID && (
              <>
                <button
                  type="button"
                  onClick={overwriteActiveTab}
                  disabled={savingTabs || !canManageActiveTab}
                  className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
                >
                  Aktif Tabı Güncelle
                </button>
                <button
                  type="button"
                  onClick={deleteActiveTab}
                  disabled={savingTabs || !canManageActiveTab}
                  className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
                >
                  Tabı Sil
                </button>
              </>
            )}
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-950 dark:text-white">{title}</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <button type="button" onClick={() => void (externalRows && onExternalRefresh ? onExternalRefresh() : loadRows())} disabled={effectiveLoading} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]">
                <RefreshCw className={`h-4 w-4 ${effectiveLoading ? 'animate-spin' : ''}`} />
                Yenile
              </button>

              {loadTimeMs !== null && (
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums">
                  {loadTimeMs < 1000 ? `${loadTimeMs}ms` : `${(loadTimeMs / 1000).toFixed(1)}s`} &middot; {rows.length} kayıt
                </span>
              )}

              <button type="button" onClick={saveView} disabled={savingView || !viewLoaded} className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-950/60">
                <Save className="h-4 w-4" />
                Görünümü Kaydet
              </button>

              <button type="button" onClick={resetView} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]">
                <RotateCcw className="h-4 w-4" />
                Varsayılan
              </button>

              <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/60">
                <Download className="h-4 w-4" />
                Excel
              </button>
              <button type="button" onClick={() => setBulkSelectPasteOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-950/60">
                Excelden Seç
              </button>
              {canManageAssignments ? (
                <>
                  <button type="button" onClick={() => void handleRecalculateNearestHubs()} disabled={recalculatingNearestHubs} className="inline-flex items-center gap-2 rounded-xl border border-cyan-300 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-60 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:hover:bg-cyan-950/60">
                    <RefreshCw className={`h-4 w-4 ${recalculatingNearestHubs ? 'animate-spin' : ''}`} />
                    {recalculatingNearestHubs ? 'Hub Hesaplanıyor...' : 'Eksik Hub Hesapla'}
                  </button>
                  <button type="button" onClick={() => void handleCalculateRouteKms()} disabled={calculatingRouteKms} className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60">
                    <LucideMap className={`h-4 w-4 ${calculatingRouteKms ? 'animate-spin' : ''}`} />
                    {calculatingRouteKms ? 'KM Hesaplanıyor...' : 'KM Kontrol'}
                  </button>
                  <button type="button" onClick={() => void handleSyncPointsText()} disabled={syncingPoints} className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60">
                    <RefreshCw className={`h-4 w-4 ${syncingPoints ? 'animate-spin' : ''}`} />
                    {syncingPoints ? 'Güncelleniyor...' : 'Noktaları Güncelle'}
                  </button>
                </>
              ) : null}
              <button type="button" onClick={() => void copyTableToClipboard()} className="inline-flex items-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-950/60">
                <ClipboardCopy className="h-4 w-4" />
                Tablo Kopyala
              </button>
              <button type="button" onClick={() => void copyTableAsImage()} className="inline-flex items-center gap-2 rounded-xl border border-pink-300 bg-pink-50 px-3 py-2 text-sm font-semibold text-pink-700 hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950/40 dark:text-pink-200 dark:hover:bg-pink-950/60">
                <Image className="h-4 w-4" />
                Resim Kopyala
              </button>
              {!isStatusListScope && !hideCreateActions ? (
                <>
                  <button type="button" onClick={() => { setEditingWizardShipment(null); setWizardOpen(true); }} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-white shadow-sm hover:bg-emerald-600">
                    Sevk Siparişi Aç
                  </button>
                  <button type="button" onClick={() => setBulkImportOpen(true)} title="Açılan ekranda örnek sipariş dosyasını indirebilirsiniz" className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-violet-700">
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel'den Toplu Sipariş
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(260px,420px)_auto] xl:items-center xl:justify-between">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Genel arama..." className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-orange-400 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setShowFilterPanel((current) => !current)} className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${showFilterPanel ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]'}`}>
                <Filter className="h-4 w-4" />
                Filtrele
              </button>

              <button type="button" onClick={() => setShowColumnPanel((current) => !current)} className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${showColumnPanel ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]'}`}>
                {showColumnPanel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                Sütunlar
              </button>

              <select value={groupBy} onChange={(event) => {
                const newGroupBy = event.target.value as GroupByKey;
                setGroupBy(newGroupBy);
                setPage(1);
                if (activeTabId !== MAIN_TAB_ID && activeTabId !== FLOW_TAB_ID && activeSavedTab && canManageActiveTab) {
                  const nextTabs = (activeSavedTab.visibility === 'public' ? publicTabs : privateTabs).map((tab) =>
                    tab.id === activeTabId ? { ...tab, groupBy: sanitizeGroupBy(newGroupBy) } : tab
                  );
                  if (activeSavedTab.visibility === 'public') {
                    setPublicTabs(nextTabs);
                    persistPublicTabs(nextTabs).catch(() => {});
                  } else {
                    setPrivateTabs(nextTabs);
                    persistPrivateTabs(nextTabs).catch(() => {});
                  }
                }
              }} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                <option value="none">Gruplandırma Yok</option>
                {GROUPABLE_COLUMNS.map((column) => (
                  <option key={column.key} value={column.key}>{column.label}</option>
                ))}
              </select>

              <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                <option value={10}>10 kayıt</option>
                <option value={25}>25 kayıt</option>
                <option value={50}>50 kayıt</option>
                <option value={100}>100 kayıt</option>
                <option value={250}>250 kayıt</option>
              </select>
            </div>
          </div>

          {showFilterPanel && (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  <Filter className="h-4 w-4" />
                  Filtre ve Gruplandırma
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input value={newTabName} onChange={(event) => setNewTabName(event.target.value)} placeholder="Yeni tab adı" className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-orange-400 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
                  <select value={newTabVisibility} onChange={(event) => setNewTabVisibility(event.target.value as 'private' | 'public')} className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                  </select>
                  <button type="button" onClick={saveCurrentFiltersAsTab} disabled={savingTabs} className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                    <Plus className="h-4 w-4" />
                    Hızlı Arama Kaydet
                  </button>
                  <button type="button" onClick={clearFiltersAndGroup} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
                    <X className="h-4 w-4" />
                    Temizle
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {filters.map((rule, index) => (
                  <div key={rule.id} className="grid gap-2 lg:grid-cols-[90px_220px_180px_minmax(220px,1fr)_48px] lg:items-center">
                    <select value={index === 0 ? 'and' : rule.join} onChange={(event) => updateFilterRule(rule.id, { join: event.target.value as FilterJoin })} disabled={index === 0} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                      <option value="and">Ve</option>
                      <option value="or">Veya</option>
                    </select>

                    <select value={rule.column} onChange={(event) => updateFilterRule(rule.id, { column: event.target.value as ColumnKey })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                      {FILTERABLE_COLUMNS.map((column) => (
                        <option key={column.key} value={column.key}>{column.label}</option>
                      ))}
                    </select>

                    <select value={rule.operator} onChange={(event) => updateFilterRule(rule.id, { operator: event.target.value as FilterOperator })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
                      <option value="contains">İçinde</option>
                      <option value="notContains">İçermiyor</option>
                      <option value="equals">Eşit</option>
                       <option value="notEquals">Eşit Değil</option>
                       {rule.column === 'surucuSonKonum' && <option value="minutesOver">Dakikadan fazla</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateEquals">Tarih =</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateOnOrAfter">Tarih &gt;=</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateOnOrBefore">Tarih &lt;=</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateToday">Bugün</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateThisWeek">Bu Hafta</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateThisMonth">Bu Ay</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateLastMonth">Geçen Ay</option>}
                      {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateRange">Özel Tarih Aralığı</option>}
                      <option value="empty">Boş</option>
                      <option value="notEmpty">Dolu</option>
                    </select>

                    {rule.column === 'surucuSonKonum' && rule.operator === 'minutesOver' ? (
                      <input
                        type="number"
                        min="0"
                        value={rule.value}
                        onChange={(event) => updateFilterRule(rule.id, { value: event.target.value })}
                        placeholder="Örn. 30"
                        className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                      />
                    ) : DATE_FILTER_COLUMNS.has(rule.column) && rule.operator === 'dateRange' ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="date"
                          value={String(rule.value).split(',')[0] || ''}
                          onChange={(event) => updateFilterRule(rule.id, { value: `${event.target.value},${String(rule.value).split(',')[1] || ''}` })}
                          placeholder="Başlangıç"
                          className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                        />
                        <span className="shrink-0 text-xs text-slate-400">→</span>
                        <input
                          type="date"
                          value={String(rule.value).split(',')[1] || ''}
                          onChange={(event) => updateFilterRule(rule.id, { value: `${String(rule.value).split(',')[0] || ''},${event.target.value}` })}
                          placeholder="Bitiş"
                          className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                        />
                      </div>
                    ) : CITY_FILTER_COLUMNS.has(rule.column) ? (
                      <CityMultiSelect
                        value={rule.value}
                        options={cityFilterOptions}
                        onChange={(value) => updateFilterRule(rule.id, { value })}
                        disabled={rule.operator === 'empty' || rule.operator === 'notEmpty'}
                      />
                    ) : (
                      <input value={rule.value} onChange={(event) => updateFilterRule(rule.id, { value: event.target.value })} type={DATE_FILTER_COLUMNS.has(rule.column) && (rule.operator === 'dateEquals' || rule.operator === 'dateOnOrAfter' || rule.operator === 'dateOnOrBefore') ? 'date' : 'text'} disabled={rule.operator === 'empty' || rule.operator === 'notEmpty' || rule.operator === 'dateToday' || rule.operator === 'dateThisWeek' || rule.operator === 'dateThisMonth' || rule.operator === 'dateLastMonth'} placeholder="Filtre değeri" className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
                    )}

                    <button type="button" onClick={() => removeFilterRule(rule.id)} className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addFilterRule} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
                <Plus className="h-4 w-4" />
                Yeni Filtre Ekle
              </button>
            </div>
          )}

          {showColumnPanel && (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
              <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Sütun görünürlüğü, sırası ve genişliği</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {[...visibleColumns.map((key) => getColumn(key)).filter(Boolean), ...ALL_COLUMNS.filter((column) => !visibleColumns.includes(column.key))].map((column) => {
                  const col = column as ColumnDef;
                  const visible = visibleColumns.includes(col.key);
                  const index = visibleColumns.indexOf(col.key);
                  const effectiveWidth = getEffectiveWidth(col.key);

                  return (
                    <div key={col.key} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${visible ? 'border-orange-200 bg-white text-slate-800 dark:border-orange-900/70 dark:bg-[#111111] dark:text-slate-100' : 'border-slate-200 bg-white/70 text-slate-500 dark:border-slate-800 dark:bg-[#101010] dark:text-slate-500'}`}>
                      <button type="button" onClick={() => toggleColumnVisibility(col.key)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                        {visible ? <Eye className="h-4 w-4 shrink-0" /> : <EyeOff className="h-4 w-4 shrink-0" />}
                        <span className="truncate">{col.label}</span>
                      </button>

                      {visible && (
                        <div className="flex shrink-0 items-center gap-1">
                          <input
                            type="number"
                            min={30}
                            max={600}
                            value={effectiveWidth ?? ''}
                            onChange={(event) => {
                              const val = parseInt(event.target.value, 10);
                              updateColumnWidth(col.key, Number.isNaN(val) ? 0 : val);
                            }}
                            className="h-7 w-[62px] rounded-lg border border-slate-300 bg-white px-1.5 text-center text-[11px] text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                            title="Sütun genişliği (px)"
                            placeholder="px"
                          />
                          <button type="button" onClick={() => moveColumn(col.key, 'left')} disabled={index <= 0} className="rounded-lg border border-slate-300 p-1.5 text-slate-600 disabled:opacity-30 dark:border-slate-700 dark:text-slate-300" title="Sola taşı">
                            <ArrowLeft className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" onClick={() => moveColumn(col.key, 'right')} disabled={index === -1 || index >= visibleColumns.length - 1} className="rounded-lg border border-slate-300 p-1.5 text-slate-600 disabled:opacity-30 dark:border-slate-700 dark:text-slate-300" title="Sağa taşı">
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {!isStatusListScope && activeTabId === FLOW_TAB_ID && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
            <div className="mb-3 text-sm font-bold text-slate-800 dark:text-slate-100">Akış Yönetimi</div>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const flowTabColors = [
                  { border: '#f97316', bg: '#fff7ed' },
                  { border: '#3b82f6', bg: '#eff6ff' },
                  { border: '#10b981', bg: '#ecfdf5' },
                  { border: '#8b5cf6', bg: '#f5f3ff' },
                  { border: '#e11d48', bg: '#fff1f2' },
                  { border: '#06b6d4', bg: '#ecfeff' },
                  { border: '#f59e0b', bg: '#fffbeb' },
                  { border: '#ec4899', bg: '#fdf2f8' },
                  { border: '#14b8a6', bg: '#f0fdfa' },
                  { border: '#6366f1', bg: '#eef2ff' },
                  { border: '#84cc16', bg: '#f7fee7' },
                  { border: '#a855f7', bg: '#faf5ff' },
                  { border: '#0ea5e9', bg: '#f0f9ff' },
                  { border: '#ef4444', bg: '#fef2f2' },
                ];
                const allFlowTabs = [
                  { key: 'all', label: 'Tümü', sublabel: '', count: flowStats.all ?? 0 },
                  ...effectiveFlowStatusOptions.map((o, i) => ({ key: o.value, label: `Adım ${i+1}`, sublabel: o.label, count: flowStats[o.value] ?? 0 })),
                  ...(hideArchiveTab ? [] : [{ key: 'arsiv' as const, label: 'Arşiv', sublabel: 'Fiziksel Arşiv', count: archiveRows.length }]),
                ];
                return allFlowTabs.map((tab, idx) => {
                  const c = flowTabColors[idx % flowTabColors.length];
                  const isActive = activeFlowTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => { setActiveFlowTab(tab.key); setPage(1); if (tab.key === 'arsiv') void loadArchiveRows(); }}
                      style={{
                        borderColor: c.border + (isActive ? '' : '60'),
                        backgroundColor: isActive ? c.bg : c.bg + '80',
                        color: '#000000',
                      }}
                      className={`min-w-[140px] flex-1 rounded-xl border px-3 py-2.5 text-left transition ${
                        isActive
                          ? 'ring-2 ring-inset ring-orange-400 font-semibold'
                          : 'hover:opacity-80'
                      }`}
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#000000' }}>
                        {tab.label}
                      </div>
                      {tab.sublabel && <div className="mt-1 text-sm leading-tight">{tab.sublabel}</div>}
                      <div className="mt-2 text-xs opacity-80">{tab.count} kayıt</div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 gap-4 ${showDetailPanel && detailRow && !disableDetailPanel ? 'xl:grid-cols-[minmax(0,1fr)_420px]' : ''}`}>
          <div className="min-w-0 space-y-3">
        {selectedRowKeys.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-800 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
            <span>{selectedRowKeys.length} satır seçildi</span>
            <div className="flex items-center gap-2">
              {canManageAssignments ? (
                <button type="button" onClick={() => void handleBulkMoveToOfferCollection()} className="rounded-lg border border-violet-300 bg-white px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100 dark:border-violet-700 dark:bg-[#171717] dark:text-violet-200 dark:hover:bg-[#202020]">
                  Teklif Topla
                </button>
              ) : null}
              {canBulkUpdateAccountingStatus ? (
                <button type="button" onClick={() => setBulkAccountingModalOpen(true)} className="rounded-lg border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-[#171717] dark:text-amber-200 dark:hover:bg-[#202020]">
                  Toplu Durum Güncelle
                </button>
              ) : null}
              {externalRows && (activeFlowTab === 'invoice-waiting' || activeFlowTab === 'invoice-approval-pending') ? (
                <button type="button" onClick={() => void handleBulkInvoiceUpload()} disabled={uploadingShipmentDocumentKey === '__bulk_invoice__'} className="rounded-lg border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-700 dark:bg-[#171717] dark:text-amber-200 dark:hover:bg-[#202020]">
                  {uploadingShipmentDocumentKey === '__bulk_invoice__' ? 'Faturalar Yukleniyor...' : 'Toplu Fatura Yukle'}
                </button>
              ) : null}
              {externalRows && activeFlowTab === 'invoice-approval-pending' && onBulkRequestESign ? (
                <button type="button" onClick={() => void onBulkRequestESign(selectedRows, 'controller')} className="rounded-lg border border-indigo-300 bg-white px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-[#171717] dark:text-indigo-200 dark:hover:bg-[#202020]">
                  Toplu E-Imza (Kontrol)
                </button>
              ) : null}
              {externalRows && (activeFlowTab === 'invoice-approval-pending' || activeFlowTab === 'invoice-check-approved') && onBulkRefreshPreApprovalPdf ? (
                <button type="button" onClick={() => void onBulkRefreshPreApprovalPdf(selectedRows)} className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]">
                  Toplu Ön Onay PDF Güncelle
                </button>
              ) : null}
              {externalRows && (activeFlowTab === 'invoice-approval-pending' || activeFlowTab === 'invoice-check-approved') && onBulkResetESign ? (
                <button type="button" onClick={() => void onBulkResetESign(selectedRows)} className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-700 dark:bg-[#171717] dark:text-rose-200 dark:hover:bg-[#202020]">
                  E-Imza Reddet
                </button>
              ) : null}
              {externalRows && activeFlowTab === 'invoice-check-approved' && onBulkRequestESign ? (
                <button type="button" onClick={() => void onBulkRequestESign(selectedRows, 'approver')} className="rounded-lg border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-[#171717] dark:text-emerald-200 dark:hover:bg-[#202020]">
                  Toplu E-Imza (Son Onay)
                </button>
              ) : null}
              {externalRows && activeFlowTab === 'payment-pending' && onBulkSendSignedPdfEmail ? (
                <button type="button" onClick={() => void onBulkSendSignedPdfEmail(selectedRows)} className="rounded-lg border border-cyan-300 bg-white px-3 py-1 text-xs font-semibold text-cyan-700 hover:bg-cyan-100 dark:border-cyan-700 dark:bg-[#171717] dark:text-cyan-200 dark:hover:bg-[#202020]">
                  Toplu E-Posta Gonder
                </button>
              ) : null}
              {canBulkUpdateProcessStatus ? (
                <button type="button" onClick={() => setBulkProcessStatusModalOpen(true)} className="rounded-lg border border-sky-300 bg-white px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:border-sky-700 dark:bg-[#171717] dark:text-sky-200 dark:hover:bg-[#202020]">
                  Toplu Durum Güncelle
                </button>
              ) : null}
              {(isFleetStatusList || settingsKeyScope === 'flexible_tracking') && canManageAssignments ? (
                <button type="button" onClick={() => setLocationReminderModalOpen(true)} className="inline-flex items-center gap-1 rounded-lg border border-orange-300 bg-white px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:bg-[#171717] dark:text-orange-200 dark:hover:bg-[#202020]">
                  <BellRing className="h-3.5 w-3.5" /> Konum Hatırlat
                </button>
              ) : null}
              {canManageAssignments ? (
                <button type="button" onClick={() => setBulkServiceTypeModalOpen(true)} className="rounded-lg border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-[#171717] dark:text-emerald-200">
                  Toplu Hizmet Türü Güncelle
                </button>
              ) : null}
              {demurrageLocked && selectedRows.length > 0 && (user as { canManualDemurrageEdit?: boolean })?.canManualDemurrageEdit && (
                <>
                  <button type="button" onClick={() => void handleBulkDemurrageManualEdit(true)} disabled={Boolean(bulkDemurrageToggling)} className="rounded-lg border border-teal-300 bg-white px-3 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60 dark:border-teal-700 dark:bg-[#171717] dark:text-teal-200 dark:hover:bg-[#202020]">
                    {bulkDemurrageToggling === 'enable' ? 'Açılıyor...' : 'Manuel Bekleme Bedeli Girişi Aç'}
                  </button>
                  <button type="button" onClick={() => void handleBulkDemurrageManualEdit(false)} disabled={Boolean(bulkDemurrageToggling)} className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-700 dark:bg-[#171717] dark:text-rose-200 dark:hover:bg-[#202020]">
                    {bulkDemurrageToggling === 'disable' ? 'Kapatılıyor...' : 'Manuel Bekleme Bedeli Girişi Kapat'}
                  </button>
                </>
              )}
                <button type="button" onClick={() => void copySelectedRows()} className="rounded-lg border border-orange-300 bg-white px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:bg-[#171717] dark:text-orange-200 dark:hover:bg-[#202020]">
                  Kopyala
                </button>
              {canBulkDelete ? (
                <button type="button" onClick={() => void handleBulkDelete()} disabled={bulkDeleting} className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-700 dark:bg-[#171717] dark:text-rose-200 dark:hover:bg-[#202020]">
                  {bulkDeleting ? 'Siliniyor...' : 'Toplu Sil'}
                </button>
              ) : null}
            </div>
          </div>
        )}

        {selectedRow && String(selectedRow.sevkKodu || '').trim() && (
          <div className="mb-0 flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
            <span>Seçili sipariş: #{selectedRow.sevkKodu}</span>
            <span className="text-xs font-semibold text-sky-700 dark:text-sky-200">Detay için çift tıklayın</span>
          </div>
        )}

        {selectedRow && !showDetailPanel ? (
          <div className="sticky top-4 z-20 rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-[#151515]">
            <div className="mb-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">İşlemler</div>
            {renderSelectedRowActions(selectedRow)}
          </div>
        ) : null}

        <div className="general-list-strong-borders rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#111111]">
           <div ref={tableContainerRef} style={constrainTableToViewport ? { height: 'clamp(450px, calc(100vh - 260px), 900px)', maxHeight: 'calc(100vh - 260px)' } : undefined} className={`overflow-auto ${forceBlackTableText ? '[&_td]:!text-black [&_td_*]:!text-black [&_td_select]:!text-black [&_td_span]:!text-black' : ''}`}>
            <table className="min-w-full border-collapse text-[13px]">
               <thead className="sticky top-0 z-30 bg-slate-100 text-slate-700 dark:bg-[#181818] dark:text-slate-200">
                <tr>
                   <th className="sticky left-0 top-0 z-40 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-2 py-1.5 text-left font-bold dark:border-slate-800 dark:bg-[#181818]">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectVisibleRows} />
                  </th>
                   <th className="sticky top-0 z-30 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-2 py-1.5 text-left font-bold dark:border-slate-800 dark:bg-[#181818]">#</th>
                  {activeColumns.map((column) => (
                     <th key={column.key} style={{ minWidth: getEffectiveWidth(column.key) !== undefined ? `${getEffectiveWidth(column.key)}px` : undefined, width: getEffectiveWidth(column.key) !== undefined ? `${getEffectiveWidth(column.key)}px` : undefined, position: 'sticky', top: 0 }} className={`z-30 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-2 py-1.5 font-bold dark:border-slate-800 dark:bg-[#181818] ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}`}>
                      <div
                        draggable
                        onDragStart={(event) => {
                          dragColumnRef.current = column.key;
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', column.key);
                        }}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          const draggedKey = (event.dataTransfer.getData('text/plain') as ColumnKey) || dragColumnRef.current;
                          if (!draggedKey || draggedKey === column.key) return;
                          setVisibleColumns((current) => {
                            const fromIndex = current.indexOf(draggedKey as ColumnKey);
                            const toIndex = current.indexOf(column.key);
                            if (fromIndex < 0 || toIndex < 0) return current;
                            const next = [...current];
                            const [moved] = next.splice(fromIndex, 1);
                            next.splice(toIndex, 0, moved);
                            return next;
                          });
                          dragColumnRef.current = null;
                        }}
                        onDragEnd={() => { dragColumnRef.current = null; }}
                        className={`flex cursor-move items-center gap-1.5 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'}`}
                        title="Sürükleyip bırakarak kolon yerini değiştirin"
                      >
                        <button type="button" onClick={() => toggleSort(column.key)} className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-left hover:bg-slate-200 dark:hover:bg-[#242424]" title="Sırala">
                          <span>{column.label}</span>
                          {renderSortIcon(column.key)}
                        </button>
                        {column.description ? <span title={column.description} aria-label={column.description} className="inline-flex cursor-help text-sky-600 dark:text-sky-300"><Info className="h-3.5 w-3.5" /></span> : null}
                      </div>
                      <div
                        onMouseDown={(event) => handleResizeStart(column.key, event)}
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-orange-400 active:bg-orange-500"
                        title="Sütun genişliğini değiştir"
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {effectiveLoading ? (
                  <tr>
                    <td colSpan={activeColumns.length + 2} className="px-4 py-10 text-center text-sm font-medium text-slate-500 dark:text-slate-400">Veriler yükleniyor...</td>
                  </tr>
                ) : paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={activeColumns.length + 2} className="px-4 py-10 text-center text-sm font-medium text-slate-500 dark:text-slate-400">Kayıt bulunamadı.</td>
                  </tr>
                ) : (
                  paginatedItems.map((item: any, itemIndex) => {
                    if (item.__group) {
                      const group = item.group as { label: string; rows: OperationRow[] };
                      const groupColumn = groupBy !== 'none' ? getColumn(groupBy) : null;
                      const isCollapsed = collapsedGroups[group.key] ?? true;
                      return (
                        <tr key={`group-${group.label}-${itemIndex}`} className="cursor-pointer bg-slate-100 dark:bg-[#1c1c1c]" onClick={() => toggleGroupCollapse(group.key)}>
                          <td colSpan={activeColumns.length + 2} className="px-3 py-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white text-[11px] dark:border-slate-700 dark:bg-[#171717]">{isCollapsed ? '+' : '-'}</span>
                              <Layers className="h-4 w-4" />
                              {groupColumn?.label}: {group.label} ({group.rows.length})
                            </span>
                          </td>
                        </tr>
                      );
                    }

                    if (item.__invoiceGroup) {
                      const group = item.group as { key: string; label: string; rows: OperationRow[]; invoiceGroupKey: string };
                      const isCollapsed = collapsedGroups[group.key] ?? false;
                      const groupColor = getInvoiceGroupRowBackground(group.invoiceGroupKey) || '#ecfeff';
                      return (
                        <tr key={`invoice-group-${group.invoiceGroupKey}-${itemIndex}`} className="cursor-pointer" onClick={() => toggleGroupCollapse(group.key)}>
                          <td colSpan={activeColumns.length + 2} className="px-3 py-2 text-sm font-bold text-slate-800 dark:text-slate-100" style={{ backgroundColor: groupColor, borderLeft: `4px solid ${INVOICE_GROUP_BORDER_COLOR}`, borderRight: `4px solid ${INVOICE_GROUP_BORDER_COLOR}`, borderTop: `2px solid ${INVOICE_GROUP_BORDER_COLOR}` }}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-2">
                                <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white text-[11px] dark:border-slate-700 dark:bg-[#171717]">{isCollapsed ? '+' : '-'}</span>
                                <Layers className="h-4 w-4" />
                                {group.label}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleUndoInvoiceGroup(group.invoiceGroupKey);
                                }}
                                className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:bg-[#171717] dark:text-rose-200"
                              >
                                Fatura Birleştirmeyi Geri Al
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const row = item as OperationRow;
                    const selected = selectedRowKey === row.rowKey;
                    const checked = selectedRowKeys.includes(row.rowKey);
                    const absoluteIndex = (page - 1) * pageSize + itemIndex + 1;
                    const invoiceGroupMeta = (item as any).__invoiceGroupMeta as { key: string; index: number; count: number; color: string } | undefined;
                    const invoiceGroupRowClassName = invoiceGroupMeta
                      ? `[&>td]:border-x-4 [&>td]:border-x-[#7f1d1d] ${invoiceGroupMeta.index === 0 ? '[&>td]:border-t-2 [&>td]:border-t-[#7f1d1d]' : ''} ${invoiceGroupMeta.index === invoiceGroupMeta.count - 1 ? '[&>td]:border-b-2 [&>td]:border-b-[#7f1d1d]' : ''}`
                      : '';

                    const isDisNakliye = row.disNakliye === 'Evet';

                    return (
                      <tr key={row.rowKey} onClick={() => { setSelectedRowKey(row.rowKey); onRowSelect?.(row); }} onDoubleClick={() => { setSelectedRowKey(row.rowKey); onRowSelect?.(row); if (!disableDetailPanel) setShowDetailPanel(true); }} style={!selected ? {
                        ['--row-bg' as any]: getFlowStatusRowBackground(String(row.islemDurum || '')) || 'transparent',
                      } : undefined} className={`min-h-[2rem] align-middle cursor-pointer border-b border-slate-100 dark:border-slate-800 ${isDisNakliye ? 'border-l-4 border-l-amber-400' : ''} ${selected ? 'bg-orange-50 ring-2 ring-inset ring-orange-400 dark:bg-orange-950/30' : '[&>td]:!bg-[var(--row-bg)] hover:brightness-[0.98] dark:hover:brightness-110'} ${invoiceGroupRowClassName}`}>
                        <td className="sticky left-0 z-10 whitespace-nowrap border-r border-slate-200 bg-white px-2 py-1 dark:border-slate-800 dark:bg-[#111111]">
                          <input type="checkbox" checked={checked} onChange={(event) => { event.stopPropagation(); toggleRowSelection(row.rowKey); }} onClick={(event) => event.stopPropagation()} />
                        </td>
                        <td className={`whitespace-nowrap px-2 py-1 text-[12px] font-semibold text-slate-500 dark:text-slate-400 ${invoiceGroupMeta ? 'pl-4' : ''}`}>
                          {invoiceGroupMeta ? <span className="mr-1 inline-block text-[#7f1d1d]">{invoiceGroupMeta.index === invoiceGroupMeta.count - 1 ? '└' : '├'}</span> : null}
                          {absoluteIndex}
                        </td>
                        {activeColumns.map((column) => {
                          const value = renderCell(row, column);

                          const documentBucket = getDocumentBucketForColumn(column.key);

                          if (documentBucket) {
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[210px] whitespace-nowrap px-2 py-1" title="Evraklar">
                                {renderDocumentThumbs(row, documentBucket)}
                              </td>
                            );
                          }

                          if (column.key === 'cekici') {
                            const plate = String(row.cekici || '').trim();
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[320px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100">
                                {plate || '-'}
                              </td>
                            );
                          }

                          if (column.key === 'dorse') {
                            const plate = String(row.dorse || '').trim();
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[320px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100">
                                {plate || '-'}
                              </td>
                            );
                          }

                          if (column.key === 'disNakliye') {
                            const isEvet = row.disNakliye === 'Evet';
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[160px] whitespace-nowrap px-2 py-1">
                                {isEvet ? (
                                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[12px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                    <Truck className="h-3 w-3" /> Evet
                                  </span>
                                ) : (
                                  <span className="text-[12px] text-slate-500">Hayır</span>
                                )}
                              </td>
                            );
                          }

                          if (column.key === 'sevkKodu') {
                            const displayValue = String(value || '-');
                            const accountingLocked = isAccountingLockedForRow(row);
                            const hasArchive = !!(
                              (row.archiveCode && row.archiveCode !== '-')
                              || archiveRecordByShipmentId[String(row.id)]
                            );
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, displayValue)} className="max-w-[220px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100" title={displayValue}>
                                <span className="inline-flex max-w-full items-center gap-1 truncate">
                                  <span className="truncate">{displayValue}</span>
                                  {accountingLocked ? <Lock className="h-3 w-3 shrink-0 text-rose-600" title="Muhasebeye aktarildi, detaylar kilitli" /> : null}
                                  {hasArchive ? <Archive className="h-3 w-3 shrink-0 text-amber-500" title="Arsivlendi" /> : null}
                                </span>
                              </td>
                            );
                          }

                          if (column.key === 'folderNumber' || column.key === 'evrakSiraNo') {
                            const displayValue = String(value || '-');
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, displayValue)} className="max-w-[180px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100" title={displayValue}>
                                <span className="truncate">{displayValue}</span>
                              </td>
                            );
                          }

                          if (column.key === 'paymentControllerInfo') {
                            const name = String(row.paymentControllerInfo || '').trim();
                            const pdfPath = String(row.paymentControllerPdfPath || '').trim();
                            const approvedAt = String(row.paymentControllerAt || '').trim();
                            const signedText = approvedAt ? `${name || 'Kontrol eden'} imzalıdır` : '';
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[220px] px-2 py-1 text-[12px] text-slate-800 dark:text-slate-100">
                                <div className="leading-tight">
                                  <div className="truncate font-medium">{name || '-'}</div>
                                  <div className="truncate text-[10px] text-slate-500">{approvedAt ? formatDateTime(approvedAt) : '-'}</div>
                                  {signedText ? <div className="truncate text-[10px] text-emerald-600 dark:text-emerald-300">{signedText}</div> : null}
                                  {pdfPath ? <a href={resolveFileUrl(pdfPath)} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-blue-600 hover:underline dark:text-blue-300">PDF</a> : null}
                                </div>
                              </td>
                            );
                          }

                          if (column.key === 'preApprovalPdfPath') {
                            const pdfPath = String(row.preApprovalPdfPath || '').trim();
                            const pdfPageCount = Number((row as any).preApprovalPdfPageCount || 0);
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[180px] px-2 py-1 text-[12px] text-slate-800 dark:text-slate-100">
                                {pdfPath ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setDocumentSlider({
                                        docs: [{
                                          fileName: `on-onay-${row.sevkKodu || row.id || 'belge'}.pdf`,
                                          url: resolveFileUrl(pdfPath),
                                          isImage: false,
                                        }],
                                        currentIndex: 0,
                                      });
                                    }}
                                    className="text-[11px] font-semibold text-blue-600 hover:underline dark:text-blue-300"
                                  >
                                    {`Önizle${pdfPageCount > 0 ? `(${pdfPageCount})` : ''}`}
                                  </button>
                                ) : <span className="text-slate-400">-</span>}
                              </td>
                            );
                          }

                          if (column.key === 'paymentApproverInfo') {
                            const name = String(row.paymentApproverInfo || '').trim();
                            const pdfPath = String(row.paymentApproverPdfPath || '').trim();
                            const approvedAt = String(row.paymentApprovedAt || '').trim();
                            const signedText = approvedAt ? `${name || 'Onaylayan'} imzalıdır` : '';
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[220px] px-2 py-1 text-[12px] text-slate-800 dark:text-slate-100">
                                <div className="leading-tight">
                                  <div className="truncate font-medium">{name || '-'}</div>
                                  <div className="truncate text-[10px] text-slate-500">{approvedAt ? formatDateTime(approvedAt) : '-'}</div>
                                  {signedText ? <div className="truncate text-[10px] text-emerald-600 dark:text-emerald-300">{signedText}</div> : null}
                                  {pdfPath ? (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        window.open(resolveFileUrl(pdfPath), '_blank', 'noopener,noreferrer');
                                      }}
                                      className="mt-1 inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                                    >
                                      PDF Gör
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            );
                          }

                          if (column.key === 'eSignAction') {
                            const signStatus = String(row.eSignStatus || '').trim();
                            const canRequest = typeof row.__requestESign === 'function';
                            const statusLabel = signStatus && signStatus !== '-' ? signStatus : 'hazir';
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="max-w-[180px] px-2 py-1 text-[12px] text-slate-800 dark:text-slate-100">
                                <div className="flex items-center gap-2">
                                  {canRequest ? (
                                    <button type="button" onClick={(event) => { event.stopPropagation(); row.__requestESign?.(); }} className="inline-flex w-fit items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">
                                      E-İmzala
                                    </button>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                  <span className="truncate text-[10px] text-slate-500">{statusLabel || '-'}</span>
                                </div>
                              </td>
                            );
                          }

                          if (column.key === 'durum') {
                            const displayValue = getDurumText(String(row.durum));
                            const currentValue = SUPPLY_STATUS_OPTIONS.some((option) => option.value === String(row.durum))
                              ? String(row.durum)
                              : 'tedarik-bekleniyor';
                            if (canManageAssignments) {
                              return (
                                <td key={`${row.rowKey}-${column.key}`} className="max-w-[260px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-semibold">
                                  <select
                                    value={currentValue}
                                    disabled={updatingRowSupplyStatusId === row.id}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => void handleSupplyStatusChange(row, event.target.value as (typeof SUPPLY_STATUS_OPTIONS)[number]['value'])}
                                    className={`w-full rounded-md border px-2 py-1 text-[12px] font-semibold outline-none ${getDurumClass(currentValue)} ${updatingRowSupplyStatusId === row.id ? 'opacity-60' : ''}`}
                                  >
                                    {SUPPLY_STATUS_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </td>
                              );
                            }
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, displayValue)} className={`${getDurumClass(String(row.durum))} max-w-[260px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-bold`} title={displayValue}>
                                <span className="block truncate">{displayValue}</span>
                              </td>
                            );
                          }

                          if (column.key === 'islemDurum') {
                            if (canManageAssignments) {
                              const rawCurrentValue = getEffectiveFlowStatus(row);
                              const currentValue = MANUAL_PROCESS_STATUS_OPTIONS.some((option) => option.value === rawCurrentValue)
                                ? rawCurrentValue
                                : 'tedarik-bekleniyor';
                              return (
                                <td key={`${row.rowKey}-${column.key}`} className="max-w-[260px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-semibold">
                                  <select
                                    value={currentValue}
                                    disabled={updatingRowStatusId === row.id}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => void handleManualProcessStatusChange(row, event.target.value as (typeof MANUAL_PROCESS_STATUS_OPTIONS)[number]['value'])}
                                    className={`w-full rounded-md border px-2 py-1 text-[12px] font-semibold outline-none ${getFlowStatusClass(currentValue)} ${updatingRowStatusId === row.id ? 'opacity-60' : ''}`}
                                  >
                                    {MANUAL_PROCESS_STATUS_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </td>
                              );
                            }
                            const displayValue = getFlowStatusLabel(String(getEffectiveFlowStatus(row)));
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, displayValue)} className="max-w-[260px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-semibold" title={displayValue}>
                                <span className={`block truncate rounded-md px-2 py-0.5 ring-1 ${getFlowStatusClass(String(getEffectiveFlowStatus(row)))}`}>{displayValue}</span>
                              </td>
                            );
                          }

                          if (column.key === 'surucuAdi') {
                            const temporaryInfo = row.hasTemporaryAssignment
                              ? `Gecici gorevlendirme aktif${row.temporaryDriverName ? `: ${row.temporaryDriverName}` : ''}${row.permanentDriverName ? ` | Asil: ${row.permanentDriverName}` : ''}${row.temporaryAssignmentStartDate ? ` | Baslangic: ${row.temporaryAssignmentStartDate}` : ''}${row.temporaryAssignmentEndDate ? ` | Bitis: ${row.temporaryAssignmentEndDate}` : ''}${row.temporaryAssignmentNotes ? ` | Not: ${row.temporaryAssignmentNotes}` : ''}`
                              : '';
                            const canImpersonateStatusDriver = isStatusListScope && Array.from(new Set([user?.role, ...((user as any)?.roles || [])].filter(Boolean))).includes('admin') && row.driverUserId && String(row.driverUserId) !== String(user?.id || '');
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, String(value))} title={String(value)} className={`max-w-[320px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100 ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}`}>
                                <span className="inline-flex max-w-full items-center gap-1.5">
                                  <span className="truncate">{String(value)}</span>
                                  {row.hasTemporaryAssignment ? <Info className="h-3.5 w-3.5 shrink-0 text-sky-500" title={temporaryInfo} /> : null}
                                  {canImpersonateStatusDriver ? <button type="button" onClick={async (event) => {
                                    event.stopPropagation();
                                    if (!window.confirm(`${String(value)} hesabina gecilsin mi?`)) return;
                                    const response = await impersonateUser(String(row.driverUserId));
                                    if (!response.success) {
                                      toast.error(response.message || 'Kullanici oturumu acilamadi');
                                      return;
                                    }
                                    const effectiveRoles = Array.from(new Set([response.user?.role, ...(response.user?.roles || [])].filter(Boolean)));
                                    window.location.href = effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver') ? '/driver-mobile-home' : '/';
                                  }} className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40" title="Bu kullanici olarak giris yap"><LogIn className="h-3.5 w-3.5" /></button> : null}
                                </span>
                              </td>
                            );
                          }

                          if (column.key === 'accountingStatus') {
                            const displayValue = getAccountingStatusText(String(row.accountingStatus));
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, displayValue)} className={`${getAccountingStatusClass(String(row.accountingStatus))} max-w-[260px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-semibold`} title={displayValue}>
                                <span className="block truncate">{displayValue}</span>
                              </td>
                            );
                          }

                          if (REMAINING_TIME_COLUMN_KEYS.has(column.key)) {
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, String(value))} className="max-w-[220px] whitespace-nowrap px-2 py-1 text-center text-[13px] font-semibold" title={value}>
                                <span className={`block truncate rounded-md px-2 py-0.5 ring-1 ${getRemainingHoursBadgeClass(String(value))}`}>{value}</span>
                              </td>
                            );
                          }

                          if (column.key === 'beklemeBedeli' || column.key === 'hammaliye') {
                            const field = column.key;
                            const accountingLockedCell = isAccountingLockedForRow(row);
                            const demurrageLockedCell = field === 'beklemeBedeli' && isDemurrageLockedForRow(row);
                            const canEditCostCell = !demurrageLockedCell && !accountingLockedCell;
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="whitespace-nowrap px-2 py-1">
                                {!canEditCostCell ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-right text-[12px] font-semibold text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300"
                                    title={accountingLockedCell ? 'Muhasebeye aktarildigi icin duzenlenemez' : `Otomatik hesaplanan yatma bedeli${row.yatmaGunSayisi ? ` (${row.yatmaGunSayisi} gün)` : ''}`}
                                  >
                                    <Lock className="h-3 w-3" />
                                    <span>{formatMoney(resolveCostValue(row, field))}</span>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!canEditCostCell) return;
                                      setEditingCostCell({ rowKey: row.rowKey, field });
                                      setEditingCostValue(String(resolveCostValue(row, field)));
                                    }}
                                    className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-right text-[12px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200"
                                  >
                                    <span>{formatMoney(resolveCostValue(row, field))}</span>
                                    <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-300">Düzenle</span>
                                  </button>
                                )}
                              </td>
                            );
                          }

                          if (column.key === 'toplamBedel') {
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="whitespace-nowrap px-2 py-1 text-right text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                                {formatMoney(Number(row.toplamBedel || 0))}
                              </td>
                            );
                          }

                          if (column.key === 'operasyonNot') {
                            const isEditingNotes = editingNoteId === row.rowKey;
                            if (isEditingNotes) {
                              return (
                                <td key={`${row.rowKey}-${column.key}`} className="whitespace-nowrap px-2 py-1">
                                  <textarea
                                    autoFocus
                                    value={editingNoteValue}
                                    onChange={(e) => setEditingNoteValue(e.target.value)}
                                    onBlur={async () => {
                                      const trimmed = editingNoteValue.trim();
                                      try {
                                        const resp = await supplyService.update(row.id, { notlar: trimmed });
                                        if (resp?.success) {
                                          setRows((prev) => prev.map((r) => (r.rowKey === row.rowKey ? { ...r, operasyonNot: trimmed } : r)));
                                        } else {
                                          toast.error(resp?.message || 'Not güncellenemedi');
                                        }
                                      } catch {
                                        toast.error('Not güncellenirken hata oluştu');
                                      } finally {
                                        setEditingNoteId(null);
                                        setEditingNoteValue('');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) (e.target as HTMLTextAreaElement).blur();
                                      if (e.key === 'Escape') { setEditingNoteId(null); setEditingNoteValue(''); }
                                    }}
                                    className="w-full rounded border border-blue-400 px-2 py-1 text-xs outline-none resize-none"
                                    rows={2}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                              );
                            }
                            return (
                              <td
                                key={`${row.rowKey}-${column.key}`}
                                onContextMenu={(e) => openCellFilterMenu(e, column, row.operasyonNot === '-' ? '' : row.operasyonNot)}
                                onClick={(e) => { e.stopPropagation(); setEditingNoteId(row.rowKey); setEditingNoteValue(row.operasyonNot === '-' ? '' : row.operasyonNot); }}
                                className="max-w-[320px] cursor-pointer whitespace-nowrap px-2 py-1 text-left text-[13px] text-slate-800 hover:bg-blue-50 dark:text-slate-100 dark:hover:bg-blue-950/30"
                                title={row.operasyonNot}
                              >
                                <span className="block truncate">{row.operasyonNot}</span>
                              </td>
                            );
                          }

                          if (column.key === 'invoiceNumber') {
                            return (
                              <td
                                key={`${row.rowKey}-${column.key}`}
                                onContextMenu={(e) => openCellFilterMenu(e, column, row.invoiceNumber === '-' ? '' : row.invoiceNumber)}
                                className="max-w-[220px] whitespace-nowrap px-2 py-1 text-left text-[13px] text-slate-800 dark:text-slate-100"
                                title={row.invoiceNumber}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingInvoiceRowId(row.rowKey);
                                    setEditingInvoiceValue(row.invoiceNumber === '-' ? '' : row.invoiceNumber);
                                  }}
                                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200"
                                >
                                  <span className="block max-w-[120px] truncate">{row.invoiceNumber}</span>
                                  <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-300">Düzenle</span>
                                </button>
                              </td>
                            );
                          }

                          if (column.key === 'activeShipmentCount') {
                            const count = Number(row.activeShipmentCount || 0);
                            const hasMultiple = count > 1;
                            return (
                              <td key={`${row.rowKey}-${column.key}`} className="whitespace-nowrap px-2 py-1 text-center">
                                {count > 0 ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (hasMultiple) {
                                        void handleOpenActiveShipments(row);
                                      }
                                    }}
                                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                                      hasMultiple
                                        ? 'bg-orange-100 text-orange-800 hover:bg-orange-200 cursor-pointer ring-1 ring-orange-300 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-700'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                                    }`}
                                    title={hasMultiple ? `${count} aktif sevkiyat - tıklayınca listeler` : `${count} aktif sevkiyat`}
                                  >
                                    <span className="text-sm">{count}</span>
                                    {hasMultiple && <span className="text-[10px] ml-0.5">⊕</span>}
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
                                )}
                              </td>
                            );
                          }

                          if (column.render) {
                            return (
                              <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, String(value))} className="whitespace-nowrap px-2 py-1">
                                {column.render(row)}
                              </td>
                            );
                          }

                          return (
                            <td key={`${row.rowKey}-${column.key}`} onContextMenu={(event) => openCellFilterMenu(event, column, String(value))} title={value} className={`max-w-[320px] whitespace-nowrap px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100 ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}`}>
                              <span className="block truncate">{value}</span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-300 md:flex-row md:items-center md:justify-between">
            <div className="font-medium">
              Toplam <span className="font-bold text-slate-950 dark:text-white">{sortedRows.length}</span> kayıt
              {rows.length !== sortedRows.length && <span className="text-slate-500 dark:text-slate-400"> / {rows.length} toplam</span>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage(1)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-[#171717]">İlk</button>
              <button type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-[#171717]">Önceki</button>
              <span className="px-2 font-bold text-slate-950 dark:text-white">{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-[#171717]">Sonraki</button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-[#171717]">Son</button>
            </div>
          </div>
        </div>
        </div>

        {showDetailPanel && detailRow && !disableDetailPanel && (
          <aside className="rounded-xl border border-slate-700/30 bg-[#151515] shadow-sm overflow-hidden xl:sticky xl:top-4 h-fit">
            <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
              <div>
                <div className="text-[14px] font-semibold text-slate-100">{`Sipariş ${detailRow.sevkKodu}`}</div>
                <div className="mt-1 text-[11px] text-slate-400">Seçili siparişin detayları</div>
              </div>
              <button onClick={() => setShowDetailPanel(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-slate-200" title="Kapat">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="border-b border-slate-700/30 px-5 py-3 bg-[#1a1a1a]">
              <div className="flex flex-wrap items-center gap-2">
                {canManageAssignments ? (
                  <button type="button" onClick={() => void handleSendDriverMessage(detailRow)} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
                    <MessageCircle className="h-3.5 w-3.5" />
                    Sürücüye SMS
                  </button>
                ) : null}
                {canManageAssignments ? (
                  <button type="button" onClick={() => void handleAddShipmentNote(detailRow)} className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">
                    <FileText className="h-3.5 w-3.5" />
                    Sevkiyat Notu
                  </button>
                ) : null}
                {canManageAssignments ? (
                  <button type="button" onClick={() => void handleLocationCheck(detailRow)} disabled={locationCheckingRowId === detailRow.id} className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50">
                    <MapPinned className="h-3.5 w-3.5" />
                    {locationCheckingRowId === detailRow.id ? 'Kontrol...' : 'Konum Kontrol'}
                  </button>
                ) : null}
                <button type="button" onClick={() => void handleOpenRoute(detailRow)} disabled={routeLoadingRowId === detailRow.rowKey} className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-50">
                  <Navigation className="h-3.5 w-3.5" />
                  {routeLoadingRowId === detailRow.rowKey ? 'Rota Hesaplanıyor...' : 'Rota Aç'}
                </button>
                {canManageAssignments ? (
                  <button type="button" onClick={() => void handleSendRouteSms(detailRow)} disabled={routeLoadingRowId === detailRow.rowKey} className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50">
                    <Send className="h-3.5 w-3.5" />
                    {routeLoadingRowId === detailRow.rowKey ? 'Rota Hesaplanıyor...' : 'Rota İlet'}
                  </button>
                ) : null}
                {canManageAssignments ? (
                  <>
                    <button type="button" onClick={() => void handleMoveProcess(detailRow, 'prev')} disabled={!getProcessMoveState(detailRow).canRevert} className="inline-flex items-center gap-2 rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[11px] font-semibold text-slate-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40">
                      Önceki Süreç
                    </button>
                    <button type="button" onClick={() => void handleMoveProcess(detailRow, 'next')} disabled={!getProcessMoveState(detailRow).canAdvance} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40">
                      {getProcessMoveState(detailRow).canMoveToDeliveryApproval ? 'Teslim Onayına Gönder' : 'Sonraki Süreç'}
                    </button>
                  </>
                ) : null}
                {detailRow.archiveCode && detailRow.archiveCode !== '-' ? (
                  <>
                    <button type="button" onClick={() => void handleOpenArchiveModal(detailRow)} className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100">
                      <Archive className="h-3.5 w-3.5" />
                      Arşiv Bilgileri
                    </button>
                    <button type="button" onClick={() => void handleRestoreArchive(detailRow)} className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-semibold text-orange-700 hover:bg-orange-100">
                      Arşivden Geri Al
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="border-b border-slate-700/30 px-3 pt-2">
              <div className="grid grid-cols-4 gap-1">
                {[
                  ['info', 'Bilgi'],
                  ['goods', 'Yük'],
                  ['driver', 'Sürücü'],
                  ['tracking', 'Takip'],
                  ['documents', 'Evraklar'],
                  ['audit', 'İşlem Takibi'],
                ].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setDetailTab(key as any)} className={`border-b-2 px-2 py-3 text-center text-[11px] ${detailTab === key ? 'border-orange-500 font-semibold text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5">
              {detailTab === 'info' && (
                <div className="space-y-3 text-[12px]">
                  {[
                    ['Durum', getFlowStatusLabel(String(getEffectiveFlowStatus(detailRow)))],
                    ['Lojistik Türü', detailRow.lojistikTuru || '-'],
                    ['Yükleme Tarihi', formatDateTime(detailRow.yuklemeTarihi)],
                    ['Termin Tarihi', formatDateTime(detailRow.terminTarihi)],
                    ['Teslim Tipi', detailRow.randevuluTeslim || 'Standart'],
                    ['Çıkış / Yükleme', `${detailRow.yuklemeIli || '-'} ${detailRow.yuklemeIlcesi || ''}`.trim()],
                    ['Yükleyici', detailRow.yuklemeMusterisi || '-'],
                    ['Teslim Müşterisi', detailRow.bosaltmaMusterisi || '-'],
                    ['Yükleme Adresi', `${detailRow.yuklemeIli || '-'}${detailRow.yuklemeIlcesi ? ` / ${detailRow.yuklemeIlcesi}` : ''}`],
                    ['Teslim Adresi', `${detailRow.bosaltmaIli || '-'}${detailRow.teslimIlcesi ? ` / ${detailRow.teslimIlcesi}` : ''}`],
                    ['Uğrama Sayısı', String(detailRow.ugrmaSayisi || 1)],
                    ['Sipariş Notu', detailRow.operasyonNot || '-'],
                    ['Sürücü', detailRow.surucuAdi || '-'],
                    ['İrtibat Telefonu', detailRow.irtibatTelefon || '-'],
                    ['Sürücü Telefon', detailRow.surucuTelefon || '-'],
                    ['Fatura Başlığı', detailRow.faturaBasligi || '-'],
                    ['Dış Nakliye', detailRow.disNakliye || '-'],
                  ].map(([label, value]) => (
                    <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                      <div className="text-[11px] text-slate-400">{label}</div>
                      <div className="mt-1 font-semibold text-slate-100">{value}</div>
                    </div>
                  ))}
                </div>
              )}
              {detailTab === 'goods' && (
                <div className="space-y-3 text-[12px]">
                  {[
                    ['Yük Tipi', detailRow.hizmetTuru || '-'],
                    ['Araç / Kasa Tipi', detailRow.istenenKasaTipi || '-'],
                    ['Ağırlık', detailRow.yukAgirlik ? `${Number(detailRow.yukAgirlik).toLocaleString('tr-TR')} Kg` : '-'],
                    ['Hacim', detailRow.yukHacim ? `${Number(detailRow.yukHacim).toLocaleString('tr-TR')} m3` : '-'],
                    ['Palet Sayısı', detailRow.palletCount ? String(detailRow.palletCount) : '-'],
                    ['Navlun', `₺${Number(detailRow.navlun || 0).toLocaleString('tr-TR')}`],
                    ['Sefer Km', detailRow.seferKm ? `${Number(detailRow.seferKm).toLocaleString('tr-TR')} km` : '-'],
                    ['Fatura Başlığı', detailRow.faturaBasligi || '-'],
                    ['Dış Nakliye', detailRow.disNakliye || '-'],
                  ].map(([label, value]) => (
                    <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                      <div className="text-[11px] text-slate-400">{label}</div>
                      <div className="mt-1 font-semibold text-slate-100">{value}</div>
                    </div>
                  ))}
                </div>
              )}
              {detailTab === 'driver' && (
                <div className="space-y-3 text-[12px]">
                  {[
                    ['Sürücü Adı', detailRow.surucuAdi || '-'],
                    ['İrtibat Telefonu', detailRow.irtibatTelefon || '-'],
                    ['Sürücü Telefonu', detailRow.surucuTelefon || '-'],
                    ['Çekici', detailRow.cekici || '-'],
                    ['Dorse', detailRow.dorse || '-'],
                    ['Lojistik Türü', detailRow.lojistikTuru || '-'],
                  ].map(([label, value]) => (
                    <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                      <div className="text-[11px] text-slate-400">{label}</div>
                      <div className="mt-1 font-semibold text-slate-100">{value}</div>
                    </div>
                  ))}
                </div>
              )}
              {detailTab === 'tracking' && (
                <div className="space-y-3 text-[12px]">
                  <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                    <div className="text-[11px] text-slate-400">Takip bilgileri burada görüntülenecek</div>
                  </div>
                </div>
              )}
              {detailTab === 'documents' && (
                <div className="space-y-3 text-[12px]">
                  {[
                    ['Yükleme Evrakları', 'loading'],
                    ['Teslim Evrakları', 'delivery'],
                    ['İade / Tutanak Evrakları', 'return'],
                    ['Faturalar', 'invoice'],
                  ].map(([label, bucket]) => {
                    const documents = rowDocumentPreviews[detailRow.rowKey]?.[bucket as TrackingDocumentBucket] || [];
                    return (
                      <div key={bucket} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
                        {documents.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {documents.map((doc, index) => (
                              <div key={`${doc.url}-${index}`} className="flex items-center gap-2">
                                <button type="button" onClick={() => setDocumentSlider({ docs: documents, currentIndex: index, onDeleteCurrent: (currentDoc, currentIndex) => handleDeleteShipmentDocument(detailRow, bucket, currentDoc, currentIndex) })} className="rounded-lg border border-blue-700/40 bg-blue-950/40 px-3 py-1.5 text-[12px] font-semibold text-blue-200 hover:bg-blue-950/60">
                                  <Eye className="mr-1 inline h-3.5 w-3.5" />Görüntüle
                                </button>
                                <a href={doc.url} download className="rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-3 py-1.5 text-[12px] font-semibold text-emerald-200 hover:bg-emerald-950/60">
                                  <Download className="mr-1 inline h-3.5 w-3.5" />İndir
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-400">Evrak bulunmuyor</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {detailTab === 'audit' && (
                <div className="space-y-3 text-[12px]">
                  {shipmentAuditLogsLoading ? (
                    <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-4 text-slate-400">İşlem kayıtları yükleniyor...</div>
                  ) : shipmentAuditLogs.length === 0 ? (
                    <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-4 text-slate-400">Bu sevkiyat için işlem kaydı bulunmuyor</div>
                  ) : shipmentAuditLogs.map((log) => (
                    <div key={String(log.id)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[11px] text-slate-400">{log.logged_at ? formatDateTime(String(log.logged_at)) : '-'}</div>
                          <div className="mt-1 font-semibold text-slate-100">{log.description || `${log.category || '-'} / ${log.action || '-'}`}</div>
                          <div className="mt-1 text-[11px] text-slate-300">Kullanıcı: {log.user_name || '-'} • Rol: {log.user_role || '-'}</div>
                          <div className="mt-1 text-[11px] text-slate-400">Kategori: {log.category || '-'} • İşlem: {log.action || '-'}</div>
                          {log.error_message ? <div className="mt-1 text-[11px] text-rose-300">Hata: {log.error_message}</div> : null}
                        </div>
                        <div className={`rounded-full px-2 py-1 text-[10px] font-semibold ${log.success ? 'bg-emerald-950/50 text-emerald-300' : 'bg-rose-950/50 text-rose-300'}`}>
                          {log.success ? 'Başarılı' : 'Hata'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}
        </div>

        {locationReminderModalOpen ? (
          <LocationReminderModal
            rows={selectedRows.map((row) => ({
              driverUserId: row.driverUserId,
              driverName: row.surucuAdi,
              locationAge: formatLocationAge(row.surucuSonKonum),
            }))}
            onClose={() => setLocationReminderModalOpen(false)}
            onSent={() => setSelectedRowKeys([])}
          />
        ) : null}

        {bulkServiceTypeModalOpen ? (
          <BulkServiceTypeModal
            shipmentIds={selectedRows.map((row) => row.id)}
            onClose={() => setBulkServiceTypeModalOpen(false)}
            onSaved={() => { setSelectedRowKeys([]); void loadRows(); }}
          />
        ) : null}

        {archiveModalOpen && archiveShipmentRow ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-700/30 bg-[#151515] shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-400">{archiveModalMode === 'edit' ? 'Fiziksel Arşiv Kaydını Düzenle' : 'Fiziksel Arşive Taşı'}</div>
                  <div className="mt-1 text-lg font-semibold text-slate-100">#{archiveShipmentRow.sevkKodu || '-'}</div>
                </div>
                <button type="button" onClick={() => { if (!archiveSaving) setArchiveModalOpen(false); }} className="rounded-lg p-2 text-slate-400 hover:bg-white/5"><X className="h-4 w-4" /></button>
              </div>
              <div className="space-y-4 px-5 py-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Arşiv Kodu</label>
                  <input value={archivePreviewCode || 'Yükleniyor...'} disabled className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-200" />
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Klasör No</label>
                  <select value={archiveForm.folderName} onChange={(e) => setArchiveForm((prev) => ({ ...prev, folderName: e.target.value }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400">
                    <option value="">Klasör no seçin</option>
                    {archiveFolderOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Fatura No</label>
                  <input value={archiveForm.invoiceNumber} onChange={(e) => setArchiveForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))} placeholder="Fatura numarası girin" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400" />
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Yakıt Alımı</label>
                  <select value={String(archiveForm.hasFuelPurchase)} onChange={(e) => setArchiveForm((prev) => ({ ...prev, hasFuelPurchase: e.target.value === 'true' }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400">
                    <option value="false">Yok</option>
                    <option value="true">Var</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">PDF Döküman</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {archiveModalMode === 'create' ? (
                      pendingArchiveFile ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] text-slate-300">{pendingArchiveFile.name}</span>
                          <button type="button" onClick={() => setPendingArchiveFile(null)} className="rounded p-1 text-rose-400 hover:bg-rose-950/30"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.jpg,.jpeg,.png';
                          input.onchange = () => {
                            const file = input.files?.[0];
                            if (file) setPendingArchiveFile(file);
                          };
                          input.click();
                        }} className="rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[12px] font-semibold text-slate-200 hover:bg-white/5">
                          PDF Seç
                        </button>
                      )
                    ) : (
                      <>
                        {modalArchivePdfDocs.map((doc, index) => {
                          const fileUrl = resolveFileUrl(doc?.file_path || doc?.filePath || doc?.url || doc?.path);
                          return (
                            <div key={`${fileUrl}-${index}`} className="flex items-center gap-2">
                              <button type="button" onClick={() => setDocumentSlider({ docs: modalArchivePdfDocs.map((d, i) => normalizeDocument(d, i)), currentIndex: index })} className="rounded-lg border border-blue-700/40 bg-blue-950/40 px-3 py-1.5 text-[12px] font-semibold text-blue-200 hover:bg-blue-950/60">
                                <Eye className="mr-1 inline h-3.5 w-3.5" />Görüntüle
                              </button>
                              <a href={fileUrl} download className="rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-3 py-1.5 text-[12px] font-semibold text-emerald-200 hover:bg-emerald-950/60">
                                <Download className="mr-1 inline h-3.5 w-3.5" />İndir
                              </a>
                            </div>
                          );
                        })}
                        <button type="button" disabled={uploadingModalArchivePdf} onClick={() => void handleArchivePdfUpload()} className="rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[12px] font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-50">
                          {uploadingModalArchivePdf ? 'Yükleniyor...' : (modalArchivePdfDocs.length > 0 ? 'Değiştir' : 'PDF Yükle')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Evrak Sıra Numarası</label>
                  <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                    <input value={archiveForm.evrakSiraNo} onChange={(e) => setArchiveForm((prev) => ({ ...prev, evrakSiraNo: e.target.value.replace(/\D/g, '') }))} placeholder="Sıra numarası girin" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400" />
                    {archiveModalMode === 'create' ? (
                      <button type="button" onClick={() => {
                        archiveService.getNextFolderNumber(archiveFolderStartFrom || null)
                          .then((response: any) => setArchiveForm((prev) => ({ ...prev, evrakSiraNo: String(response?.data?.folderNumber || '') })))
                          .catch(() => toast.error('Sıradaki numara alınamadı'));
                      }} className="rounded-xl border border-amber-700/40 bg-amber-950/40 px-4 py-3 text-[12px] font-semibold text-amber-200 hover:bg-amber-950/60">
                        Sıradaki Numara
                      </button>
                    ) : <div />}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-slate-700/30 px-5 py-4">
                <button type="button" onClick={() => { if (!archiveSaving) setArchiveModalOpen(false); }} disabled={archiveSaving} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5">Vazgeç</button>
                <button type="button" onClick={() => void handleArchiveSubmit()} disabled={archiveSaving} className="rounded-lg bg-amber-600 px-4 py-2 text-white disabled:opacity-50">{archiveSaving ? (archiveModalMode === 'edit' ? 'Güncelleniyor...' : 'Arşivleniyor...') : (archiveModalMode === 'edit' ? 'Kaydet' : 'Arşive Gönder')}</button>
              </div>
            </div>
          </div>
        ) : null}

        {freightModalRow ? (
          <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-[#171717]" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Navlun Güncelle</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">#{freightModalRow.sevkKodu}</div>
                </div>
                <button type="button" onClick={() => !savingFreightUpdate && setFreightModalRow(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-[#202020]"><X className="h-4 w-4" /></button>
              </div>
              <div className="space-y-4 px-5 py-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mevcut Navlun</label>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-100">{Number(freightModalRow.navlun || 0).toLocaleString('tr-TR')} ₺</div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Yeni Navlun</label>
                  <input value={freightUpdateForm.amount} onChange={(e) => setFreightUpdateForm((current) => ({ ...current, amount: e.target.value }))} type="number" min="0" step="0.01" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-emerald-400 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-100" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Güncelleme Nedeni</label>
                  <select value={freightUpdateForm.reasonCode} onChange={(e) => {
                    const selectedReason = FREIGHT_UPDATE_REASONS.find((item) => item.value === e.target.value);
                    setFreightUpdateForm((current) => ({ ...current, reasonCode: e.target.value, reasonText: selectedReason?.label || e.target.value }));
                  }} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-emerald-400 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-100">
                    {FREIGHT_UPDATE_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Not</label>
                  <textarea value={freightUpdateForm.note} onChange={(e) => setFreightUpdateForm((current) => ({ ...current, note: e.target.value }))} rows={3} placeholder="Opsiyonel açıklama" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-emerald-400 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-100" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
                <button type="button" onClick={() => setFreightModalRow(null)} disabled={savingFreightUpdate} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200">Vazgeç</button>
                <button type="button" onClick={() => void submitFreightUpdate()} disabled={savingFreightUpdate} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{savingFreightUpdate ? 'Güncelleniyor...' : 'Navlunu Güncelle'}</button>
              </div>
            </div>
          </div>
        ) : null}

        {cellFilterMenu ? (
          <div className="fixed z-[80] min-w-[220px] rounded-xl border border-slate-200 bg-white p-2 shadow-2xl dark:border-slate-700 dark:bg-[#171717]"
            style={{ left: Math.max(12, cellFilterMenu.x), top: Math.max(12, cellFilterMenu.y) }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{cellFilterMenu.columnLabel}</div>
            <div className="truncate px-2 pb-2 text-sm text-slate-800 dark:text-slate-100">{cellFilterMenu.value}</div>
            <button
              type="button"
              onClick={() => applyFilterFromCell()}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-[#202020]"
            >
              Seçili değer ile filtrele
            </button>
          </div>
        ) : null}

        {showAssignModal && assignRow ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-[96vw] overflow-hidden rounded-xl border border-slate-700/30 bg-[#151515] shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-400">Özmal Atama</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-100">#{assignRow.sevkKodu}</div>
                </div>
                <button type="button" onClick={() => setShowAssignModal(false)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
              </div>

                <div className="space-y-5 p-5">
                  <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] p-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                    <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                      <div className="text-xs text-slate-400">Rota</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{`${assignRow.yuklemeIli || '-'} -> ${assignRow.bosaltmaIli || '-'}`}</div>
                      <div className="mt-1 text-xs text-slate-400">{assignRow.yuklemeMusterisi || '-'} / {assignRow.bosaltmaMusterisi || '-'}</div>
                    </div>
                    <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                      <div className="text-xs text-slate-400">Mesafe</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{assignRow.seferKm || 0} km</div>
                    </div>
                    <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                      <div className="text-xs text-slate-400">Yük Ağırlığı</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{Number(assignRow.yukAgirlik || 0).toLocaleString('tr-TR')} kg</div>
                    </div>
                    <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                      <div className="text-xs text-slate-400">İstenen Kasa Tipi</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{assignRow.istenenKasaTipi || '-'}</div>
                    </div>
                    <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                      <div className="text-xs text-slate-400">Yükleme Talep Tarihi</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{formatDateTime(assignRow.yuklemeTarihi)}</div>
                    </div>
                  </div>
                  {Number(assignRow.seferKm || 0) > 0 ? (
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                        <div className="text-xs font-semibold text-blue-600">Tahmini Özmal Maliyet</div>
                        <div className="mt-1 text-lg font-bold text-blue-900">{(Number(assignRow.seferKm) * kmCosts.ozmal).toLocaleString('tr-TR')} ₺</div>
                        <div className="text-xs text-blue-500">{kmCosts.ozmal.toLocaleString('tr-TR')} ₺/km × {assignRow.seferKm} km</div>
                      </div>
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <div className="text-xs font-semibold text-amber-600">Tahmini Spot Maliyet</div>
                        <div className="mt-1 text-lg font-bold text-amber-900">{(Number(assignRow.seferKm) * kmCosts.spot).toLocaleString('tr-TR')} ₺</div>
                        <div className="text-xs text-amber-500">{kmCosts.spot.toLocaleString('tr-TR')} ₺/km × {assignRow.seferKm} km</div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setAssignType('own-fleet')} className={`rounded-xl border px-4 py-3 text-left ${assignType === 'own-fleet' ? 'border-orange-500/40 bg-orange-950/30 text-orange-100' : 'border-slate-700/40 bg-[#111111] text-slate-200'}`}>
                    <div className="font-semibold">Özmal</div>
                    <div className={`mt-1 text-xs ${assignType === 'own-fleet' ? 'text-orange-200' : 'text-slate-400'}`}>Araç seçerek atama yap</div>
                  </button>
                  <button type="button" onClick={() => { setAssignType('spot'); setAgreedPrice((current) => String(current || '').trim() ? current : getSuggestedSpotAgreedPrice(assignRow)); }} className={`rounded-xl border px-4 py-3 text-left ${assignType === 'spot' ? 'border-blue-700/40 bg-blue-950/40 text-blue-100' : 'border-slate-700/40 bg-[#111111] text-slate-200'}`}>
                    <div className="font-semibold">Spot</div>
                    <div className={`mt-1 text-xs ${assignType === 'spot' ? 'text-blue-200' : 'text-slate-400'}`}>Sürücü ve anlaşma fiyatı gir</div>
                  </button>
                </div>

                {assignType === 'own-fleet' ? (
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-200">Özmal Araç ve Sürücü Seçimi</label>
                    </div>
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-emerald-950/60 px-2.5 py-1 font-semibold text-emerald-300">Uygun Özmal: {fleetOptions.filter((item) => item.isAssignable).length}</span>
                      <span className="rounded-full bg-slate-800 px-2.5 py-1 font-semibold text-slate-200">Toplam: {fleetOptions.length}</span>
                      <span className="rounded-full bg-amber-950/60 px-2.5 py-1 font-semibold text-amber-300">Devam Eden Görevli: {fleetOptions.filter((item) => item.hasActiveTask).length}</span>
                    </div>
                    <input value={fleetSearch} onChange={(e) => setFleetSearch(e.target.value)} placeholder="Sürücü, çekici, dorse, kasa tipi veya konum ara" className="mb-3 w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" />
                    <div className="max-h-80 overflow-auto rounded-xl border border-slate-700/30 bg-[#111111]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#1a1a1a]">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Sürücü</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Telefon</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Çekici</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Dorse</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Kasa Tipi</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Atamaya Uygun</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Aktiflik</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Devam Eden Görev</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Kalan Çalışma</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Çalışma Süresi</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Haftalık</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">2 Hafta</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Mevcut Konum</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Yüklemeye Km</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Seç</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fleetOptions.map((item) => {
                            const selected = selectedVehicleId === item.vehicleId;
                            return (
                              <tr key={item.vehicleId} className={selected ? 'bg-blue-950/30' : 'bg-[#111111]'}>
                                <td className="px-3 py-2 font-medium text-slate-100">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span>{item.driverName}</span>
                                    {item.hasTemporaryAssignment ? (
                                      <Info
                                        className="h-3.5 w-3.5 text-sky-400"
                                        title={`Gecici gorevlendirme aktif${item.temporaryDriverName ? `: ${item.temporaryDriverName}` : ''}${item.permanentDriverName ? ` | Asil: ${item.permanentDriverName}` : ''}${item.temporaryAssignmentStartDate ? ` | Baslangic: ${item.temporaryAssignmentStartDate}` : ''}${item.temporaryAssignmentEndDate ? ` | Bitis: ${item.temporaryAssignmentEndDate}` : ''}${item.temporaryAssignmentNotes ? ` | Not: ${item.temporaryAssignmentNotes}` : ''}`}
                                      />
                                    ) : null}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-slate-300">{item.driverPhone || '-'}</td>
                                <td className="px-3 py-2 text-slate-300">{item.tractorPlate}</td>
                                <td className="px-3 py-2 text-slate-300">{item.trailerPlate}</td>
                                <td className="px-3 py-2 text-slate-300">{item.vehicleType}</td>
                                <td className="px-3 py-2 text-slate-300"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isAssignable ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>{item.isAssignable ? 'Uygun' : 'Uygun Değil'}</span></td>
                                <td className="px-3 py-2 text-slate-300"><div className="flex flex-col gap-1"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isVehicleActive ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>Araç {item.isVehicleActive ? 'Aktif' : 'Pasif'}</span><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isDriverActive ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>Sürücü {item.isDriverActive ? 'Aktif' : 'Pasif'}</span></div></td>
                                <td className="px-3 py-2 text-slate-300">{item.hasActiveTask ? <div className="max-w-[220px] space-y-1 text-xs text-amber-300"><div className="font-semibold">{item.activeTaskCount} aktif görev</div><div className="truncate" title={item.activeTaskCodes.join(', ')}>{item.activeTaskCodes.join(', ')}</div></div> : <span className="inline-flex rounded-full bg-emerald-950/60 px-2.5 py-1 text-xs font-semibold text-emerald-300">Görev Yok</span>}</td>
                                <td className="px-3 py-2 text-slate-300">{item.remainingWorkTime}</td>
                                <td className="px-3 py-2 text-slate-300">{item.workDuration}</td>
                                <td className="px-3 py-2 text-slate-300">{item.currentWeekHours}</td>
                                <td className="px-3 py-2 text-slate-300">{item.twoWeekHours}</td>
                                <td className="max-w-[280px] truncate px-3 py-2 text-slate-300" title={item.currentAddress}>{item.currentAddress}</td>
                                <td className="px-3 py-2 text-slate-300">{item.distanceToPickupKm != null ? <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getDistanceBadgeClass(item.distanceToPickupKm)}`}>{item.distanceToPickupKm} km</span> : '-'}</td>
                                <td className="px-3 py-2"><button type="button" onClick={() => setSelectedVehicleId(item.vehicleId)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${selected ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>{selected ? 'Seçildi' : 'Seç'}</button></td>
                              </tr>
                            );
                          })}
                          {assignmentSourcesLoading ? <tr><td colSpan={15} className="px-3 py-6 text-center text-slate-400">Özmal araçlar yükleniyor...</td></tr> : fleetOptions.length === 0 ? <tr><td colSpan={15} className="px-3 py-6 text-center text-slate-400">Gösterilecek özmal araç bulunamadı</td></tr> : null}
                        </tbody>
            </table>
          </div>
          {!constrainTableToViewport && stickyScrollbarMetrics.scrollWidth > stickyScrollbarMetrics.clientWidth && stickyScrollbarViewportStyle.visible ? (
            <div className="fixed bottom-3 z-40 rounded-xl border border-slate-200 bg-white/95 px-2 py-2 shadow-lg backdrop-blur dark:border-slate-800 dark:bg-[#111111]/95" style={{ left: `${stickyScrollbarViewportStyle.left}px`, width: `${stickyScrollbarViewportStyle.width}px` }}>
              <div ref={stickyScrollbarRef} className="overflow-x-auto overflow-y-hidden">
                <div ref={stickyScrollbarInnerRef} style={{ width: `${stickyScrollbarMetrics.scrollWidth}px`, height: '1px' }} />
              </div>
            </div>
          ) : null}
        </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-slate-200">Spot Sürücü Seçimi</label>
                        <button type="button" onClick={() => setShowCreateSpotDriverModal(true)} className="rounded-lg border border-slate-700/40 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/5">+ Yeni Spot Sürücü</button>
                      </div>
                      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <select value={selectedSpotCompanyId} onChange={(e) => setSelectedSpotCompanyId(e.target.value)} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100">
                          <option value="all">Tüm Spot Kaynakları</option>
                          <option value="individual">Bireysel Spot Sürücüler</option>
                          {corporateCarrierAccounts.map((item: any) => (
                            <option key={item.id} value={String(item.id)}>{item.full_name || item.username || `Firma #${item.id}`}</option>
                          ))}
                        </select>
                        <input value={fleetSearch} onChange={(e) => setFleetSearch(e.target.value)} placeholder="Sürücü, telefon, çekici, dorse, kasa tipi veya konum ara" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" />
                      </div>
                      <div className="max-h-80 overflow-auto rounded-xl border border-slate-700/30 bg-[#111111]">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-[#1a1a1a]">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Firma</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Sürücü</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Telefon</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Çekici</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Dorse</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Kasa Tipi</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Uyumluluk</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Mevcut Konum</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Yüklemeye Km</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-300">Seç</th>
                            </tr>
                          </thead>
                          <tbody>
                            {spotOptions.map((item) => {
                              const selected = selectedApplicationId === item.applicationId;
                              return (
                                <tr key={item.applicationId} className={selected ? 'bg-blue-950/30' : 'bg-[#111111]'}>
                                  <td className="px-3 py-2 text-slate-300">{item.externalTransportAccountName || <span className="text-slate-500">Bireysel</span>}</td>
                                  <td className="px-3 py-2 font-medium text-slate-100">{item.driverName}</td>
                                  <td className="px-3 py-2 text-slate-300">{item.phone}</td>
                                  <td className="px-3 py-2 text-slate-300">{item.tractorPlate}</td>
                                  <td className="px-3 py-2 text-slate-300">{item.trailerPlate}</td>
                                  <td className="px-3 py-2 text-slate-300">{item.vehicleType}</td>
                                  <td className="px-3 py-2 text-slate-300"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isCompatible ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>{item.isCompatible ? 'Uyumlu' : 'Uyumsuz'}</span></td>
                                  <td className="max-w-[220px] truncate px-3 py-2 text-slate-300" title={item.currentAddress}>{item.currentAddress}</td>
                                  <td className="px-3 py-2 text-slate-300">{item.distanceToPickupKm != null ? <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getDistanceBadgeClass(item.distanceToPickupKm)}`}>{item.distanceToPickupKm} km</span> : <span className="inline-flex rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">Konum Yok</span>}</td>
                                  <td className="px-3 py-2"><button type="button" onClick={() => setSelectedApplicationId(item.applicationId)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${selected ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>{selected ? 'Seçildi' : 'Seç'}</button></td>
                                </tr>
                              );
                            })}
                            {assignmentSourcesLoading ? <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">Spot sürücüler yükleniyor...</td></tr> : spotOptions.length === 0 ? <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">Gösterilecek spot sürücü bulunamadı</td></tr> : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-200">Anlaşılan Fiyat</label>
                      <div className="mb-2 rounded-xl border border-rose-700/50 bg-rose-950/40 px-4 py-3 text-sm font-semibold text-rose-200">
                        Satınalma Max Fiyat: <span className="text-base text-rose-100">{formatMoney(Number(assignRow.bazFiyat || 0))}</span>
                      </div>
                      <input value={agreedPrice} onChange={(e) => setAgreedPrice(e.target.value)} type="number" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: 24500" />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-700/30 bg-[#1a1a1a] px-5 py-4">
                <button type="button" onClick={() => setShowAssignModal(false)} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5">Vazgeç</button>
                <button type="button" onClick={() => void submitAssignment()} disabled={assigning || (assignType === 'own-fleet' ? !selectedVehicleId : !selectedApplicationId)} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
                  {assigning ? 'Atanıyor...' : 'Ata ve Süreci Başlat'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {bulkProcessStatusModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-xl border border-slate-700/30 bg-[#151515] shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-400">Genel Liste</div>
                  <div className="mt-1 text-xl font-semibold text-slate-100">Toplu Durum Güncelle</div>
                </div>
                <button type="button" onClick={() => !bulkUpdatingProcessStatus && setBulkProcessStatusModalOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4 p-5">
                <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-4 text-sm text-slate-200">
                  <div className="font-semibold text-slate-100">{selectedRows.length} kayıt seçildi</div>
                  <div className="mt-1 text-xs text-slate-400">Seçilen kayıtların işlem durumu tek adımda aynı değere çekilir.</div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200">Yeni İşlem Durumu</label>
                  <select value={bulkProcessStatusValue} onChange={(event) => setBulkProcessStatusValue(event.target.value)} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100">
                    {MANUAL_PROCESS_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-700/30 pt-4">
                  <button type="button" onClick={() => setBulkProcessStatusModalOpen(false)} disabled={bulkUpdatingProcessStatus} className="rounded-lg border border-slate-700/40 bg-[#111111] px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-60">
                    Vazgeç
                  </button>
                  <button type="button" onClick={() => void handleBulkProcessStatusUpdate()} disabled={bulkUpdatingProcessStatus} className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                    {bulkUpdatingProcessStatus ? 'Güncelleniyor...' : 'Güncelle'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {bulkAccountingModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-xl border border-slate-700/30 bg-[#151515] shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-400">Finansal İşlemler</div>
                  <div className="mt-1 text-xl font-semibold text-slate-100">Toplu Finansal Durum Güncelle</div>
                </div>
                <button type="button" onClick={() => !bulkUpdatingAccounting && setBulkAccountingModalOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4 p-5">
                <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-4 text-sm text-slate-200">
                  <div className="font-semibold text-slate-100">{selectedRows.length} kayıt seçildi</div>
                  <div className="mt-1 text-xs text-slate-400">Seçilen kayıtların finansal durumu toplu olarak güncellenecek.</div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200">Hedef Durum</label>
                  <select value={bulkAccountingTargetStatus} onChange={(event) => setBulkAccountingTargetStatus(event.target.value)} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100 outline-none">
                    <option value="invoice-waiting">Fatura Bekliyor</option>
                    <option value="invoice-approval-pending">Fatura Kontrol</option>
                    <option value="invoice-check-approved">Fatura Onay</option>
                    <option value="payment-pending">Muhasebeye Aktarıldı</option>
                    <option value="payment-completed">Ödemesi Gerçekleşen</option>
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-700/30 pt-4">
                  <button type="button" onClick={() => setBulkAccountingModalOpen(false)} disabled={bulkUpdatingAccounting} className="rounded-lg border border-slate-700/40 bg-[#111111] px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-60">
                    Vazgeç
                  </button>
                  <button type="button" onClick={() => void handleBulkAccountingStatusUpdate()} disabled={bulkUpdatingAccounting} className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60">
                    {bulkUpdatingAccounting ? 'Güncelleniyor...' : 'Durumu Güncelle'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {bulkSelectPasteOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setBulkSelectPasteOpen(false)}>
            <div className="w-full max-w-xl rounded-2xl bg-[#111111] text-slate-100 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="border-b border-slate-800 px-5 py-4">
                <div className="text-xl font-semibold text-slate-100">Excelden Toplu Seç</div>
                <div className="mt-1 text-sm text-slate-400">Exceldeki siparis no sutununu kopyalayip buraya yapistirin. Her satir bir siparis no olabilir.</div>
              </div>
              <div className="space-y-4 px-5 py-4">
                <textarea
                  autoFocus
                  rows={10}
                  value={bulkSelectPasteValue}
                  onChange={(e) => setBulkSelectPasteValue(e.target.value)}
                  placeholder="Ornek:&#10;DN0626000003&#10;DN0626000004&#10;DN0626000005"
                  className="w-full rounded-xl border border-slate-700 bg-[#0b0b0b] px-4 py-3 text-sm text-slate-100 outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-5 py-4">
                <button type="button" onClick={() => setBulkSelectPasteOpen(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">Vazgec</button>
                <button type="button" onClick={handleBulkSelectByShipmentNumbers} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Kayitlari Sec</button>
              </div>
            </div>
          </div>
        ) : null}

        {showCreateSpotDriverModal ? (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
            <div className="w-full max-w-2xl rounded-2xl border border-slate-700/40 bg-[#1a1a1a] shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
                <div>
                  <div className="text-lg font-semibold text-slate-100">Yeni Spot Sürücü</div>
                  <div className="mt-1 text-sm text-slate-400">Kayıt onaylı oluşturulur ve listeye hemen eklenir.</div>
                </div>
                <button type="button" onClick={() => { if (!creatingSpotDriver) { setShowCreateSpotDriverModal(false); resetNewSpotDriverForm(); } }} className="rounded-lg border border-slate-700/40 px-3 py-1.5 text-slate-300 hover:bg-white/5">Kapat</button>
              </div>
              <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2">
                <div className="md:col-span-2"><label className="mb-2 block text-sm font-medium text-slate-200">Ad Soyad</label><input value={newSpotDriverForm.full_name} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, full_name: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Ad soyad girin" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">Telefon</label><input value={newSpotDriverForm.phone} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, phone: normalizePhoneInput(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="5XXXXXXXXX" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">TC Kimlik No</label><input value={newSpotDriverForm.tc_kimlik_no} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, tc_kimlik_no: e.target.value }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="11 haneli TC kimlik no" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">Kasa Tipi</label><select value={newSpotDriverForm.vehicle_type} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, vehicle_type: e.target.value }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100"><option value="">Kasa tipi seçin</option>{spotVehicleTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">Çekici Plaka</label><input value={newSpotDriverForm.cekici_plaka} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, cekici_plaka: normalizePlate(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="34 ABC 123" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">Dorse Plaka</label><input value={newSpotDriverForm.dorse_plaka} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, dorse_plaka: normalizePlate(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="34 ABC 124" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">İl</label><input value={newSpotDriverForm.city} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, city: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="İl girin" /></div>
                <div><label className="mb-2 block text-sm font-medium text-slate-200">İlçe</label><input value={newSpotDriverForm.district} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, district: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="İlçe girin" /></div>
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-700/30 px-5 py-4">
                <button type="button" onClick={() => { setShowCreateSpotDriverModal(false); resetNewSpotDriverForm(); }} disabled={creatingSpotDriver} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5 disabled:opacity-50">Vazgeç</button>
                <button type="button" onClick={() => void submitNewSpotDriver()} disabled={creatingSpotDriver} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{creatingSpotDriver ? 'Kaydediliyor...' : 'Sürücüyü Ekle'}</button>
              </div>
            </div>
          </div>
        ) : null}

        {documentSlider && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          >
            <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-700 bg-white shadow-2xl dark:bg-[#111111]"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
                <div className="min-w-0 truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                  {documentSlider.docs[documentSlider.currentIndex]?.fileName || 'Belge'}
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    ({documentSlider.currentIndex + 1} / {documentSlider.docs.length})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={documentSlider.docs[documentSlider.currentIndex]?.url}
                    download={documentSlider.docs[documentSlider.currentIndex]?.fileName || 'belge'}
                    className="rounded-lg border border-emerald-300 p-2 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-200 dark:hover:bg-[#202020]"
                    title="İndir"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  {documentSlider.onDeleteCurrent && documentSlider.docs[documentSlider.currentIndex]?.id ? (
                    <button
                      type="button"
                      onClick={() => void documentSlider.onDeleteCurrent?.(documentSlider.docs[documentSlider.currentIndex], documentSlider.currentIndex)}
                      disabled={deletingSliderDocument}
                      className="rounded-lg border border-rose-300 p-2 text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-800 dark:text-rose-200 dark:hover:bg-[#202020]"
                      title="Sil"
                    >
                      {deletingSliderDocument ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setDocumentSlider(prev => prev ? { ...prev, currentIndex: Math.max(0, prev.currentIndex - 1) } : null)}
                    disabled={documentSlider.currentIndex === 0}
                    className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#202020]"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDocumentSlider(prev => prev ? { ...prev, currentIndex: Math.min(prev.docs.length - 1, prev.currentIndex + 1) } : null)}
                    disabled={documentSlider.currentIndex >= documentSlider.docs.length - 1}
                    className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#202020]"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDocumentSlider(null)}
                    className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#202020]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex max-h-[78vh] items-center justify-center overflow-auto bg-slate-100 p-4 dark:bg-black">
                {documentSlider.docs[documentSlider.currentIndex]?.isImage ? (
                  <img src={documentSlider.docs[documentSlider.currentIndex].url} alt={documentSlider.docs[documentSlider.currentIndex].fileName} className="max-h-[74vh] max-w-full rounded-lg object-contain" />
                ) : (
                  <embed src={documentSlider.docs[documentSlider.currentIndex].url} title={documentSlider.docs[documentSlider.currentIndex].fileName} className="h-[74vh] w-full rounded-lg bg-white" type="application/pdf" />
                )}
              </div>

              <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                <a
                  href={documentSlider.docs[documentSlider.currentIndex].url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
                >
                  Yeni sekmede aç
                </a>
              </div>
            </div>
          </div>
        )}

        {offerCollectionLoading ? (
          <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl dark:bg-[#111111]">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-violet-600" />
              <div className="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Teklif talebi oluşturuluyor</div>
              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">{offerCollectionLoadingCode ? `#${offerCollectionLoadingCode} için işlem sürüyor. Lütfen bekleyin.` : 'Lütfen bekleyin.'}</div>
            </div>
          </div>
        ) : null}

        {offerCollectionConfirmRows.length > 0 ? (
          <div className="fixed inset-0 z-[94] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#111111]">
              <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">Teklif Talebi Oluştur</div>
              <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">{offerCollectionConfirmRows.length === 1 ? `#${offerCollectionConfirmRows[0].sevkKodu} sevkiyatı teklif toplamaya alınsın mı?` : `${offerCollectionConfirmRows.length} kayıt teklif toplamaya alınsın mı?`}</div>

              {offerCollectionConfirmRows.length === 1 && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50">
                  <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-slate-700">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-violet-600" />
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Bildirim Gidecek Sürücüler</span>
                    </div>
                    {!eligibleDriversLoading && eligibleDriversMeta && (
                      <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-bold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {eligibleDriversMeta.totalEligible} sürücü
                      </span>
                    )}
                  </div>

                  {eligibleDriversMeta && (
                    <div className="border-b border-slate-200 px-4 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      {eligibleDriversMeta.radiusKm > 0 ? `Km Çapı: ${eligibleDriversMeta.radiusKm} km` : 'Km Kısıtı Yok'}
                      {eligibleDriversMeta.recencyMinutes > 0 && ` · Son ${eligibleDriversMeta.recencyMinutes} dk`}
                      {eligibleDriversMeta.whitelistActive && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Whitelist Aktif</span>}
                    </div>
                  )}

                  <div className="max-h-48 overflow-y-auto px-4 py-2">
                    {eligibleDriversLoading ? (
                      <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sürücüler hesaplanıyor...
                      </div>
                    ) : eligibleDrivers.length === 0 ? (
                      <div className="py-4 text-center text-sm text-slate-400">
                        Uygun spot sürücü bulunamadı
                      </div>
                    ) : (
                      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {eligibleDrivers.map((driver) => (
                          <li key={driver.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex-shrink-0">
                                {(driver.full_name || driver.phone || '?').charAt(0).toUpperCase('tr-TR')}
                              </span>
                              <div className="min-w-0">
                                <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                                  {driver.full_name || `Sürücü #${driver.id}`}
                                </span>
                                {driver.phone && <span className="block text-xs text-slate-400">{driver.phone}</span>}
                              </div>
                            </div>
                            {driver.distance_km != null && (
                              <span className="ml-2 flex-shrink-0 rounded bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                {driver.distance_km} km
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {!eligibleDriversLoading && eligibleDrivers.length > 0 && (
                    <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      Bu sevkiyat açıldığında yukarıdaki {eligibleDrivers.length} sürücüye bildirim gidecektir.
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => !offerCollectionLoading && setOfferCollectionConfirmRows([])} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#171717]">Vazgeç</button>
                <button type="button" disabled={offerCollectionLoading} onClick={() => offerCollectionConfirmRows.length === 1 ? void executeSingleOfferCollection(offerCollectionConfirmRows[0]) : void confirmOfferCollection()} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">{offerCollectionLoading ? 'Açılıyor...' : 'Teklif Topla'}</button>
              </div>
            </div>
          </div>
        ) : null}

        {paymentApprovalModalOpen && paymentApprovalRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-700 bg-white shadow-2xl dark:bg-[#111111]">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
                <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                  {paymentApprovalMode === 'controller' ? 'Fatura Kontrol - Kontrol Eden' : 'Fatura Onay - Onaylayan'}
                </div>
                <button type="button" onClick={() => { setPaymentApprovalModalOpen(false); setPaymentApprovalRows([]); }} className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#202020]">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid max-h-[78vh] grid-cols-1 gap-4 overflow-y-auto p-4 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-400">Kullanıcı Seç</label>
                    <select value={paymentApprovalSelectedUserId} onChange={(e) => setPaymentApprovalSelectedUserId(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
                      <option value="">Kullanıcı seçin...</option>
                      {paymentApprovalUsers.map((u: any) => (
                        <option key={u.id} value={u.id}>{u.full_name || u.name || u.email} ({u.email})</option>
                      ))}
                    </select>
                  </div>

                  {paymentApprovalRows.length > 1 ? (
                    <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
                      {paymentApprovalRows.length} kayda toplu islem uygulanacak.
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-400">Sevkiyat Bilgileri</label>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-[#1a1a1a]">
                      <div className="mb-1"><span className="font-semibold">Sipariş:</span> {paymentApprovalRow.sevkKodu}</div>
                      <div className="mb-1"><span className="font-semibold">Yükleme:</span> {paymentApprovalRow.yuklemeIli}</div>
                      <div className="mb-1"><span className="font-semibold">Boşaltma:</span> {paymentApprovalRow.bosaltmaIli}</div>
                      <div className="mb-1"><span className="font-semibold">Navlun:</span> {Number(paymentApprovalRow.navlun).toLocaleString('tr-TR')} TL</div>
                      <div><span className="font-semibold">Fatura:</span> {paymentApprovalRow.invoiceNumber || '-'}</div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-400">İmza</label>
                    <div className="rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-white">
                      <canvas
                        ref={paymentApprovalSignatureRef}
                        className="h-32 w-full cursor-crosshair rounded-md border border-slate-300 dark:border-slate-600"
                        onMouseDown={(e) => {
                          const canvas = paymentApprovalSignatureRef.current;
                          if (!canvas) return;
                          const ctx = canvas.getContext('2d');
                          if (!ctx) return;
                          const rect = canvas.getBoundingClientRect();
                          ctx.strokeStyle = '#000';
                          ctx.lineWidth = 2;
                          ctx.beginPath();
                          ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                          const drawHandler = (ev: MouseEvent) => {
                            ctx.lineTo(ev.clientX - rect.left, ev.clientY - rect.top);
                            ctx.stroke();
                          };
                          const upHandler = () => {
                            canvas.removeEventListener('mousemove', drawHandler);
                            canvas.removeEventListener('mouseup', upHandler);
                            setPaymentApprovalSignatureAvailable(true);
                          };
                          canvas.addEventListener('mousemove', drawHandler);
                          canvas.addEventListener('mouseup', upHandler);
                        }}
                        onTouchStart={(e) => {
                          const canvas = paymentApprovalSignatureRef.current;
                          if (!canvas) return;
                          const ctx = canvas.getContext('2d');
                          if (!ctx) return;
                          const rect = canvas.getBoundingClientRect();
                          const touch = e.touches[0];
                          ctx.strokeStyle = '#000';
                          ctx.lineWidth = 2;
                          ctx.beginPath();
                          ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
                          const drawHandler = (ev: TouchEvent) => {
                            ev.preventDefault();
                            const t = ev.touches[0];
                            ctx.lineTo(t.clientX - rect.left, t.clientY - rect.top);
                            ctx.stroke();
                          };
                          const upHandler = () => {
                            canvas.removeEventListener('touchmove', drawHandler);
                            canvas.removeEventListener('touchend', upHandler);
                            setPaymentApprovalSignatureAvailable(true);
                          };
                          canvas.addEventListener('touchmove', drawHandler, { passive: false });
                          canvas.addEventListener('touchend', upHandler);
                        }}
                      />
                    </div>
                    <button type="button" onClick={() => {
                      const canvas = paymentApprovalSignatureRef.current;
                      if (!canvas) return;
                      canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
                      setPaymentApprovalSignatureAvailable(false);
                    }} className="mt-1 text-[11px] text-slate-400 hover:text-slate-600">Temizle</button>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-400">Evraklar</label>
                  <div className="max-h-[55vh] space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-[#1a1a1a]">
                    {(['loading', 'delivery', 'invoice'] as const).map((bucket) => {
                      const docs = rowDocumentPreviews[paymentApprovalRow.rowKey]?.[bucket] || [];
                      if (!docs.length) return null;
                      return (
                        <div key={bucket} className="mb-3">
                          <div className="mb-1 text-[11px] font-semibold text-slate-400">
                            {bucket === 'loading' ? '📦 Yükleme Evrakları' : bucket === 'delivery' ? '📄 Teslim Evrakları' : '💰 Faturalar'}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {docs.map((doc, i) => (
                              <button key={i} type="button" onClick={() => setDocumentSlider({ docs, currentIndex: i })} className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border border-slate-300 bg-white text-[9px] font-bold text-slate-500 hover:ring-2 hover:ring-orange-400 dark:border-slate-600 dark:bg-[#171717]">
                                {doc.isImage ? (
                                  <img src={doc.url} alt={doc.fileName} className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                  <span className="uppercase">{doc.fileName.split('.').pop()?.slice(0, 3) || 'PDF'}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {['loading', 'delivery', 'invoice'].every((b) => (rowDocumentPreviews[paymentApprovalRow.rowKey]?.[b as TrackingDocumentBucket] || []).length === 0) && (
                      <div className="py-8 text-center text-xs text-slate-400">Henüz evrak yüklenmemiş</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                <button type="button" onClick={() => { setPaymentApprovalModalOpen(false); setPaymentApprovalRows([]); }} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#202020]">İptal</button>
                <button type="button" onClick={() => void handlePaymentApprovalConfirm()} disabled={paymentApprovalSaving || !paymentApprovalSelectedUserId} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">{paymentApprovalSaving ? 'Kaydediliyor...' : 'İmzala ve Kaydet'}</button>
              </div>
            </div>
          </div>
        )}

        {showActiveShipmentsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-[#151515]" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700/30">
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Aktif Sevkiyatlar</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {activeShipmentsLoading ? 'Yükleniyor...' : `${activeShipmentsList.length} kayıt`}
                  </div>
                </div>
                <button type="button" onClick={() => setShowActiveShipmentsModal(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {activeShipmentsLoading ? (
                  <div className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400">Yükleniyor...</div>
                ) : activeShipmentsList.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400">Aktif sevkiyat bulunamadı</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 dark:bg-[#1a1a1a]">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Sipariş #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Yükleme</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Boşaltma</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Durum</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Talep Tarihi</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Atama Tarihi</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-200">Seç</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeShipmentsList.map((shipment: any, idx: number) => {
                        const isSelected = selectedActiveShipmentId === String(shipment.id || '');
                        const processStatus = String(shipment.process_status || '').trim();
                        const statusLabel = MANUAL_PROCESS_STATUS_OPTIONS.find((o) => o.value === processStatus)?.label || formatSlugLabel(processStatus) || processStatus || '-';
                        return (
                          <tr
                            key={shipment.id || idx}
                            className={`border-b border-slate-100 transition dark:border-slate-800 ${isSelected ? 'bg-orange-50 dark:bg-orange-950/30' : 'hover:bg-slate-50 dark:hover:bg-[#171717]'}`}
                          >
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{shipment.shipment_no || '-'}</td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {[shipment.pickup_city, shipment.pickup_district].filter(Boolean).join(' / ') || '-'}
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {[shipment.delivery_city, shipment.delivery_district].filter(Boolean).join(' / ') || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ${getFlowStatusClass(processStatus)}`}>
                                {statusLabel}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{formatDateTime(shipment.pickup_date) || '-'}</td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{formatDateTime(shipment.assigned_at) || '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => handleSelectActiveShipment(shipment)}
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                                  isSelected
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                                }`}
                              >
                                {isSelected ? 'Seçili' : 'Göster'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-slate-700/30">
                <button
                  type="button"
                  onClick={() => setShowActiveShipmentsModal(false)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        <ShipmentWizardModal
          isOpen={wizardOpen}
          onClose={() => { setWizardOpen(false); setEditingWizardShipment(null); }}
          onCreated={() => { void loadRows(); }}
          editingShipment={editingWizardShipment}
        />

        <BulkShipmentImportModal
          isOpen={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          onCreated={() => { void loadRows(); }}
        />

        {editingInvoiceRow ? (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => { setEditingInvoiceRowId(null); setEditingInvoiceValue(''); }}>
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#111111]" onClick={(event) => event.stopPropagation()}>
              <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">Fatura No Düzenle</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{editingInvoiceRow.sevkKodu || editingInvoiceRow.id}</div>
              </div>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Fatura No</label>
                  <input
                    autoFocus
                    value={editingInvoiceValue}
                    onChange={(event) => setEditingInvoiceValue(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void commitInvoiceEdit(editingInvoiceRow, editingInvoiceValue);
                      }
                    }}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                    placeholder="Fatura numarası girin"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
                <button type="button" onClick={() => { setEditingInvoiceRowId(null); setEditingInvoiceValue(''); }} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200">Vazgeç</button>
                <button type="button" onClick={() => void commitInvoiceEdit(editingInvoiceRow, editingInvoiceValue)} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Kaydet</button>
              </div>
            </div>
          </div>
        ) : null}

        {editingCostCell && editingCostRow ? (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => { if (!savingCostCell) { setEditingCostCell(null); setEditingCostValue(''); } }}>
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#111111]" onClick={(event) => event.stopPropagation()}>
              <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{editingCostCell.field === 'beklemeBedeli' ? 'Bekleme Bedeli Düzenle' : 'Hammaliye Bedeli Düzenle'}</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{editingCostRow.sevkKodu || editingCostRow.id}</div>
              </div>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">{editingCostCell.field === 'beklemeBedeli' ? 'Bekleme Bedeli' : 'Hammaliye Bedeli'}</label>
                  <input
                    autoFocus
                    inputMode="decimal"
                    value={editingCostValue}
                    onChange={(event) => setEditingCostValue(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void commitCostEdit(editingCostRow, editingCostCell.field); } }}
                    disabled={Boolean(savingCostCell) || (editingCostCell.field === 'beklemeBedeli' && demurrageLocked && isDemurrageLockedForRow(editingCostRow))}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-right text-sm outline-none focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                    placeholder="0"
                  />
                  {editingCostCell.field === 'beklemeBedeli' && demurrageLocked && isDemurrageLockedForRow(editingCostRow) && (
                    <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Geçiş tarihinden itibaren bekleme bedeli otomatik hesaplanır, manuel giriş yapılamaz.</p>
                  )}
                </div>
                <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600 dark:bg-[#171717] dark:text-slate-300">
                    Toplam: {formatMoney(Number(editingCostRow.navlun || 0) + (editingCostCell.field === 'beklemeBedeli' ? (Number(String(editingCostValue || '').replace(',', '.')) || 0) : resolveCostValue(editingCostRow, 'beklemeBedeli')) + (editingCostCell.field === 'hammaliye' ? (Number(String(editingCostValue || '').replace(',', '.')) || 0) : resolveCostValue(editingCostRow, 'hammaliye')))}
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
                <button type="button" disabled={Boolean(savingCostCell)} onClick={() => { setEditingCostCell(null); setEditingCostValue(''); }} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200">Vazgeç</button>
                <button type="button" disabled={Boolean(savingCostCell)} onClick={() => void commitCostEdit(editingCostRow, editingCostCell.field)} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{savingCostCell ? 'Kaydediliyor...' : 'Kaydet'}</button>
              </div>
            </div>
          </div>
        ) : null}

        {routeSheet ? (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"
            onClick={() => setRouteSheet(null)}
          >
            <div
              className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-[#111111]"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                <div>
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    TIR Rotası · #{routeSheet.row.sevkKodu}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    OpenRouteService driving-hgv profili ile hesaplandı; yükseklik, genişlik, uzunluk ve tonaj kısıtları uygulanır.
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFullscreenRouteMap(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    Tam Ekran Harita
                  </button>
                  <button
                    type="button"
                    onClick={() => setRouteSheet(null)}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
                  >
                    Kapat
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto p-4">
                <ShipmentRouteMap route={routeSheet.route} className="mb-4 h-[380px] w-full overflow-hidden rounded-xl" />
                <ShipmentRouteSummary route={routeSheet.route} />
                {routeSheet.route?.failedSegments > 0 && (
                  <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    {routeSheet.route.failedSegments} durak arası segment için harita servisi yanıt vermedi; bu bölüm düz çizgiyle gösterilmedi ve mesafeye eklenmedi.
                  </p>
                )}
              </div>

              {routeSheet.route?.google_maps_url && (
                <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                  <a
                    href={routeSheet.route.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12px] font-medium text-sky-600 underline dark:text-sky-400"
                  >
                    Google Haritalar'da aç (otomobil rotası)
                  </a>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Google Haritalar TIR profili desteklemediği için bu bağlantı otomobil rotası verir; tonaj ve boyut kısıtları uygulanmaz.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {showFullscreenRouteMap && routeSheet ? (
          <div className="fixed inset-0 z-[11000] flex flex-col bg-white dark:bg-[#111111]">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="min-w-0">
                <div className="truncate text-base font-bold text-slate-900 dark:text-slate-100">
                  TIR Rotası · #{routeSheet.row.sevkKodu}
                </div>
                <div className="text-[11px] text-slate-400">
                  Tüm durak ve geçiş noktaları
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowFullscreenRouteMap(false)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Kapat
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <ShipmentRouteMap route={routeSheet.route} className="h-full w-full" />
            </div>
          </div>
        ) : null}

      </div>
    </div>
    </>
  );
}

export default FlexibleTracking;
